import "../lib/readable_stream_polyfill"
import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"
import { createConsumer } from "@rails/actioncable"
import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs"
import { extractPageHeaders, splitDrawingBySheets, splitSpecBySections } from "../pdf_splitter"
import { showSheetPreview, hideSheetPreview } from "../lib/sheet_preview"

const DISCIPLINE_PREFIXES = {
  "FP": "Fire Protection", "FA": "Fire Protection", "FS": "Fire Protection",
  "SK": "Sketch", "SD": "Civil", "EC": "Civil", "SW": "Civil", "UT": "Civil", "GR": "Civil",
  "ID": "Architectural",
  "A": "Architectural", "S": "Structural", "T": "Structural",
  "M": "Mechanical", "E": "Electrical", "P": "Plumbing",
  "C": "Civil", "L": "Landscape", "G": "General"
}

const CSI_DIVISIONS = {
  "01": "General Requirements", "02": "Existing Conditions", "03": "Concrete",
  "04": "Masonry", "05": "Metals", "06": "Wood, Plastics & Composites",
  "07": "Thermal & Moisture Protection", "08": "Openings", "09": "Finishes",
  "10": "Specialties", "11": "Equipment", "12": "Furnishings",
  "13": "Special Construction", "14": "Conveying Equipment", "21": "Fire Suppression",
  "22": "Plumbing", "23": "HVAC", "25": "Integrated Automation", "26": "Electrical",
  "27": "Communications", "28": "Electronic Safety & Security",
  "31": "Earthwork", "32": "Exterior Improvements", "33": "Utilities"
}

const DISCIPLINE_COLORS = {
  "Architectural": "bg-blue-100 text-blue-700",
  "Structural":    "bg-amber-100 text-amber-700",
  "Mechanical":    "bg-purple-100 text-purple-700",
  "Electrical":    "bg-yellow-100 text-yellow-700",
  "Plumbing":      "bg-cyan-100 text-cyan-700",
  "Fire Protection":"bg-red-100 text-red-700",
  "Civil":         "bg-green-100 text-green-700",
  "Landscape":     "bg-lime-100 text-lime-700",
  "General":       "bg-gray-100 text-gray-600",
  "Sketch":        "bg-violet-100 text-violet-700"
}

// Conventional construction sheet order (General/site first, then structure,
// architecture, MEP) — orders the discipline group rows.
const DISCIPLINE_ORDER = ["General", "Civil", "Landscape", "Structural", "Architectural", "Mechanical", "Plumbing", "Electrical", "Fire Protection", "Sketch"]

function detectDiscipline(sheetNumber) {
  if (!sheetNumber) return null
  const prefix = sheetNumber.trim().toUpperCase().replace(/[-\s].*/, "")
  return DISCIPLINE_PREFIXES[prefix.substring(0, 2)] || DISCIPLINE_PREFIXES[prefix.substring(0, 1)] || null
}

function detectDivision(sectionNumber) {
  if (!sectionNumber) return null
  const prefix = sectionNumber.trim().replace(/\s+/g, " ").split(" ")[0]
  return CSI_DIVISIONS[prefix.padStart(2, "0")] || null
}

function typeBadgeHtml(docType) {
  if (docType === "specification") {
    return '<span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide bg-purple-100 text-purple-700">SPEC</span>'
  }
  if (docType === "document") {
    return '<span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide bg-gray-100 text-gray-500">DOC</span>'
  }
  // drawing — default
  return '<span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide bg-blue-100 text-blue-700">DRAWING</span>'
}

// DWG files (E5-3, Phelipe 2026-09-26) ride in Design for reference only —
// this pill says so on the list and in the upload modal.
function cadBadgeHtml() {
  return '<span class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[13px] font-semibold bg-amber-100 text-amber-800" title="CAD file — download to open">DWG</span>'
}

async function extractPdfText(arrayBuffer, maxPages = 5) {
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
  const pageCount = Math.min(pdf.numPages, maxPages)
  const pageTexts = []
  for (let i = 1; i <= pageCount; i++) {
    const page = await pdf.getPage(i)
    const content = await page.getTextContent()
    pageTexts.push(content.items.map(item => item.str).join(" "))
  }
  return { pageTexts, totalPages: pdf.numPages }
}

async function renderPdfPagesToJpegs(arrayBuffer, maxPages = 5, targetWidth = 800) {
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
  const pageCount = Math.min(pdf.numPages, maxPages)
  const images = await Promise.all(
    Array.from({ length: pageCount }, async (_, i) => {
      const page = await pdf.getPage(i + 1)
      const viewport = page.getViewport({ scale: 1 })
      const scale = targetWidth / viewport.width
      const scaledViewport = page.getViewport({ scale })
      const canvas = document.createElement("canvas")
      canvas.width = scaledViewport.width
      canvas.height = scaledViewport.height
      const ctx = canvas.getContext("2d")
      ctx.fillStyle = "#fff"
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise
      const jpegBase64 = canvas.toDataURL("image/jpeg", 0.70).split(",")[1]
      canvas.width = canvas.height = 0
      return jpegBase64
    })
  )
  return { images, totalPages: pdf.numPages }
}

async function callExtractMetadata(url, mode, content, filename) {
  const token = document.querySelector('meta[name="csrf-token"]')?.content
  const body = { mode, filename }
  if (mode === "text") body.text = content
  else body.images = content
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
    credentials: "same-origin",
    signal: AbortSignal.timeout(35000),
    body: JSON.stringify(body)
  })
  if (!res.ok) throw new Error(`extract_metadata HTTP ${res.status}`)
  return await res.json()
}

let sharedConsumer = null
function getCable() {
  if (!sharedConsumer) sharedConsumer = createConsumer()
  return sharedConsumer
}

// Filter chip states — the date presets use the same two strings.
const CHIP_ON = "bg-brand/10 text-brand font-medium border-brand/30"
const CHIP_OFF = "text-gray-500 hover:bg-gray-50 border-gray-200"

export default class extends Controller {
  static targets = [
    "pdfTableWrap", "pdfTable", "pdfTableBody", "folderBackBar",
    "gridContainer", "sentinel",
    "loadingSkeleton", "emptyState",
    "disciplineMenu", "typeChips", "versionChips",
    "searchClear", "aiFilterChip", "filterBadge", "clearFiltersRow",
    "datePresets", "dateFrom", "dateTo", "viewSwitcher",
    "selectAllCheckbox", "selectAllCol", "bulkToggle", "bulkBar", "bulkCount", "compareBtn",
    "compareChoiceModal", "compareChoiceOverlayBtn",
    "comparePickerModal", "comparePickerTitle", "comparePickerList", "comparePickerEmpty",
    "comparePickerPreviousWrap", "comparePickerOlderSection",
    "detailModal", "detailBackdrop", "detailPanel", "detailContent", "detailLoading",
    "detailTitle", "detailPreview", "detailMeta", "detailDownloadBtn", "prevBtn", "nextBtn",
    "detailRevisionHistory", "detailLinkedItems",
    "distributeModal", "distributeSearchInput", "distributeDropdownList", "distributeSendBtn",
    "distributeSelectAll", "distributeCount",
    "sortIconCurrentDiscipline",
    "uploadModal", "dropZone", "fileInput",
    "uploadFolderSelect", "uploadNotifyCheck", "uploadSubmitBtn", "uploadMenu", "uploadMenuBtn", "uploadMenuDefault",
    "uploadSheetTable", "uploadSheetHead", "uploadSheetRows", "uploadAiStatus", "uploadAiStatusText",
    "folderModal", "folderModalTitle", "folderNameInput", "folderEditId",
    "trashModal", "trashList", "trashEmpty", "trashLoading", "trashBadge",
    "historyUploads", "historyUploadsEmpty", "undoUploadModal", "undoUploadText", "undoUploadMarked", "undoUploadBtn",
    "uploadDupBanner", "uploadDupText",
    // DWG pop-up (E5-3): markup is PDF-only, so a CAD file offers a download
    "cadModal", "cadModalName", "cadModalDownload",
    "privacyConsultants", "privacyContractors", "privacyOtherVendors", "privacyCollaborators",
    "editPdfModal", "editPdfTitle", "editPdfSheetNumber", "editPdfDiscipline", "editPdfFolder", "editPdfId",
    "editPdfType", "editPdfSheetDisciplineRow", "editPdfSheetLabel", "editPdfDisciplineLabel",
    "searchInput",
    "dueDateInput", "daysLeft",
    "bluebeamPopover", "copyLinkLabel", "bluebeamSessionId", "bluebeamSessionIdCopyLabel",
    "saveMarkupsModal",
    "deletePdfModal", "deletePdfName", "deletePdfPackNote",
    "deleteFolderModal", "deleteFolderName",
    "closeBluebeamModal",
    "alertModal", "alertModalTitle", "alertModalMessage",
    "notifyModal", "notifyBody", "notifySelectAll", "notifySendBtn",
    "sortIconCurrentDocumentType", "sortIconCurrentSheetNumber", "sortIconCurrentTitle", "sortIconCurrentActionItems", "sortIconCurrentUploadedBy", "sortIconCurrentDate",
    "exportModal", "exportDocTypes", "exportFolderSelect", "exportHistorical", "exportSortSelect", "exportFileCount",
    "exportZipBtn", "exportCompiledBtn", "exportBtn",
    "viewerPrevBtn", "viewerNextBtn"
  ]

  static values = {
    projectId: Number,
    stepName: String,
    phaseStatusId: Number,
    foldersUrl: String,
    folderFilesUrl: String,
    presignUrl: String,
    registerUrl: String,
    saveFolderUrl: String,
    updateFolderUrl: String,
    deleteFolderUrl: String,
    destroyPdfUrl: String,
    trashUrl: String,
    restorePdfUrl: String,
    restoreFolderUrl: String,
    updatePdfUrl: String,
    viewerDataBaseUrl: String,
    saveAnnotationsBaseUrl: String,
    splitDocumentsBaseUrl: String,
    canUpload: Boolean,
    isOwner: Boolean,
    defaultShareRole: String,
    isCompleted: Boolean,
    currentUserId: Number,
    setDeadlineUrl: String,
    bluebeamOpenBaseUrl: String,
    bluebeamSyncBaseUrl: String,
    bluebeamCloseBaseUrl: String,
    bluebeamStatusBaseUrl: String,
    bluebeamConnected: Boolean,
    notifyMembersUrl: String,
    notifyCommentsUrl: String,
    mergedPdfUrl: String,
    saveSortUrl: String,
    initialSort: String,
    extractMetadataUrl: String,
    analyzeCombinedUrl: String,
    checkSheetsUrl: String,
    checkSectionsUrl: String,
    pdfWorkerUrl: String,
    exportCountsUrl: String,
    exportZipUrl: String,
    exportCompiledPdfUrl: String,
    compiledPdfStatusUrl: String,
    sheetHistoryUrlTemplate: String,
    referencesUrl: String,
    distributeUrl: String,
    distributionMembersUrl: String,
    bulkDestroyUrl: String,
    uploadHistoryUrl: String,
    checkDuplicatesUrl: String,
    saveFiltersUrl: String,
    saveViewModeUrl: String,
    savedFilters: { type: Object, default: {} },
    currentView: { type: String, default: "list" }
  }

  connect() {
    track("Design Step Viewed", { step_name: this.element.dataset.stepName })
    this._currentFolderId = null
    this._currentFolderName = null
    this._folders = []
    this._pdfs = []
    this._uploadFiles = []
    this._dupMode = "skip"
    this._versionFilter = ""
    this._typeFilter = ""
    this._searchTerm = ""
    this._searchTimeout = null
    this._bluebeamSessionUrl = null
    this._bluebeamPollers = {}
    // List state — paged loading, tree grouping, selection and search-inside
    // results, all mirroring the Drawings/Specs pages.
    this._page = 1
    this._loading = false
    this._isLastPage = false
    this._loadSeq = 0
    this._pdfsById = {}
    this._contentMatches = {}
    this._activeDiscipline = null
    this._filters = {}
    this._groupState = {}
    this._selectedIds = new Set()
    this._bulkMode = false
    this._modalOpen = false
    this._activeFileId = null
    this._aiSources = null
    this._onKeydown = this._onKeydown.bind(this)

    // Sort state per table (current / old)
    const savedSort = this._parseInitialSort()
    this._sortCurrent = savedSort.current || { key: null, order: null }

    // Init PDF.js worker for AI metadata extraction
    if (this.hasPdfWorkerUrlValue && this.pdfWorkerUrlValue) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = this.pdfWorkerUrlValue
    }

    this._restoreSavedFilters()
    this._applyViewMode(this.currentViewValue, { reload: false, persist: false })

    // Infinite scroll — the next page loads as the sentinel comes into view.
    this._observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this._loading && !this._isLastPage) this._loadPage(this._page)
      },
      { rootMargin: "300px", threshold: 0 }
    )

    this._loadInitial()

    // "Show me" from the Ask-AI panel → filter the list to the answer's sources.
    this._onAiSources = (e) => this._applyAiSources(e.detail?.sources)
    window.addEventListener("docs-ai:show-sources", this._onAiSources)

    // Auto-poll if returning from Bluebeam OAuth with a pending session
    const urlParams = new URLSearchParams(window.location.search)
    const bluebeamPoll = urlParams.get("bluebeam_poll")
    if (bluebeamPoll) {
      this._showBluebeamToast("Creating Bluebeam session...")
      this._pollBluebeamStatus(bluebeamPoll)
      // Clean up URL param
      const cleanUrl = new URL(window.location)
      cleanUrl.searchParams.delete("bluebeam_poll")
      window.history.replaceState({}, "", cleanUrl)
    }

    if (this.canUploadValue) {
      this._setupPageDrag()
    }

    // Close row menus on outside click
    this._onDocClick = (e) => {
      // Close row menus
      document.querySelectorAll("[data-row-menu]").forEach(m => {
        if (!m.contains(e.target) && !e.target.closest("[data-action*='toggleRowMenu']")) {
          m.classList.add("hidden")
        }
      })
    }
    document.addEventListener("click", this._onDocClick)

    // Refresh file list when PDF viewer closes (annotations may have changed)
    this._onViewerClosed = () => this._refreshCurrentView()
    this.element.addEventListener("pdf-viewer:closed", this._onViewerClosed)

    // Handle "Markup in Bluebeam" button from the viewer
    this._onOpenBluebeam = (e) => {
      const pdfId = e.detail?.pdfId
      if (!pdfId) return
      this._triggerBluebeamOpen(pdfId)
    }
    this.element.addEventListener("pdf-viewer:open-bluebeam", this._onOpenBluebeam)

    // Subscribe to ActionCable for real-time annotation count updates
    this._subscribeToDevPdfChannel()
  }

  disconnect() {
    this._teardownPageDrag()
    if (this._observer) this._observer.disconnect()
    document.removeEventListener("keydown", this._onKeydown)
    window.removeEventListener("docs-ai:show-sources", this._onAiSources)
    document.removeEventListener("click", this._onDocClick)
    this.element.removeEventListener("pdf-viewer:closed", this._onViewerClosed)
    this.element.removeEventListener("pdf-viewer:open-bluebeam", this._onOpenBluebeam)
    // Clear Bluebeam pollers
    Object.values(this._bluebeamPollers || {}).forEach(id => clearInterval(id))
    this._bluebeamPollers = {}
    // Unsubscribe from ActionCable
    if (this._cableSubscription) {
      this._cableSubscription.unsubscribe()
      this._cableSubscription = null
    }
  }

  // ── Loading ───────────────────────────────────────────────────
  // Folders come from their own endpoint; documents are paged by the server
  // with search / date / discipline / type filters applied there, so the list
  // behaves identically to Drawings and Specs.

  async _loadInitial() {
    try {
      const foldersRes = await this._fetch(this.foldersUrlValue)
      this._folders = await foldersRes.json()
    } catch (e) {
      console.error("[design-step] Folder load error:", e)
      this._folders = []
    }
    this._resetAndLoad()
  }

  _resetAndLoad() {
    this._page = 1
    this._isLastPage = false
    this._loading = false
    // Stale-response guard: a reload invalidates any in-flight page fetch so a
    // slow older response can't clobber a newer one.
    this._loadSeq = (this._loadSeq || 0) + 1
    this._pdfs = []
    this._pdfsById = {}
    this._contentMatches = {}
    if (this._observer) this._observer.disconnect()
    if (this.hasPdfTableBodyTarget) this.pdfTableBodyTarget.innerHTML = ""
    if (this.hasGridContainerTarget) this.gridContainerTarget.innerHTML = ""
    if (this.hasLoadingSkeletonTarget) this.loadingSkeletonTarget.classList.remove("hidden")
    this._hideEmpty()
    this._loadPage(1)
  }

  _filesUrl(page) {
    const url = new URL(this.folderFilesUrlValue, window.location.origin)
    url.searchParams.set("page", page)
    if (this._currentFolderId) url.searchParams.set("folder_id", this._currentFolderId)
    // "" (All) means every version; the server defaults to current-only.
    url.searchParams.set("version", this._versionFilter || "all")
    if (this._searchTerm && !this._aiSources) url.searchParams.set("search", this._searchTerm)
    if (this._typeFilter) url.searchParams.set("document_type", this._typeFilter)
    if (this._activeDiscipline) url.searchParams.set("discipline", this._activeDiscipline)
    if (this._filters.date_from) url.searchParams.set("date_from", this._filters.date_from)
    if (this._filters.date_to) url.searchParams.set("date_to", this._filters.date_to)
    if (this._aiSources) {
      [...new Set(this._aiSources.map(s => String(s.id)))].forEach(id => url.searchParams.append("doc_ids[]", id))
    }
    return url.toString()
  }

  async _loadPage(page) {
    if (this._loading || (page > 1 && this._isLastPage)) return
    this._loading = true
    const seq = (this._loadSeq ||= 1)

    try {
      const res = await this._fetch(this._filesUrl(page))
      const data = await res.json()
      if (seq !== this._loadSeq) return // superseded by a newer reload

      if (this.hasLoadingSkeletonTarget) this.loadingSkeletonTarget.classList.add("hidden")

      if (page === 1) {
        this._pdfs = []
        this._pdfsById = {}
        this._contentMatches = {}
      }

      Object.assign(this._contentMatches, data.content_matches || {})
      if (this._aiSources) this._mergeAiMatches()

      const rows = data.pdfs || []
      rows.forEach(pdf => {
        this._pdfs.push(pdf)
        this._pdfsById[String(pdf.id)] = pdf
        // Register nested prior versions too, so they can be selected and
        // compared without switching to the Historical view.
        ;(pdf.versions || []).forEach(v => { this._pdfsById[String(v.id)] = v })
      })

      if (page === 1) {
        this._renderDisciplineTabs(data.discipline_counts)
        const badge = document.getElementById("design-step-count")
        if (badge) badge.textContent = data.total ?? rows.length
        if (this._searchTerm && !this._aiSources) {
          track("Design Documents Searched", {
            project_id: this.projectIdValue,
            step_name: this.stepNameValue,
            results_count: data.total,
            documents_with_text_matches: Object.keys(data.content_matches || {}).length
          })
        }
      }

      this._render()

      this._isLastPage = !data.has_more
      this._page = page + 1

      if (!this._isLastPage && this.hasSentinelTarget) {
        this.sentinelTarget.classList.remove("hidden")
        this._observer.observe(this.sentinelTarget)
      } else if (this.hasSentinelTarget) {
        this.sentinelTarget.classList.add("hidden")
        this._observer.disconnect()
      }
    } catch (e) {
      console.error("[design-step] Load error:", e)
      if (this.hasLoadingSkeletonTarget) this.loadingSkeletonTarget.classList.add("hidden")
      if (page === 1) this._showEmpty()
    } finally {
      this._loading = false
    }
  }

  // ── Rendering ─────────────────────────────────────────────────

  _render() {
    this._renderPdfTable()
    if (this.hasLoadingSkeletonTarget) this.loadingSkeletonTarget.classList.add("hidden")
  }

  _renderPdfTable() {
    const folders = this._filteredFolders()
    const rows = this._sortPdfs(this._pdfs, this._sortCurrent)

    if (folders.length === 0 && rows.length === 0) {
      this._showEmpty()
      return
    }
    this._hideEmpty()

    if (this.currentViewValue === "grid") {
      if (this.hasPdfTableTarget) this.pdfTableTarget.classList.add("hidden")
      if (this.hasGridContainerTarget) {
        this.gridContainerTarget.classList.remove("hidden")
        this.gridContainerTarget.innerHTML =
          folders.map(f => this._folderCardHtml(f)).join("") + rows.map(p => this._pdfCardHtml(p)).join("")
      }
      return
    }

    if (this.hasGridContainerTarget) this.gridContainerTarget.classList.add("hidden")
    if (this.hasPdfTableTarget && this.hasPdfTableBodyTarget) {
      this.pdfTableTarget.classList.remove("hidden")
      this.pdfTableBodyTarget.innerHTML =
        folders.map(f => this._folderRowHtml(f)).join("") + this._renderRowsTree(rows)
    }

    // Navigation order = what's on screen, top to bottom. Group headers carry
    // synthetic ids (discgrp-…), so only real document ids belong in the walk.
    this._navIds = [...new Set([...this.pdfTableBodyTarget.querySelectorAll("tr[data-pdf-id]")]
      .map(r => r.dataset.pdfId))].filter(id => /^\d+$/.test(id))

    this._updateSortIcons()
    this._syncBulkColumn()
    // Rows just got replaced — a hover card anchored to an old row would never
    // receive its mouseleave.
    hideSheetPreview()
  }

  _navOrder() {
    return (this._navIds && this._navIds.length) ? this._navIds : this._pdfs.map(p => String(p.id))
  }

  _filteredFolders() {
    let folders = this._currentFolderId
      ? this._findSubFolders(this._currentFolderId)
      : this._folders
    if (this._searchTerm) {
      const term = this._searchTerm.toLowerCase()
      folders = folders.filter(f => f.name.toLowerCase().includes(term))
    }
    // Folders hold current documents — hide them in the Historical view, and
    // while an AI answer or a discipline tab is filtering the documents.
    if (this._versionFilter === "superseded" || this._aiSources || this._activeDiscipline) return []
    return folders
  }

  // ── Document tree: combined pack ▸ discipline ▸ sheet ▸ versions ──

  _renderRowsTree(sorted) {
    const presentIds = new Set(sorted.map(f => String(f.id)))
    const childrenByParent = {}
    sorted.forEach(f => {
      if (f.combined_parent_id && presentIds.has(String(f.combined_parent_id))) {
        (childrenByParent[f.combined_parent_id] ||= []).push(f)
      }
    })
    // A search / AI filter / discipline tab forces every group open so matches
    // aren't hidden inside a collapsed one.
    const searching = !!(this._searchTerm || this._aiSources || this._activeDiscipline)

    const renderSheet = (f, opts = {}, parentOpen = true) => {
      const vers = f.versions || []
      if (!vers.length) return this._pdfRowHtml(f, { ...opts, hidden: !parentOpen })
      const key = `s${f.id}`
      const open = searching ? true : this._groupExpanded(key, false)
      let out = this._pdfRowHtml(f, { ...opts, hidden: !parentOpen, expandable: true, collapsed: !open, groupKey: key })
      vers.forEach(v => {
        out += this._pdfRowHtml(v, { child: true, grandchild: opts.grandchild, parentId: f.id, version: true, hidden: !(parentOpen && open) })
      })
      return out
    }

    let html = ""
    const loose = []
    sorted.forEach(f => {
      if (f.combined_parent_id && presentIds.has(String(f.combined_parent_id))) return
      const kids = childrenByParent[f.id]
      if (f.combined && kids && kids.length) {
        const bookOpen = searching ? true : this._groupExpanded(`c${f.id}`, true)
        html += this._pdfRowHtml(f, { expandable: true, collapsed: !bookOpen, groupKey: `c${f.id}` })
        html += this._renderDisciplineGroups(kids, f.id, 1, renderSheet, searching, bookOpen)
      } else if (f.combined) {
        html += this._pdfRowHtml(f, {})
      } else {
        loose.push(f)
      }
    })
    html += this._renderDisciplineGroups(loose, null, 0, renderSheet, searching, true)
    return html
  }

  // Bucket sibling documents into one group per discipline (conventional sheet
  // order). Spec sections and loose documents fall into their own buckets.
  _renderDisciplineGroups(rows, parentId, depth, renderSheet, searching = false, parentOpen = true) {
    if (!rows.length) return ""
    const buckets = new Map()
    rows.forEach(r => {
      const key = r.discipline || (r.document_type === "document" ? "Documents" : "Other")
      if (!buckets.has(key)) buckets.set(key, [])
      buckets.get(key).push(r)
    })
    const orderOf = name => {
      if (name === "Documents") return 9999
      if (name === "Other") return 9998
      const i = DISCIPLINE_ORDER.indexOf(name)
      return i === -1 ? 9000 : i
    }
    const keys = [...buckets.keys()].sort((a, b) => orderOf(a) - orderOf(b) || String(a).localeCompare(String(b)))

    const prefixOf = sheets => {
      for (const s of sheets) {
        const m = String(s.sheet_number || "").trim().match(/^([A-Za-z]+)/)
        if (m) return m[1].toUpperCase()
      }
      return ""
    }

    let html = ""
    keys.forEach((name, i) => {
      const sheets = buckets.get(name)
      // Default view: natural sheet-number order within the group (C-1, C-2 …
      // C-10). An active column sort already ordered them, so leave it alone.
      if (!this._sortCurrent.key) {
        sheets.sort((a, b) => (a.sheet_number || "").localeCompare(b.sheet_number || "", undefined, { numeric: true, sensitivity: "base" }))
      }
      const gid = `discgrp-${parentId || "top"}-${i}`
      const groupKey = `g${parentId || "top"}:${name}`
      const open = searching ? true : this._groupExpanded(groupKey, false)
      html += this._disciplineGroupRowHtml({
        gid, parentId, name, prefix: prefixOf(sheets), count: sheets.length, depth,
        collapsed: !open, hidden: !parentOpen, groupKey
      })
      const childOpen = parentOpen && open
      const childOpts = depth > 0
        ? { child: true, grandchild: true, parentId: gid }
        : { child: true, parentId: gid }
      sheets.forEach(sheet => { html += renderSheet(sheet, childOpts, childOpen) })
    })
    return html
  }

  _disciplineGroupRowHtml({ gid, parentId, name, prefix, count, depth, collapsed = false, hidden = false, groupKey = "" }) {
    const checkboxCol = `<td class="pl-3.5 pr-2 py-2 ${this._bulkMode ? "" : "hidden"}" data-checkbox-col></td>`
    const treeAttr = parentId ? `data-row-child-of="${parentId}"` : ""
    const color = DISCIPLINE_COLORS[name] || "bg-gray-100 text-gray-600"
    const prefixBadge = prefix
      ? `<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium ${color}">${this._esc(prefix)}</span>`
      : ""
    const discBadge = (name && name !== "Other" && name !== "Documents")
      ? `<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium ${color}">${this._esc(name)}</span>`
      : ""
    return `<tr class="bg-gray-50/80 cursor-pointer transition-colors hover:bg-brand/[0.04] ${hidden ? "hidden" : ""}"
                ${treeAttr} data-pdf-id="${gid}"
                data-action="click->design-step#groupRowClick">
      <td class="${depth > 0 ? "pl-6" : "pl-2"} pr-2 py-2 align-middle">
        <button type="button" onclick="event.stopPropagation()" data-action="click->design-step#toggleTreeRow" data-parent-id="${gid}" data-group-key="${groupKey}" data-expanded="${collapsed ? "false" : "true"}" class="align-middle p-1.5 -m-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 cursor-pointer"><svg data-chevron class="w-4 h-4 inline transition-transform${collapsed ? " -rotate-90" : ""}" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg></button>
      </td>
      ${checkboxCol}
      <td class="py-2 pl-3 pr-2"></td>
      <td class="py-2 px-2"></td>
      <td class="py-2 px-2 whitespace-nowrap">${prefixBadge}</td>
      <td class="py-2 px-2 whitespace-nowrap"><span class="font-semibold text-gray-800">${this._esc(name)}</span> <span class="text-gray-400">· ${count}</span></td>
      <td class="py-2 px-2 whitespace-nowrap">${discBadge}</td>
      <td class="py-2 px-2"></td>
      <td class="py-2 px-2"></td>
      <td class="py-2 px-2 hidden md:table-cell"></td>
      <td class="py-2 px-2 hidden lg:table-cell"></td>
      <td class="py-2 px-2 hidden lg:table-cell"></td>
      <td class="py-2 px-2"></td>
    </tr>`
  }

  // Clicking a group row expands/collapses its children — same as the arrow.
  groupRowClick(event) {
    const btn = event.currentTarget.querySelector("[data-parent-id]")
    if (btn) btn.click()
  }

  // Expand/collapse a row's children (combined → disciplines, discipline →
  // sheets, sheet → versions). Collapsing hides ALL levels beneath so
  // re-expanding never surfaces an orphaned grandchild row.
  toggleTreeRow(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const pid = btn.dataset.parentId
    const collapse = btn.dataset.expanded !== "false"
    if (collapse) {
      this._collapseDescendants(pid)
    } else {
      this.pdfTableBodyTarget.querySelectorAll(`[data-row-child-of="${pid}"]`).forEach(r => r.classList.remove("hidden"))
    }
    btn.dataset.expanded = collapse ? "false" : "true"
    btn.querySelector("[data-chevron]")?.classList.toggle("-rotate-90", collapse)
    this._recordGroup(btn.dataset.groupKey, !collapse)
  }

  _collapseDescendants(pid) {
    this.pdfTableBodyTarget.querySelectorAll(`[data-row-child-of="${pid}"]`).forEach(r => {
      r.classList.add("hidden")
      const childBtn = r.querySelector("button[data-parent-id]")
      if (childBtn) {
        childBtn.dataset.expanded = "false"
        childBtn.querySelector("[data-chevron]")?.classList.add("-rotate-90")
      }
      const cid = r.dataset.pdfId
      if (!cid) return
      const matchRow = this.pdfTableBodyTarget.querySelector(`[data-match-row-for="${cid}"]`)
      if (matchRow) {
        matchRow.remove()
        r.querySelector("[data-match-chevron]")?.classList.remove("rotate-180")
      }
      this._collapseDescendants(cid)
    })
  }

  _folderRowHtml(folder) {
    // The server decides who may rename, re-share or delete a folder (owner
    // side, ConsLog admins, the folder's maker) and enforces it too.
    const canManage = folder.can_manage === true
    const fileCount = folder.pdf_count || 0
    const privacy = folder.privacy_settings || ["private"]
    const isShared = privacy.length > 1 || (privacy.length === 1 && privacy[0] !== "private")
    const checkboxCol = `<td class="pl-3.5 pr-2 py-2.5 ${this._bulkMode ? "" : "hidden"}" data-checkbox-col></td>`

    const folderIcon = isShared
      ? `<svg class="w-[22px] h-[22px] text-brand" fill="currentColor" viewBox="0 0 24 24"><path d="M19,17H11V16C11,14.67 13.67,14 15,14C16.33,14 19,14.67 19,16M15,9A2,2 0 0,1 17,11A2,2 0 0,1 15,13A2,2 0 0,1 13,11C13,9.89 13.9,9 15,9M20,6H12L10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6Z"/></svg>`
      : `<svg class="w-[22px] h-[22px] text-brand" fill="currentColor" viewBox="0 0 24 24"><path d="M19.5 21a3 3 0 0 0 3-3v-4.5a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h15ZM1.5 10.146V6a3 3 0 0 1 3-3h5.379a2.25 2.25 0 0 1 1.59.659l2.122 2.121c.14.141.331.22.53.22H19.5a3 3 0 0 1 3 3v1.146A4.483 4.483 0 0 0 19.5 9h-15a4.483 4.483 0 0 0-3 1.146Z"/></svg>`

    return `
      <tr class="hover:bg-brand/5 cursor-pointer transition-colors group"
          data-action="click->design-step#openFolder" data-folder-id="${folder.id}" data-folder-name="${this._esc(folder.name)}">
        <td class="py-2.5 pl-2 pr-2"></td>
        ${checkboxCol}
        <td class="py-2.5 pl-3 pr-2">${folderIcon}</td>
        <td class="py-2.5 px-2"></td>
        <td class="py-2.5 px-2"></td>
        <td class="py-2.5 px-2 text-sm font-medium text-gray-800">
          ${this._esc(folder.name)}
          ${fileCount > 0 ? `<span class="ml-2 text-[11px] text-gray-400 font-normal">${fileCount} file${fileCount !== 1 ? 's' : ''}</span>` : ''}
        </td>
        <td class="py-2.5 px-2"></td>
        <td class="py-2.5 px-2"></td>
        <td class="py-2.5 px-2"></td>
        <td class="py-2.5 px-2 hidden md:table-cell"></td>
        <td class="py-2.5 px-2 text-[13px] text-gray-500 whitespace-nowrap hidden lg:table-cell">${this._esc(folder.creator_name || '')}</td>
        <td class="py-2.5 px-2 text-[13px] text-gray-400 whitespace-nowrap hidden lg:table-cell">${this._esc(folder.formatted_date || '')}</td>
        <td class="py-2.5 px-2 relative" data-action="click->design-step#stopProp">
          ${canManage ? `
            <button type="button" data-action="click->design-step#toggleRowMenu" data-pdf-id="f${folder.id}"
                    class="p-1 rounded text-text-muted hover:text-text-heading transition-colors cursor-pointer">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>
            </button>
            <div data-row-menu data-row-menu-id="f${folder.id}" class="hidden absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
              <button type="button" data-action="click->design-step#openEditFolderModal" data-folder-id="${folder.id}" data-folder-name="${this._esc(folder.name)}" data-folder-privacy='${JSON.stringify(folder.privacy_settings || ["private"])}'
                      class="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"/></svg>
                Edit
              </button>
              <button type="button" data-action="click->design-step#deleteFolder" data-folder-id="${folder.id}" data-folder-name="${this._esc(folder.name)}"
                      class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>
                Delete
              </button>
            </div>
          ` : ''}
        </td>
      </tr>
    `
  }

  _pdfRowHtml(pdf, opts = {}) {
    const canManage = pdf.uploader_id === this.currentUserIdValue || this.isOwnerValue
    const isSuperseded = pdf.status !== "current"
    const rev = pdf.revision_number ? `v${pdf.revision_number}` : "—"
    const textColor = isSuperseded ? "text-gray-400" : "text-gray-600"

    const checkboxCol = `<td class="pl-3.5 pr-2 py-2.5 ${this._bulkMode ? "" : "hidden"}" data-checkbox-col data-action="click->design-step#stopProp">
           <input type="checkbox" class="form-checkbox rounded" style="accent-color: var(--color-brand)"
                  data-file-checkbox data-file-id="${pdf.id}"
                  data-action="change->design-step#toggleFileSelect"
                  ${this._selectedIds.has(String(pdf.id)) ? "checked" : ""}>
         </td>`

    const discBadge = pdf.discipline
      ? `<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium ${DISCIPLINE_COLORS[pdf.discipline] || "bg-gray-100 text-gray-600"}">${this._esc(pdf.discipline)}</span>`
      : '<span class="text-gray-300">—</span>'

    // Search-inside-documents: result count on the left of matching rows; the
    // chevron flips when the row is expanded to show its snippets.
    const match = this._contentMatches?.[String(pdf.id)]
    const matchBadge = match
      ? `<span class="inline-flex items-center gap-1 rounded-full bg-brand/10 text-brand px-2 py-px text-[13px] font-semibold mr-2 align-middle" title="${match.count} result${match.count === 1 ? "" : "s"} inside this document — click to preview">${match.count}<svg data-match-chevron class="w-3 h-3 transition-transform" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg></span>`
      : ""

    // Hover thumbnail — resting on the glyph floats a page-1 preview.
    const thumbIcon = pdf.pdf_url && !pdf.combined
      ? `<span class="inline-flex align-middle mr-1.5 p-0.5 rounded text-gray-400 hover:text-brand hover:bg-brand/10 cursor-zoom-in" data-pdf-url="${this._esc(pdf.pdf_url)}" data-thumb-label="${this._esc([pdf.sheet_number, pdf.title].filter(Boolean).join(" — "))}" data-action="mouseenter->design-step#sheetPreviewShow mouseleave->design-step#sheetPreviewHide click->design-step#sheetPreviewHide"><svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Z"/></svg></span>`
      : ""

    const numberCell = pdf.combined
      ? '<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium bg-blue-100 text-blue-700">Combined</span>'
      : opts.version
        ? `<span class="${textColor}">Version ${pdf.revision_number ? this._esc(String(pdf.revision_number)) : "—"}</span>`
        : (pdf.sheet_number ? `<span class="font-mono">${this._esc(pdf.sheet_number)}</span>` : '<span class="text-gray-300">—</span>')

    const treeAttr = opts.child ? `data-row-child-of="${opts.parentId}"` : ""
    const childBg = opts.child ? "bg-gray-50/50" : ""
    const chevCollapsed = opts.collapsed ? " -rotate-90" : ""
    const expandedAttr = opts.collapsed ? ' data-expanded="false"' : ""
    const numGutter = opts.child ? "pl-6 pr-2" : "px-2"

    return `
      <tr class="${childBg} hover:bg-brand/[0.04] cursor-pointer transition-colors group ${opts.hidden ? "hidden" : ""}"
          data-action="click->design-step#openPdf" data-pdf-id="${pdf.id}" ${treeAttr}>
        <td class="${opts.grandchild ? "pl-10" : (opts.child ? "pl-6" : "pl-2")} pr-2 py-2.5 align-middle">${opts.expandable
          ? `<button type="button" onclick="event.stopPropagation()" data-action="click->design-step#toggleTreeRow" data-parent-id="${pdf.id}"${opts.groupKey ? ` data-group-key="${opts.groupKey}"` : ""}${expandedAttr} class="align-middle p-1.5 -m-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 cursor-pointer" title="Show / hide"><svg data-chevron class="w-4 h-4 inline transition-transform${chevCollapsed}" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg></button>`
          : ""}</td>
        ${checkboxCol}
        <td class="py-2.5 pl-3 pr-2">
          ${this.isCompletedValue
            ? '<svg class="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 24 24"><path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clip-rule="evenodd"/></svg>'
            : '<svg class="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>'
          }
        </td>
        <td class="py-2.5 px-2">${typeBadgeHtml(pdf.document_type)}</td>
        <td class="py-2.5 ${numGutter} text-[13px] text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis">${matchBadge}${thumbIcon}${numberCell}</td>
        <td class="py-2.5 px-2 text-sm font-medium ${isSuperseded ? "text-gray-400" : "text-gray-800"}">
          <span class="inline">${this._esc(pdf.title || 'Untitled')}</span>${pdf.extension === "dwg" ? cadBadgeHtml() : ""}
          ${pdf.bluebeam_status === "active" ? `<button type="button" data-action="click->design-step#showSaveMarkupsModal" data-pdf-id="${pdf.id}"
            class="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer transition-colors" title="Save markups from Bluebeam and close session">
            <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"/></svg>
            Save Bluebeam Markups &amp; Close Session</button>` : ""}
          ${pdf.bluebeam_status === "creating" || pdf.bluebeam_status === "finalizing" || pdf.bluebeam_status === "syncing" ? '<span class="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] font-medium bg-blue-50 text-blue-500"><svg class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>Saving...</span>' : ""}
        </td>
        <td class="py-2.5 px-2 whitespace-nowrap">${discBadge}</td>
        <td class="py-2.5 px-2">
          <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[13px] font-medium ${pdf.status === "current" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}">${rev}</span>
        </td>
        <td class="py-2.5 px-2 text-center text-[13px] text-gray-500">${pdf.comments_count || 0}</td>
        <td class="py-2.5 px-2 text-center text-[13px] hidden md:table-cell">${pdf.action_items_count > 0 ? `<span class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[13px] font-medium bg-amber-100 text-amber-700">${pdf.action_items_count}</span>` : ''}</td>
        <td class="py-2.5 px-2 text-[13px] text-gray-500 whitespace-nowrap hidden lg:table-cell">${this._esc(pdf.uploader_name || '')}</td>
        <td class="py-2.5 px-2 text-[13px] text-gray-400 whitespace-nowrap hidden lg:table-cell">${this._esc(pdf.formatted_date || '')}</td>
        <td class="py-2.5 px-2 relative" data-action="click->design-step#stopProp">
          ${canManage ? `
            <button type="button" data-action="click->design-step#toggleRowMenu" data-pdf-id="${pdf.id}"
                    class="p-1 rounded text-text-muted hover:text-text-heading transition-colors cursor-pointer">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>
            </button>
            ${this._rowMenuHtml(pdf)}
          ` : ''}
        </td>
      </tr>
    `
  }

  // The row's ⋯ menu — Bluebeam actions, compare, edit, delete. Kept apart
  // from _pdfRowHtml so the row itself stays readable.
  _rowMenuHtml(pdf) {
    const isPdf = pdf.is_pdf !== false
    const compareItem = this._hasPreviousVersion(pdf)
      ? `<button type="button" data-action="click->design-step#compareToPrevious" data-pdf-id="${pdf.id}"
                      class="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap">
                <svg class="w-4 h-4 shrink-0 text-brand" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 3v18M4.5 6.75h4.5v10.5H4.5zM15 6.75h4.5v10.5H15z"/></svg>
                Compare to previous
              </button>`
      : ""
    return `
            <div data-row-menu data-row-menu-id="${pdf.id}" class="hidden absolute right-0 top-full mt-1 w-auto bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
              ${isPdf ? `
              ${pdf.bluebeam_status === "active" ? `
              <button type="button" data-action="click->design-step#openInBluebeam" data-pdf-id="${pdf.id}"
                      class="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap">
                <svg class="w-4 h-4 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/></svg>
                Open in Bluebeam
              </button>
              <button type="button" data-action="click->design-step#showSaveMarkupsModal" data-pdf-id="${pdf.id}"
                      class="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap">
                <svg class="w-4 h-4 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"/></svg>
                Save Bluebeam Markups &amp; Close Session
              </button>
              <button type="button" data-action="click->design-step#closeBluebeamSession" data-pdf-id="${pdf.id}"
                      class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap">
                <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
                Close Bluebeam Session
              </button>
              ` : `
              <button type="button" data-action="click->design-step#openInBluebeam" data-pdf-id="${pdf.id}"
                      class="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap">
                <svg class="w-4 h-4 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/></svg>
                Markup in Bluebeam
              </button>
              `}
              <div class="border-t border-gray-100 my-1"></div>
              ` : ""}
              ${compareItem}
              <button type="button" data-action="click->design-step#openEditPdfModal" data-pdf-id="${pdf.id}" data-title="${this._esc(pdf.title || '')}" data-sheet-number="${this._esc(pdf.sheet_number || '')}" data-discipline="${this._esc(pdf.discipline || '')}" data-document-type="${pdf.document_type || 'drawing'}" data-folder-id="${pdf.folder_id || ''}"
                      class="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap">
                <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"/></svg>
                Edit
              </button>
              <button type="button" data-action="click->design-step#deletePdf" data-pdf-id="${pdf.id}" data-title="${this._esc(pdf.title || 'this PDF')}"
                      class="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap">
                <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>
                Delete
              </button>
            </div>`
  }


  // ── Grid cards ───────────────────────────────────────────────

  _folderCardHtml(folder) {
    return `<div class="relative rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                 data-action="click->design-step#openFolder" data-folder-id="${folder.id}" data-folder-name="${this._esc(folder.name)}">
      <div class="aspect-[4/3] flex items-center justify-center bg-brand/5">
        <svg class="w-12 h-12 text-brand" fill="currentColor" viewBox="0 0 24 24"><path d="M19.5 21a3 3 0 0 0 3-3v-4.5a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h15ZM1.5 10.146V6a3 3 0 0 1 3-3h5.379a2.25 2.25 0 0 1 1.59.659l2.122 2.121c.14.141.331.22.53.22H19.5a3 3 0 0 1 3 3v1.146A4.483 4.483 0 0 0 19.5 9h-15a4.483 4.483 0 0 0-3 1.146Z"/></svg>
      </div>
      <div class="px-2.5 py-2">
        <p class="text-[13px] font-medium text-gray-700 truncate">${this._esc(folder.name)}</p>
        <p class="text-[13px] text-gray-400 mt-0.5">${folder.pdf_count || 0} file${(folder.pdf_count || 0) === 1 ? "" : "s"}</p>
      </div>
    </div>`
  }

  _pdfCardHtml(pdf) {
    const muted = pdf.status !== "current" ? "opacity-50" : ""
    const checkboxHtml = this._bulkMode
      ? `<div class="absolute top-2 left-2 z-10" data-action="click->design-step#stopProp">
           <input type="checkbox" class="form-checkbox rounded" style="accent-color: var(--color-brand)"
                  data-file-checkbox data-file-id="${pdf.id}"
                  data-action="change->design-step#toggleFileSelect"
                  ${this._selectedIds.has(String(pdf.id)) ? "checked" : ""}>
         </div>`
      : ""
    const preview = pdf.is_image && pdf.thumb_url
      ? `<img src="${pdf.thumb_url}" alt="" class="w-full h-full object-cover" loading="lazy">`
      : `<div class="flex items-center justify-center h-full bg-gray-50">
           <svg class="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>
         </div>`
    const sheetOverlay = pdf.sheet_number
      ? `<div class="absolute bottom-1 left-1 bg-black/60 text-white text-[11px] font-mono font-bold px-1.5 py-0.5 rounded">${this._esc(pdf.sheet_number)}</div>`
      : ""
    const revBadge = pdf.revision_number
      ? `<span class="text-[11px] font-medium text-gray-400 ml-1">Rev ${this._esc(pdf.revision_number)}</span>`
      : ""

    return `<div class="relative rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${muted}"
                 data-action="click->design-step#openPdf" data-pdf-id="${pdf.id}">
      ${checkboxHtml}
      <div class="aspect-[4/3] overflow-hidden bg-gray-100 relative">
        ${preview}
        ${sheetOverlay}
      </div>
      <div class="px-2.5 py-2">
        <p class="text-[13px] font-medium text-gray-700 truncate">${this._esc(pdf.title || pdf.file_name || "")}</p>
        <p class="text-[13px] text-gray-400 mt-0.5">${this._esc(pdf.formatted_date || "")}${revBadge}</p>
      </div>
    </div>`
  }

  _showEmpty() {
    if (this.hasLoadingSkeletonTarget) this.loadingSkeletonTarget.classList.add("hidden")
    if (this.hasPdfTableTarget) this.pdfTableTarget.classList.add("hidden")
    if (this.hasGridContainerTarget) this.gridContainerTarget.classList.add("hidden")
    if (this.hasSentinelTarget) this.sentinelTarget.classList.add("hidden")
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
  }

  _hideEmpty() {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
  }

  // ── Folder Navigation ─────────────────────────────────────────

  openFolder(event) {
    // Don't navigate if clicking menu actions (stopProp handles this, but guard just in case)
    if (event.target.closest("[data-row-menu]")) return

    const folderId = parseInt(event.currentTarget.dataset.folderId)
    const folderName = event.currentTarget.dataset.folderName
    this._navigateToFolder(folderId, folderName)
  }

  _navigateToFolder(folderId, folderName) {
    this._currentFolderId = folderId
    this._currentFolderName = folderName
    this._updateBreadcrumbs()
    this._updateFolderBackBar()
    this._resetAndLoad()
  }

  navigateBack() {
    const path = this._buildFolderPath(this._currentFolderId)
    if (path.length > 1) {
      const parent = path[path.length - 2]
      this._navigateToFolder(parent.id, parent.name)
    } else {
      this.navigateRoot()
    }
  }

  navigateRoot() {
    this._currentFolderId = null
    this._currentFolderName = null
    this._updateBreadcrumbs()
    this._updateFolderBackBar()
    this._resetAndLoad()
  }

  _updateFolderBackBar() {
    if (!this.hasFolderBackBarTarget) return

    if (!this._currentFolderId) {
      this.folderBackBarTarget.classList.add("hidden")
      return
    }

    const path = this._buildFolderPath(this._currentFolderId)
    const parentName = path.length > 1 ? path[path.length - 2].name : this.stepNameValue
    const label = this.folderBackBarTarget.querySelector("[data-back-label]")
    if (label) label.textContent = parentName

    this.folderBackBarTarget.classList.remove("hidden")
  }

  _updateBreadcrumbs() {
    // Find the top breadcrumb <ol> in the layout
    const ol = document.querySelector('nav[aria-label="Breadcrumb"] ol')
    if (!ol) return

    // Remove any previously injected folder breadcrumb items
    ol.querySelectorAll("[data-folder-breadcrumb]").forEach(el => el.remove())

    // Find the page-title <li> (last original item) and restore it to plain text
    const titleLi = ol.querySelector("li:last-of-type")
    if (!titleLi) return

    if (!this._currentFolderId) {
      // Restore the step name to a non-clickable heading
      const titleSpan = titleLi.querySelector("span, button")
      if (titleSpan && titleSpan.tagName === "BUTTON") {
        const span = document.createElement("span")
        span.className = "text-text-heading font-semibold"
        span.textContent = this.stepNameValue
        titleSpan.replaceWith(span)
      }
      return
    }

    // Make the step name clickable
    const titleEl = titleLi.querySelector("span, button")
    if (titleEl && titleEl.tagName !== "BUTTON") {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "text-text-muted hover:text-text-body cursor-pointer"
      btn.textContent = this.stepNameValue
      btn.addEventListener("click", () => this.navigateRoot())
      titleEl.replaceWith(btn)
    }

    // Build folder path and append items to the <ol>
    const path = this._buildFolderPath(this._currentFolderId)
    const chevronSvg = '<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/></svg>'

    path.forEach((item, i) => {
      // Chevron separator
      const sepLi = document.createElement("li")
      sepLi.className = "text-text-muted"
      sepLi.setAttribute("data-folder-breadcrumb", "")
      sepLi.innerHTML = chevronSvg
      ol.appendChild(sepLi)

      // Folder name
      const itemLi = document.createElement("li")
      itemLi.setAttribute("data-folder-breadcrumb", "")

      if (i < path.length - 1) {
        const btn = document.createElement("button")
        btn.type = "button"
        btn.className = "text-text-muted hover:text-text-body cursor-pointer"
        btn.textContent = item.name
        btn.addEventListener("click", () => this._navigateToFolder(item.id, item.name))
        itemLi.appendChild(btn)
      } else {
        const span = document.createElement("span")
        span.className = "text-text-heading font-semibold"
        span.textContent = item.name
        itemLi.appendChild(span)
      }
      ol.appendChild(itemLi)
    })
  }

  navigateBreadcrumb(event) {
    const folderId = parseInt(event.currentTarget.dataset.folderId)
    const folderName = event.currentTarget.dataset.folderName
    this._navigateToFolder(folderId, folderName)
  }

  _buildFolderPath(folderId) {
    const path = []
    const find = (folders, id) => {
      for (const f of folders) {
        if (f.id === id) {
          path.push({ id: f.id, name: f.name })
          return true
        }
        if (f.children && f.children.length > 0) {
          if (find(f.children, id)) {
            path.unshift({ id: f.id, name: f.name })
            return true
          }
        }
      }
      return false
    }
    find(this._folders, folderId)
    return path
  }

  _findSubFolders(parentId) {
    const find = (folders) => {
      for (const f of folders) {
        if (f.id === parentId) return f.children || []
        if (f.children) {
          const result = find(f.children)
          if (result) return result
        }
      }
      return null
    }
    return find(this._folders) || []
  }

  // ── Type / version / discipline filters ──────────────────────
  // All three are chips in the Filters panel (Phelipe 2026-09-14) — they
  // were separate dropdowns spread across the toolbar while the button
  // named Filters held only dates.

  // "Open last preview" (Larry 2026-08-06) — the viewer controller owns
  // the saved tabs; this toolbar button just rings its bell.
  openLastPreview() {
    window.dispatchEvent(new CustomEvent("conslog:open-last-preview"))
  }

  filterVersion(event) {
    this._versionFilter = event.currentTarget.dataset.version || ""
    this._paintChips(this.hasVersionChipsTarget ? this.versionChipsTarget : null, "version", this._versionFilter)
    this._resetAndLoad()
    this._updateFilterBadge()
  }

  filterType(event) {
    this._typeFilter = event.currentTarget.dataset.docType || ""
    this._paintChips(this.hasTypeChipsTarget ? this.typeChipsTarget : null, "docType", this._typeFilter)
    this._resetAndLoad()
    this._updateFilterBadge()
  }

  // Lights the chip whose data-<key> equals value; every other chip in the
  // row goes back to idle.
  _paintChips(container, key, value) {
    if (!container) return
    container.querySelectorAll("button").forEach(btn => {
      const on = (btn.dataset[key] || "") === (value || "")
      btn.className = (btn.className.replace(CHIP_ON, "").replace(CHIP_OFF, "") + " " + (on ? CHIP_ON : CHIP_OFF))
        .replace(/\s+/g, " ").trim()
    })
  }

  // ── Search ────────────────────────────────────────────────────

  search() {
    if (this._aiSources) this._clearAiState() // typing replaces the AI filter
    this._toggleSearchClear()
    clearTimeout(this._searchTimeout)
    this._searchTimeout = setTimeout(() => {
      this._searchTerm = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""
      this._filters.search = this._searchTerm
      this._resetAndLoad()
      this._persistFilters()
    }, 300)
  }

  // "×" inside the search box — wipes the term and reloads the list.
  clearSearch() {
    clearTimeout(this._searchTimeout)
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    this._searchTerm = ""
    this._filters.search = ""
    this._toggleSearchClear()
    this._resetAndLoad()
    this._persistFilters()
    if (this.hasSearchInputTarget) this.searchInputTarget.focus()
  }

  // The × is only visible while there's something to clear.
  _toggleSearchClear() {
    if (this.hasSearchClearTarget && this.hasSearchInputTarget) {
      this.searchClearTarget.classList.toggle("hidden", !this.searchInputTarget.value)
    }
  }

  // ── Column Sorting ──────────────────────────────────────────────

  sortColumn(event) {
    const key = event.currentTarget.dataset.sortKey
    const table = "current" // versions nest under their sheet, so there's one table
    const sort = this._sortCurrent

    if (sort.key === key) {
      sort.order = sort.order === "asc" ? "desc" : "asc"
    } else {
      sort.key = key
      sort.order = "asc"
    }

    this._renderPdfTable()
    this._persistSort(table, sort.key, sort.order)
  }

  _parseInitialSort() {
    try {
      const raw = this.initialSortValue
      if (!raw) return {}
      const parsed = JSON.parse(raw)
      const result = {}
      if (parsed.current) result.current = { key: parsed.current.sort_by, order: parsed.current.sort_order }
      if (parsed.old) result.old = { key: parsed.old.sort_by, order: parsed.old.sort_order }
      return result
    } catch { return {} }
  }

  _sortPdfs(pdfs, sort) {
    // Combined packs stay pinned above their extracted sheets in every order.
    const combinedFirst = (a, b) => (!!a.combined === !!b.combined ? 0 : a.combined ? -1 : 1)
    if (!sort.key) return [...pdfs].sort(combinedFirst)
    const sorted = [...pdfs]
    const dir = sort.order === "desc" ? -1 : 1
    sorted.sort((a, b) => {
      const cf = combinedFirst(a, b)
      if (cf !== 0) return cf
      let va, vb
      switch (sort.key) {
        case "discipline":
          va = (a.discipline || "").toLowerCase()
          vb = (b.discipline || "").toLowerCase()
          return va < vb ? -dir : va > vb ? dir : 0
        case "document_type":
          va = (a.document_type || "drawing").toLowerCase()
          vb = (b.document_type || "drawing").toLowerCase()
          return va < vb ? -dir : va > vb ? dir : 0
        case "sheet_number":
          // Natural compare: C-1 < C-2 < C-10 (a plain string compare would
          // wedge C-10 between C-1 and C-2).
          return (a.sheet_number || "").localeCompare(b.sheet_number || "", undefined, { numeric: true, sensitivity: "base" }) * dir
        case "title":
          va = (a.title || "").toLowerCase()
          vb = (b.title || "").toLowerCase()
          return va < vb ? -dir : va > vb ? dir : 0
        case "action_items":
          va = a.action_items_count || 0
          vb = b.action_items_count || 0
          return (va - vb) * dir
        case "uploaded_by":
          va = (a.uploader_name || "").toLowerCase()
          vb = (b.uploader_name || "").toLowerCase()
          return va < vb ? -dir : va > vb ? dir : 0
        case "date":
          va = a.created_at || ""
          vb = b.created_at || ""
          return va < vb ? -dir : va > vb ? dir : 0
        default:
          return 0
      }
    })
    return sorted
  }

  _updateSortIcons() {
    const arrowUp = `<svg class="w-3.5 h-3.5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5"/></svg>`
    const arrowDown = `<svg class="w-3.5 h-3.5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg>`

    const tables = { current: this._sortCurrent }
    const keys = ["document_type", "sheet_number", "title", "discipline", "action_items", "uploaded_by", "date"]
    const targetNames = { document_type: "DocumentType", sheet_number: "SheetNumber", title: "Title", discipline: "Discipline", action_items: "ActionItems", uploaded_by: "UploadedBy", date: "Date" }

    for (const [table, sort] of Object.entries(tables)) {
      const prefix = table === "current" ? "Current" : "Old"
      for (const key of keys) {
        const targetName = `sortIcon${prefix}${targetNames[key]}Target`
        const hasTarget = `hasSortIcon${prefix}${targetNames[key]}Target`
        if (this[hasTarget]) {
          if (sort.key === key) {
            this[targetName].innerHTML = sort.order === "asc" ? arrowUp : arrowDown
          } else {
            this[targetName].innerHTML = ""
          }
        }
      }
    }
  }

  _persistSort(table, key, order) {
    if (!this.saveSortUrlValue) return
    const token = this._csrfToken()
    fetch(this.saveSortUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      credentials: "same-origin",
      body: JSON.stringify({ table, sort_by: key, sort_order: order })
    })
  }

  // ── Folder CRUD ───────────────────────────────────────────────

  openNewFolderModal() {
    if (this.hasFolderModalTitleTarget) this.folderModalTitleTarget.textContent = "New Folder"
    if (this.hasFolderNameInputTarget) this.folderNameInputTarget.value = ""
    if (this.hasFolderEditIdTarget) this.folderEditIdTarget.value = ""
    this._clearPrivacyCheckboxes()
    // Default new folders to be visible to the creator's own role group (e.g. a
    // consultant's folder is shared with Consultants) so their teammates can see it.
    this._checkDefaultShareRole()
    // And always to Consultants (Phelipe 2026-09-14): the designers need the
    // design documents, and an owner's folder used to come out private — the
    // consultant saw the comment badge but no files (Robert Conklin, Mendon
    // Whitney). Still one click to untick before saving.
    if (this.hasPrivacyConsultantsTarget) this.privacyConsultantsTarget.checked = true
    if (this.hasFolderModalTarget) this.folderModalTarget.classList.remove("hidden")
  }

  _checkDefaultShareRole() {
    const role = this.hasDefaultShareRoleValue ? this.defaultShareRoleValue : ""
    const checkbox = {
      consultants: this.hasPrivacyConsultantsTarget ? this.privacyConsultantsTarget : null,
      contractors: this.hasPrivacyContractorsTarget ? this.privacyContractorsTarget : null,
      other_vendors: this.hasPrivacyOtherVendorsTarget ? this.privacyOtherVendorsTarget : null,
      collaborators: this.hasPrivacyCollaboratorsTarget ? this.privacyCollaboratorsTarget : null
    }[role]
    if (checkbox) checkbox.checked = true
  }

  closeFolderModal() {
    if (this.hasFolderModalTarget) this.folderModalTarget.classList.add("hidden")
  }

  async saveFolder() {
    const name = this.hasFolderNameInputTarget ? this.folderNameInputTarget.value.trim() : ""
    if (!name) return

    const editId = this.hasFolderEditIdTarget ? this.folderEditIdTarget.value : ""
    const privacySettings = this._getPrivacySettings()
    const token = this._csrfToken()

    try {
      let resp
      if (editId) {
        // Update existing folder
        const url = this.updateFolderUrlValue.replace("__FOLDER_ID__", editId)
        resp = await fetch(url, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
          credentials: "same-origin",
          body: JSON.stringify({ name, privacy_settings: privacySettings })
        })
      } else {
        // Create new folder
        resp = await fetch(this.saveFolderUrlValue, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
          credentials: "same-origin",
          body: JSON.stringify({
            name,
            parent_id: this._currentFolderId || null,
            privacy_settings: privacySettings
          })
        })
      }
      if (!resp.ok) throw new Error("Request failed")
      this.closeFolderModal()
      this._loadInitial()
      this._showToast(editId ? "Folder updated" : "Folder created")
    } catch (e) {
      console.error("[design-step] Save folder error:", e)
      this._showAlert("Error", "Failed to save folder. Please try again.")
    }
  }

  openEditFolderModal(event) {
    event.stopPropagation()
    const el = event.currentTarget
    if (this.hasFolderModalTitleTarget) this.folderModalTitleTarget.textContent = "Edit Folder"
    if (this.hasFolderNameInputTarget) this.folderNameInputTarget.value = el.dataset.folderName || ""
    if (this.hasFolderEditIdTarget) this.folderEditIdTarget.value = el.dataset.folderId || ""

    // Load existing privacy settings into checkboxes
    this._clearPrivacyCheckboxes()
    try {
      const privacy = JSON.parse(el.dataset.folderPrivacy || '["private"]')
      if (this.hasPrivacyConsultantsTarget) this.privacyConsultantsTarget.checked = privacy.includes("consultants")
      if (this.hasPrivacyContractorsTarget) this.privacyContractorsTarget.checked = privacy.includes("contractors")
      if (this.hasPrivacyOtherVendorsTarget) this.privacyOtherVendorsTarget.checked = privacy.includes("other_vendors")
      if (this.hasPrivacyCollaboratorsTarget) this.privacyCollaboratorsTarget.checked = privacy.includes("collaborators")
    } catch (_) { /* ignore parse errors */ }

    if (this.hasFolderModalTarget) this.folderModalTarget.classList.remove("hidden")
  }

  deleteFolder(event) {
    event.stopPropagation()
    const folderId = event.currentTarget.dataset.folderId
    const folderName = event.currentTarget.dataset.folderName || "this folder"

    document.querySelectorAll("[data-row-menu]").forEach(m => m.classList.add("hidden"))
    this._deleteFolderId = folderId
    if (this.hasDeleteFolderNameTarget) this.deleteFolderNameTarget.textContent = folderName
    if (this.hasDeleteFolderModalTarget) this.deleteFolderModalTarget.classList.remove("hidden")
  }

  closeDeleteFolderModal() {
    if (this.hasDeleteFolderModalTarget) this.deleteFolderModalTarget.classList.add("hidden")
    this._deleteFolderId = null
  }

  async confirmDeleteFolder() {
    const folderId = this._deleteFolderId
    if (!folderId) return

    this.closeDeleteFolderModal()

    try {
      const resp = await fetch(this.deleteFolderUrlValue, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
        credentials: "same-origin",
        body: JSON.stringify({ folder_id: folderId })
      })
      if (!resp.ok) throw new Error("Request failed")
      this._loadInitial()
      this._bumpTrashBadge(1)
      this._showToast("Folder deleted")
    } catch (e) {
      console.error("[design-step] Delete folder error:", e)
      this._showAlert("Error", "Failed to delete folder. Please try again.")
    }
  }

  _getPrivacySettings() {
    const settings = ["private"]
    if (this.hasPrivacyConsultantsTarget && this.privacyConsultantsTarget.checked) settings.push("consultants")
    if (this.hasPrivacyContractorsTarget && this.privacyContractorsTarget.checked) settings.push("contractors")
    if (this.hasPrivacyOtherVendorsTarget && this.privacyOtherVendorsTarget.checked) settings.push("other_vendors")
    if (this.hasPrivacyCollaboratorsTarget && this.privacyCollaboratorsTarget.checked) settings.push("collaborators")
    return settings
  }

  _clearPrivacyCheckboxes() {
    if (this.hasPrivacyConsultantsTarget) this.privacyConsultantsTarget.checked = false
    if (this.hasPrivacyContractorsTarget) this.privacyContractorsTarget.checked = false
    if (this.hasPrivacyOtherVendorsTarget) this.privacyOtherVendorsTarget.checked = false
    if (this.hasPrivacyCollaboratorsTarget) this.privacyCollaboratorsTarget.checked = false
  }

  // ── Upload Flow ───────────────────────────────────────────────

  openUploadModal() {
    this._uploadFiles = []
    this._splitCancelled = false
    this._resetDupBanner()
    if (this.hasUploadSheetRowsTarget) this.uploadSheetRowsTarget.innerHTML = ""
    if (this.hasUploadSheetTableTarget) this.uploadSheetTableTarget.classList.add("hidden")
    if (this.hasUploadAiStatusTarget) { this.uploadAiStatusTarget.classList.add("hidden"); this.uploadAiStatusTarget.classList.remove("flex") }
    this.uploadSubmitBtnTarget.disabled = true
    this._updateUploadBtnText()
    this._populateFolderSelect()
    this.uploadModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  closeUploadModal() {
    this.uploadModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    this._closeUploadMenu()
    this._uploadFiles = []
    this._splitStates = []
    this._splitCancelled = true
    clearInterval(this._aiTimerInterval)
    this.uploadAiStatusTarget.classList.add("hidden")
    this.uploadAiStatusTarget.classList.remove("flex")
  }

  selectFiles() {
    if (this.hasFileInputTarget) this.fileInputTarget.click()
  }

  addFiles(eventOrFileList) {
    const files = eventOrFileList.target ? eventOrFileList.target.files : eventOrFileList
    if (!files || files.length === 0) return

    const MAX_SIZE = 250 * 1024 * 1024
    for (const file of files) {
      const ext = file.name.split(".").pop()?.toLowerCase()
      const isCad = ext === "dwg"
      if (ext !== "pdf" && !isCad) {
        // Say WHY, not just no — Luiz spent days thinking DWG uploads were
        // broken (Luma call 2026-08-28). Markup still only works on PDFs
        // (Phelipe 2026-08-31); DWG now rides along for reference (E5-3).
        const hint = ext === "dxf"
          ? `Design takes PDFs and DWG files — save the .dxf as DWG, or export it to PDF, and upload that.`
          : `Design takes PDFs and DWG files — .${ext} files can't be uploaded here.`
        this._showToast(hint, "error")
        continue
      }
      if (file.size > MAX_SIZE) {
        this._showToast(`${file.name} exceeds 250MB limit`, "error")
        continue
      }
      const id = Date.now() + Math.round(Math.random() * 1000)
      if (isCad) {
        // The AI sheet reading only understands PDFs, so a DWG would never
        // get a row (or unlock Upload) — it gets its row now, marked done.
        this._uploadFiles.push({ id, file, sheetNumber: "", title: "", discipline: "",
          _extracted: true, _splitChecked: true, _documentType: "drawing" })
        this._renderUploadSheetRow(id, file, null)
        if (this.hasUploadSheetTableTarget) this.uploadSheetTableTarget.classList.remove("hidden")
        continue
      }
      this._uploadFiles.push({ id, file, sheetNumber: "", title: "", discipline: "" })
    }

    // Table will be shown after AI extraction renders rows
    this.uploadSubmitBtnTarget.disabled = this._uploadFiles.length === 0
    this._updateUploadBtnText()
    if (eventOrFileList.target) eventOrFileList.target.value = ""

    // Keep button disabled while AI analyzes PDFs
    if (this._uploadFiles.some(uf => !uf._extracted)) {
      this.uploadSubmitBtnTarget.disabled = true
      this.uploadSubmitBtnTarget.innerHTML = `<span class="inline-flex items-center gap-1.5"><svg class="w-3.5 h-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Extracting</span>`
    }

    // Check for combined PDFs first, then AI extraction for remaining
    this._checkForCombinedPdfs()
  }

  _renderUploadSheetRow(id, file, prefilled) {
    const uniqueDiscs = [...new Set(Object.values(DISCIPLINE_PREFIXES))]
    const disciplineOptions = uniqueDiscs.map(name =>
      `<option value="${name}" ${prefilled?.discipline === name ? "selected" : ""}>${name}</option>`
    ).join("")

    const preClass = prefilled ? "bg-blue-50 border-blue-200" : "border-gray-300"

    this.uploadSheetRowsTarget.insertAdjacentHTML("beforeend", `
      <tr data-upload-row="${id}" data-doc-type="drawing" class="border-b border-gray-100">
        <td class="py-1.5 pr-3">
          <div class="flex items-center gap-1.5 min-w-0">
            ${typeBadgeHtml("drawing")}
            <span class="text-[13px] text-gray-700 truncate" title="${this._esc(file.name)}">${this._esc(file.name)}</span>
            ${file.name.toLowerCase().endsWith(".dwg") ? cadBadgeHtml() : ""}
          </div>
          <div class="relative h-1 rounded-full mt-1 hidden" data-progress-track>
            <div data-progress-bar class="h-full bg-brand rounded-full transition-all duration-200" style="width: 0%"></div>
          </div>
        </td>
        <td class="py-1.5 px-3">
          <input type="text" class="w-full text-[13px] rounded-lg border ${preClass} focus:border-brand focus:ring-0 py-1 px-2 font-mono"
                 placeholder="A-101"
                 value="${prefilled?.sheetNumber ? this._esc(prefilled.sheetNumber) : ""}"
                 data-upload-id="${id}"
                 data-action="input->design-step#uploadSheetNumberChanged blur->design-step#uploadSheetNumberBlur">
        </td>
        <td class="py-1.5 px-3">
          <input type="text" data-title-input class="w-full text-[13px] rounded-lg border ${preClass} focus:border-brand focus:ring-0 py-1 px-2"
                 placeholder="Drawing title"
                 value="${prefilled?.title ? this._esc(prefilled.title) : ""}"
                 data-upload-id="${id}"
                 data-action="input->design-step#uploadTitleChanged">
        </td>
        <td class="py-1.5 px-3">
          <select data-discipline-input class="w-full text-[13px] rounded-lg border ${preClass} focus:border-brand focus:ring-0 py-1 px-2"
                  data-upload-id="${id}"
                  data-action="change->design-step#uploadDisciplineChanged">
            <option value="">—</option>
            ${disciplineOptions}
          </select>
        </td>
        <td class="py-1.5 pl-3">
          <button type="button" class="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                  data-upload-id="${id}" data-action="click->design-step#removeUploadFile">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          </button>
        </td>
      </tr>`)
  }

  _renderNonDrawingRow(id, file, docType = "document") {
    const titleFromName = file.name.replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").trim()
    const badge = typeBadgeHtml(docType)
    this.uploadSheetRowsTarget.insertAdjacentHTML("beforeend", `
      <tr data-upload-row="${id}" class="border-b border-gray-100">
        <td class="py-1.5 pr-3">
          <div class="flex items-center gap-1.5 min-w-0">
            ${badge}
            <span class="text-[13px] text-gray-700 truncate" title="${this._esc(file.name)}">${this._esc(file.name)}</span>
          </div>
          <div class="relative h-1 rounded-full mt-1 hidden" data-progress-track>
            <div data-progress-bar class="h-full bg-brand rounded-full transition-all duration-200" style="width: 0%"></div>
          </div>
        </td>
        <td class="py-1.5 px-3" colspan="2">
          <input type="text" data-title-input class="w-full text-[13px] rounded-lg border border-gray-300 focus:border-brand focus:ring-0 py-1 px-2"
                 placeholder="Document title"
                 value="${this._esc(titleFromName)}"
                 data-upload-id="${id}"
                 data-action="input->design-step#uploadTitleChanged">
        </td>
        <td class="py-1.5 px-3"></td>
        <td class="py-1.5 pl-3">
          <button type="button" class="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                  data-upload-id="${id}" data-action="click->design-step#removeUploadFile">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          </button>
        </td>
      </tr>`)
  }

  _renderSpecUploadRow(id, file, prefilled) {
    const uniqueDivisions = [...new Set(Object.values(CSI_DIVISIONS))]
    const divisionOptions = uniqueDivisions.map(name =>
      `<option value="${name}" ${prefilled?.division === name ? "selected" : ""}>${name}</option>`
    ).join("")

    const preClass = prefilled ? "bg-purple-50 border-purple-200" : "border-gray-300"

    this.uploadSheetRowsTarget.insertAdjacentHTML("beforeend", `
      <tr data-upload-row="${id}" data-doc-type="specification" class="border-b border-gray-100">
        <td class="py-1.5 pr-3">
          <div class="flex items-center gap-1.5 min-w-0">
            ${typeBadgeHtml("specification")}
            <span class="text-[13px] text-gray-700 truncate" title="${this._esc(file.name)}">${this._esc(file.name)}</span>
          </div>
          <div class="relative h-1 rounded-full mt-1 hidden" data-progress-track>
            <div data-progress-bar class="h-full bg-brand rounded-full transition-all duration-200" style="width: 0%"></div>
          </div>
        </td>
        <td class="py-1.5 px-3">
          <input type="text" class="w-full text-[13px] rounded-lg border ${preClass} focus:border-brand focus:ring-0 py-1 px-2 font-mono"
                 placeholder="03 30 00"
                 value="${prefilled?.sectionNumber ? this._esc(prefilled.sectionNumber) : ""}"
                 data-upload-id="${id}"
                 data-action="input->design-step#uploadSectionNumberChanged blur->design-step#uploadSectionNumberBlur">
        </td>
        <td class="py-1.5 px-3">
          <input type="text" data-title-input class="w-full text-[13px] rounded-lg border ${preClass} focus:border-brand focus:ring-0 py-1 px-2"
                 placeholder="Section title"
                 value="${prefilled?.title ? this._esc(prefilled.title) : ""}"
                 data-upload-id="${id}"
                 data-action="input->design-step#uploadTitleChanged">
        </td>
        <td class="py-1.5 px-3">
          <select data-discipline-input class="w-full text-[13px] rounded-lg border ${preClass} focus:border-brand focus:ring-0 py-1 px-2"
                  data-upload-id="${id}"
                  data-action="change->design-step#uploadDisciplineChanged">
            <option value="">—</option>
            ${divisionOptions}
          </select>
        </td>
        <td class="py-1.5 pl-3">
          <button type="button" class="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                  data-upload-id="${id}" data-action="click->design-step#removeUploadFile">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          </button>
        </td>
      </tr>`)
  }

  uploadSectionNumberChanged(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    const uf = this._uploadFiles.find(f => f.id === id)
    if (!uf) return
    uf.sheetNumber = event.currentTarget.value.trim()

    // Auto-detect division
    const div = detectDivision(uf.sheetNumber)
    if (div) {
      uf.discipline = div
      const divInput = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${id}"] [data-discipline-input]`)
      if (divInput) divInput.value = div
    }
  }

  async uploadSectionNumberBlur(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    const uf = this._uploadFiles.find(f => f.id === id)
    if (!uf || !uf.sheetNumber) return

    const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${id}"]`)
    if (!row) return
    const sectionInput = event.currentTarget

    try {
      const token = this._csrfToken()
      const res = await fetch(this.checkSectionsUrlValue + `?s=${encodeURIComponent(this.stepNameValue)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ section_numbers: [uf.sheetNumber] })
      })
      const results = await res.json()
      const result = results[0]

      const existingLabel = row.querySelector("[data-sheet-label]")
      if (existingLabel) existingLabel.remove()
      const fileNameSpan = row.querySelector("td:first-child span.text-\\[13px\\]")

      if (result.exists) {
        sectionInput.title = `Revises ${result.section_number} (currently Rev ${result.current_revision || "?"})`
        sectionInput.classList.add("bg-amber-50", "border-amber-300")
        sectionInput.classList.remove("bg-purple-50", "border-purple-200")
        fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[10px] font-bold text-amber-500 ml-1.5 whitespace-nowrap">REV</span>`)
      } else {
        sectionInput.title = "New section"
        fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[10px] font-bold text-emerald-500 ml-1.5 whitespace-nowrap">NEW</span>`)
      }

      // Populate title if not manually set
      if (result.title && uf.title === "") {
        uf.title = result.title
        const titleInput = row.querySelector("[data-title-input]")
        if (titleInput && !titleInput.value) titleInput.value = result.title
      }
      if (result.division) {
        uf.discipline = result.division
        const divInput = row.querySelector("[data-discipline-input]")
        if (divInput) divInput.value = result.division
      }
    } catch (e) {
      console.error("[design-step] check_sections failed:", e)
    }
  }

  removeUploadFile(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    this._uploadFiles = this._uploadFiles.filter(f => f.id !== id)
    const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${id}"]`)
    if (row) row.remove()
    if (this._uploadFiles.length === 0 && this.hasUploadSheetTableTarget) {
      this.uploadSheetTableTarget.classList.add("hidden")
    }
    this.uploadSubmitBtnTarget.disabled = this._uploadFiles.length === 0
    this._updateUploadBtnText()
  }

  _updateUploadBtnText() {
    // Files already on the step don't count while they're being skipped.
    const active = this._filesForMode("both")
    const count = active.length
    // The split button's menu only applies when a combined original is kept.
    const hasCombined = active.some(uf => uf._isCombined)
    if (this.hasUploadMenuBtnTarget) this.uploadMenuBtnTarget.classList.toggle("hidden", !hasCombined)
    if (!hasCombined) this._closeUploadMenu()
    this.uploadSubmitBtnTarget.classList.remove("rounded-lg", "rounded-l-lg")
    this.uploadSubmitBtnTarget.classList.add(hasCombined ? "rounded-l-lg" : "rounded-lg")
    this._setMenuCounts()
    if (count === 0) {
      this.uploadSubmitBtnTarget.textContent = this._uploadFiles.length ? "Nothing new to upload" : "Upload"
      return
    }
    const parts = active.filter(uf => !uf._isCombined)
    const hasSpec = parts.some(uf => uf._documentType === "specification")
    const hasDrawing = parts.some(uf => !uf._documentType || uf._documentType === "drawing")
    const hasDoc = parts.some(uf => uf._documentType === "document")

    let label = "file"
    if (hasDrawing && !hasSpec && !hasDoc) label = "sheet"
    else if (hasSpec && !hasDrawing && !hasDoc) label = "section"
    if (hasCombined) {
      const n = parts.length
      this.uploadSubmitBtnTarget.textContent = n === 0
        ? "Upload Combined"
        : `Upload Combined & Extract ${n} ${label[0].toUpperCase()}${label.slice(1)}${n === 1 ? "" : "s"}`
      if (this.hasUploadMenuDefaultTarget) this.uploadMenuDefaultTarget.textContent = `Upload Combined & Extract ${label[0].toUpperCase()}${label.slice(1)}s`
    } else {
      this.uploadSubmitBtnTarget.textContent = `Upload ${count} ${label}${count > 1 ? "s" : ""}`
    }

    // Show column headers when there are drawing or spec rows with extractable fields
    if (this.hasUploadSheetHeadTarget) {
      const onlyDocs = !hasDrawing && !hasSpec && hasDoc
      this.uploadSheetHeadTarget.classList.toggle("hidden", onlyDocs)
    }
  }

  uploadSheetNumberChanged(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    const uf = this._uploadFiles.find(f => f.id === id)
    if (!uf) return
    uf.sheetNumber = event.currentTarget.value.trim()

    // Auto-detect discipline
    const disc = detectDiscipline(uf.sheetNumber)
    if (disc) {
      uf.discipline = disc
      const discInput = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${id}"] [data-discipline-input]`)
      if (discInput) discInput.value = disc
    }
  }

  async uploadSheetNumberBlur(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    const uf = this._uploadFiles.find(f => f.id === id)
    if (!uf || !uf.sheetNumber) return

    const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${id}"]`)
    if (!row) return
    const sheetInput = event.currentTarget

    try {
      const token = this._csrfToken()
      const res = await fetch(this.checkSheetsUrlValue + `?s=${encodeURIComponent(this.stepNameValue)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ sheet_numbers: [uf.sheetNumber] })
      })
      const results = await res.json()
      const result = results[0]

      const existingLabel = row.querySelector("[data-sheet-label]")
      if (existingLabel) existingLabel.remove()
      const fileNameSpan = row.querySelector("td:first-child span")

      if (result && result.exists) {
        sheetInput.title = `Revises ${result.sheet_number} (currently Rev ${result.current_revision || "?"})`
        sheetInput.classList.add("bg-amber-50", "border-amber-300")
        sheetInput.classList.remove("bg-blue-50", "border-blue-200")
        fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[10px] font-bold text-amber-500 ml-1.5 whitespace-nowrap">REV</span>`)
        if (result.title && !uf.title) {
          uf.title = result.title
          const titleInput = row.querySelector('[data-title-input]')
          if (titleInput) titleInput.value = result.title
        }
      } else {
        sheetInput.title = "New sheet"
        sheetInput.classList.remove("bg-amber-50", "border-amber-300")
        fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[10px] font-bold text-emerald-500 ml-1.5 whitespace-nowrap">NEW</span>`)
      }
    } catch (e) {
      console.error("[design-step] check_sheets failed:", e)
    }
  }

  uploadTitleChanged(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    const uf = this._uploadFiles.find(f => f.id === id)
    if (uf) uf.title = event.currentTarget.value.trim()
  }

  uploadDisciplineChanged(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    const uf = this._uploadFiles.find(f => f.id === id)
    if (uf) uf.discipline = event.currentTarget.value
  }

  // ── AI Sheet Detection ──────────────────────────────────────────

  async _checkForCombinedPdfs() {
    const candidates = this._uploadFiles.filter(uf => {
      if (uf._splitChecked) return false
      const ext = uf.file.name.split(".").pop()?.toLowerCase()
      return ext === "pdf"
    })

    if (candidates.length === 0) {
      this._extractMetadataForNewFiles()
      return
    }

    candidates.forEach(uf => { uf._splitChecked = true })
    this._splitCancelled = false
    this._splitStates = []

    // Show AI banner with timer
    this.uploadAiStatusTarget.classList.remove("hidden")
    this.uploadAiStatusTarget.classList.add("flex")
    this._aiTimerStart = performance.now()
    clearInterval(this._aiTimerInterval)
    const updateBannerText = (msg) => {
      const secs = Math.round((performance.now() - this._aiTimerStart) / 1000)
      this.uploadAiStatusTextTarget.innerHTML = `<span class="font-bold ai-rainbow-text">ConsLog AI — ${msg}</span> <span data-ai-timer class="text-gray-500 font-normal tabular-nums inline-block w-8 text-left">${secs}s</span>`
    }
    updateBannerText("Scanning pages...")
    this._aiTimerInterval = setInterval(() => {
      const timerEl = this.uploadAiStatusTextTarget.querySelector("[data-ai-timer]")
      if (timerEl) timerEl.textContent = `${Math.round((performance.now() - this._aiTimerStart) / 1000)}s`
    }, 1000)
    const icon = this.uploadAiStatusTarget.querySelector("svg")
    if (icon) { icon.classList.add("ai-rainbow-icon"); icon.style.color = "" }

    let anySplit = false

    for (const uf of candidates) {
      if (this._splitCancelled) break

      try {
        const arrayBuffer = await uf.file.arrayBuffer()

        // Phase 1: Extract page headers (client-side, fast)
        const result = await extractPageHeaders(arrayBuffer, (progress) => {
          if (this._splitCancelled) return
          if (progress.phase === "scanning" && progress.current) {
            updateBannerText(`Scanning pages... ${progress.current} of ${progress.total}`)
          }
        }, 2) // minPages=2: single-page PDFs skip classification and go to per-file metadata extraction

        if (!result || this._splitCancelled) continue

        // Phase 2: Send page headers to LLM for classification + analysis
        updateBannerText("Classifying file...")

        const token = this._csrfToken()
        const res = await fetch(this.analyzeCombinedUrlValue, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
          credentials: "same-origin",
          body: JSON.stringify({ pages: result.pages })
        })
        const data = await res.json()
        const documentType = data.document_type

        // ── Specifications: split by CSI sections ──
        if (documentType === "specifications") {
          const sections = data.sections || []
          console.log(`[design-step] LLM found ${sections.length} spec sections:`, sections.map(s => `${s.section_number} — ${s.title} (p${s.start_page})`))

          if (sections.length < 2 || this._splitCancelled) {
            // Single section or couldn't extract — treat as single spec file
            updateBannerText(`Detected specification — added as single file`)
            uf._extracted = true
            uf._documentType = "specification"
            uf.title = uf.file.name.replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").trim()
            this._renderNonDrawingRow(uf.id, uf.file, "specification")
            if (this.hasUploadSheetTableTarget) this.uploadSheetTableTarget.classList.remove("hidden")
            continue
          }

          updateBannerText(`Detected specification — splitting into ${sections.length} sections...`)
          const splitter = splitSpecBySections(uf.file, arrayBuffer, sections, result.numPages, (progress) => {
            if (progress.phase === "splitting" && progress.current) {
              updateBannerText(`Splitting specification... ${progress.current} of ${progress.total} sections`)
            }
          })

          let firstSection = true
          let splitCount = 0
          const splitSectionNumbers = []
          const splitEntries = []
          // The book and the sections cut from it share a key, so the server
          // files the sections under the book (Phelipe 2026-10-06).
          const packKey = `p${uf.id}`

          for await (const section of splitter) {
            if (this._splitCancelled) break
            if (section === null) break

            if (firstSection) {
              firstSection = false
              anySplit = true
              this._uploadFiles = this._uploadFiles.filter(f => f.id !== uf.id)
              const origRow = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uf.id}"]`)
              if (origRow) origRow.remove()
            }

            const id = Date.now() + Math.round(Math.random() * 1000)
            const entry = {
              id,
              file: section.file,
              sheetNumber: section.sectionNumber,
              title: section.title,
              discipline: section.division,
              _extracted: true,
              _splitChecked: true,
              _documentType: "specification",
              _packKey: packKey
            }
            this._uploadFiles.push(entry)
            splitEntries.push(entry)
            this._renderSpecUploadRow(id, section.file, {
              sectionNumber: section.sectionNumber,
              title: section.title,
              division: section.division
            })
            splitSectionNumbers.push(section.sectionNumber)
            splitCount++

            if (this.hasUploadSheetTableTarget) {
              this.uploadSheetTableTarget.classList.remove("hidden")
            }
          }

          if (splitSectionNumbers.length > 0) {
            this._batchCheckSections(splitSectionNumbers)
          }

          if (splitCount > 0) {
            clearInterval(this._aiTimerInterval)
            const totalSecs = Math.round((performance.now() - this._aiTimerStart) / 1000)
            this.uploadAiStatusTextTarget.innerHTML = `<span style="color: #60a5ee; font-weight: bold;">ConsLog AI — Specification detected, ${splitCount} sections extracted</span> <span class="text-gray-500 font-normal tabular-nums inline-block w-8 text-left">${totalSecs}s</span>`
            const ic = this.uploadAiStatusTarget.querySelector(".ai-rainbow-icon")
            if (ic) { ic.classList.remove("ai-rainbow-icon"); ic.style.color = "#60a5ee" }

            this._splitStates.push({
              originalFile: uf.file,
              documentType: "specification",
              splitEntries,
              splitCount
            })
            this._keepCombinedOriginal(uf.file, "specification", packKey)
          }
          continue
        }

        // ── Other (reports, contracts, etc.): single file, no extraction ──
        if (documentType === "other") {
          console.log(`[design-step] ${uf.file.name} classified as document — skipping extraction`)
          uf._extracted = true
          uf._documentType = "document"
          uf.title = uf.file.name.replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").trim()
          this._renderNonDrawingRow(uf.id, uf.file, "document")
          if (this.hasUploadSheetTableTarget) this.uploadSheetTableTarget.classList.remove("hidden")

          clearInterval(this._aiTimerInterval)
          const totalSecs = Math.round((performance.now() - this._aiTimerStart) / 1000)
          this.uploadAiStatusTextTarget.innerHTML = `<span style="color: #60a5ee; font-weight: bold;">ConsLog AI — Document detected, no extraction needed</span> <span class="text-gray-500 font-normal tabular-nums inline-block w-8 text-left">${totalSecs}s</span>`
          const ic = this.uploadAiStatusTarget.querySelector(".ai-rainbow-icon")
          if (ic) { ic.classList.remove("ai-rainbow-icon"); ic.style.color = "#60a5ee" }
          continue
        }

        // ── Drawings: split by sheets (existing flow) ──
        const sheets = data.sheets || []
        console.log(`[design-step] LLM found ${sheets.length} sheets:`, sheets.map(s => `${s.sheet_number} — ${s.title} (p${s.page})`))

        // Mark as drawing so _extractMetadataForNewFiles renders the right badge
        uf._documentType = "drawing"

        if (sheets.length < 2 || this._splitCancelled) continue

        const totalSheets = sheets.length
        updateBannerText(`Drawing detected — splitting into ${totalSheets} sheets...`)

        const splitter = splitDrawingBySheets(uf.file, arrayBuffer, sheets, result.numPages, (progress) => {
          if (progress.phase === "splitting" && progress.current) {
            updateBannerText(`Splitting drawing... ${progress.current} of ${progress.total} sheets`)
          }
        })

        let firstSheet = true
        let splitCount = 0
        const splitSheetNumbers = []
        const splitEntries = []
        // The set and the sheets cut from it share a key, so the server files
        // the sheets under the set (Phelipe 2026-10-06).
        const packKey = `p${uf.id}`

        for await (const sheet of splitter) {
          if (this._splitCancelled) break
          if (sheet === null) break

          if (firstSheet) {
            firstSheet = false
            anySplit = true
            this._uploadFiles = this._uploadFiles.filter(f => f.id !== uf.id)
            const origRow = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uf.id}"]`)
            if (origRow) origRow.remove()
          }

          const id = Date.now() + Math.round(Math.random() * 1000)
          const entry = {
            id,
            file: sheet.file,
            sheetNumber: sheet.sheetNumber,
            title: sheet.title,
            discipline: sheet.discipline,
            _extracted: true,
            _splitChecked: true,
            _documentType: "drawing",
            _packKey: packKey
          }
          this._uploadFiles.push(entry)
          splitEntries.push(entry)
          this._renderUploadSheetRow(id, sheet.file, {
            sheetNumber: sheet.sheetNumber,
            title: sheet.title,
            discipline: sheet.discipline
          })
          splitSheetNumbers.push(sheet.sheetNumber)
          splitCount++

          if (this.hasUploadSheetTableTarget) {
            this.uploadSheetTableTarget.classList.remove("hidden")
          }
        }

        // Batch check NEW/REV
        if (splitSheetNumbers.length > 0) {
          this._batchCheckSheets(splitSheetNumbers)
        }

        if (splitCount > 0) {
          clearInterval(this._aiTimerInterval)
          const totalSecs = Math.round((performance.now() - this._aiTimerStart) / 1000)
          this.uploadAiStatusTextTarget.innerHTML = `<span style="color: #60a5ee; font-weight: bold;">ConsLog AI — Drawing detected, ${splitCount} sheets extracted</span> <span class="text-gray-500 font-normal tabular-nums inline-block w-8 text-left">${totalSecs}s</span>`
          const ic = this.uploadAiStatusTarget.querySelector(".ai-rainbow-icon")
          if (ic) { ic.classList.remove("ai-rainbow-icon"); ic.style.color = "#60a5ee" }

          this._splitStates.push({
            originalFile: uf.file,
            documentType: "drawing",
            splitEntries,
            splitCount
          })
          this._keepCombinedOriginal(uf.file, "drawing", packKey)
        }
      } catch (e) {
        console.error(`[design-step] PDF split failed for ${uf.file.name}:`, e)
      }
    }

    if (!anySplit) {
      clearInterval(this._aiTimerInterval)
      this.uploadAiStatusTarget.classList.add("hidden")
      this.uploadAiStatusTarget.classList.remove("flex")
    }

    // Append "Skip splitting" link to banner if any files were split
    if (this._splitStates.length > 0) {
      this._showSplitBannerWithSkipLink()
    }

    this._extractMetadataForNewFiles()
  }

  // ── Combined original kept with its sheets ─────────────────────
  // Like the Construction pages (Phelipe 2026-10-06): a split PDF keeps the
  // whole file too, marked combined, and the Upload button chooses what goes
  // up: both (default), the combined only, or the extracted sheets only.
  // Before, Design's either/or "Upload as Combined" sent the whole file as a
  // sheet numbered "Combined", which the server read as a Civil drawing.

  _showSplitBannerWithSkipLink() {
    const drawingStates = this._splitStates.filter(s => s.documentType === "drawing")
    const specStates = this._splitStates.filter(s => s.documentType === "specification")
    const drawingCount = drawingStates.reduce((sum, s) => sum + s.splitCount, 0)
    const specCount = specStates.reduce((sum, s) => sum + s.splitCount, 0)

    const parts = []
    if (drawingCount > 0) parts.push(`${drawingCount} sheet${drawingCount > 1 ? "s" : ""}`)
    if (specCount > 0) parts.push(`${specCount} section${specCount > 1 ? "s" : ""}`)
    const summary = parts.join(" + ")

    const currentTimer = this.uploadAiStatusTextTarget.querySelector("[data-ai-timer]")
    const timerHtml = currentTimer ? currentTimer.outerHTML : ""

    this.uploadAiStatusTextTarget.innerHTML =
      `<span style="color: #60a5ee; font-weight: bold;">ConsLog AI — ${summary} extracted · combined kept</span> ${timerHtml}`
  }

  _keepCombinedOriginal(file, documentType, packKey) {
    if (this._uploadFiles.some(uf => uf._isCombined && uf.file === file)) return
    const id = Date.now() + Math.round(Math.random() * 1000)
    this._uploadFiles.push({
      id,
      file,
      sheetNumber: "Combined",
      title: file.name.replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").trim(),
      discipline: "",
      _extracted: true,
      _splitChecked: true,
      _documentType: documentType,
      _isCombined: true,
      _packKey: packKey
    })
    this._renderCombinedFileRow(id, file, documentType)
    this._updateUploadBtnText()
  }

  _renderCombinedFileRow(id, file, documentType) {
    const titleFromName = file.name.replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").trim()
    this.uploadSheetRowsTarget.insertAdjacentHTML("beforeend", `
      <tr data-upload-row="${id}" class="border-b border-gray-100">
        <td class="py-1.5 pr-3">
          <div class="flex items-center gap-1.5 min-w-0">
            ${typeBadgeHtml(documentType)}
            <span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium bg-blue-100 text-blue-700">Combined</span>
            <span class="text-[13px] text-gray-700 truncate" title="${this._esc(file.name)}">${this._esc(file.name)}</span>
          </div>
          <div class="relative h-1 rounded-full mt-1 hidden" data-progress-track>
            <div data-progress-bar class="h-full bg-brand rounded-full transition-all duration-200" style="width: 0%"></div>
          </div>
        </td>
        <td class="py-1.5 px-3" colspan="2">
          <input type="text" data-title-input class="w-full text-[13px] rounded-lg border border-gray-300 focus:border-brand focus:ring-0 py-1 px-2"
                 placeholder="${documentType === "specification" ? "Specification title" : "Drawing title"}"
                 value="${this._esc(titleFromName)}"
                 data-upload-id="${id}"
                 data-action="input->design-step#uploadTitleChanged">
        </td>
        <td class="py-1.5 px-3"></td>
        <td class="py-1.5 pl-3">
          <button type="button" class="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                  data-upload-id="${id}" data-action="click->design-step#removeUploadFile">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          </button>
        </td>
      </tr>`)
  }

  async _extractMetadataForNewFiles() {
    // Find PDF files that haven't been extracted yet
    const pdfFiles = this._uploadFiles.filter(uf => {
      if (uf._extracted) return false
      const ext = uf.file.name.split(".").pop()?.toLowerCase()
      return ext === "pdf"
    })
    if (pdfFiles.length === 0) {
      this._updateUploadBtnText()
      this.uploadSubmitBtnTarget.disabled = this._uploadFiles.length === 0
      this._checkDuplicates()
      return
    }

    // Mark as in-progress so we don't re-extract
    pdfFiles.forEach(uf => { uf._extracted = true })

    // Show AI status banner with elapsed timer
    this.uploadAiStatusTarget.classList.remove("hidden")
    this.uploadAiStatusTarget.classList.add("flex")
    this._aiTimerStart = performance.now()
    this.uploadAiStatusTextTarget.innerHTML = `<span class="font-bold ai-rainbow-text">ConsLog AI — Analyzing Drawings</span> <span data-ai-timer class="text-gray-500 font-normal tabular-nums inline-block w-8 text-left">0s</span>`
    this._aiTimerInterval = setInterval(() => {
      const secs = Math.round((performance.now() - this._aiTimerStart) / 1000)
      const timerEl = this.uploadAiStatusTextTarget.querySelector("[data-ai-timer]")
      if (timerEl) timerEl.textContent = `${secs}s`
    }, 1000)

    const extractUrl = this.extractMetadataUrlValue
    let completed = 0
    const total = pdfFiles.length
    const MAX_PDF_BYTES = 250 * 1024 * 1024
    const MAX_TEXT_PAGES = 20
    const MAX_IMAGE_PAGES = 5

    const promises = pdfFiles.map(async (uf) => {
      try {
        if (uf.file.size > MAX_PDF_BYTES) return

        const t0 = performance.now()
        const arrayBuffer = await uf.file.arrayBuffer()
        const { pageTexts } = await extractPdfText(arrayBuffer.slice(0), MAX_TEXT_PAGES)

        const allText = pageTexts.map((t, i) => `--- PAGE ${i + 1} ---\n${t}`).join("\n\n")
        const hasText = pageTexts.some(t => t.trim().length > 50)

        let mode, content
        if (hasText) {
          mode = "text"
          content = allText
          console.log(`[design-step] ${uf.file.name}: text mode (${allText.length} chars), text extraction took ${Math.round(performance.now() - t0)}ms`)
        } else {
          const t1 = performance.now()
          const { images } = await renderPdfPagesToJpegs(arrayBuffer, MAX_IMAGE_PAGES)
          console.log(`[design-step] ${uf.file.name}: image mode (${images.length} images), render took ${Math.round(performance.now() - t1)}ms`)
          mode = "images"
          content = images
        }

        const t2 = performance.now()
        const result = await callExtractMetadata(extractUrl, mode, content, uf.file.name)
        console.log(`[design-step] ${uf.file.name}: Gemini ${mode} call took ${Math.round(performance.now() - t2)}ms, total ${Math.round(performance.now() - t0)}ms`)

        // Store extracted values
        if (result.sheet_number) uf.sheetNumber = result.sheet_number
        if (result.title) uf.title = result.title
        if (result.discipline) uf.discipline = result.discipline
      } catch (e) {
        console.error(`[design-step] AI extraction failed for ${uf.file.name}:`, e)
      } finally {
        // Render row with extracted data (or empty fields if extraction failed)
        const prefilled = (uf.sheetNumber || uf.title || uf.discipline)
          ? { sheetNumber: uf.sheetNumber, title: uf.title, discipline: uf.discipline }
          : null

        if (uf._documentType === "specification") {
          this._renderSpecUploadRow(uf.id, uf.file, prefilled ? { sectionNumber: uf.sheetNumber, title: uf.title, division: uf.discipline } : null)
        } else if (uf._documentType === "document") {
          this._renderNonDrawingRow(uf.id, uf.file, "document")
        } else {
          this._renderUploadSheetRow(uf.id, uf.file, prefilled)
        }
        if (this.hasUploadSheetTableTarget) this.uploadSheetTableTarget.classList.remove("hidden")
        if (uf.sheetNumber) this._checkSheetAfterExtract(uf.id, uf.sheetNumber)

        completed++
        if (completed === total) {
          clearInterval(this._aiTimerInterval)
          const totalSecs = Math.round((performance.now() - this._aiTimerStart) / 1000)
          this.uploadAiStatusTextTarget.innerHTML = `<span style="color: #60a5ee; font-weight: bold;">ConsLog AI — Values Extracted</span> <span class="text-gray-500 font-normal tabular-nums inline-block w-8 text-left">${totalSecs}s</span>`
          const icon = this.uploadAiStatusTarget.querySelector(".ai-rainbow-icon")
          if (icon) { icon.classList.remove("ai-rainbow-icon"); icon.style.color = "#60a5ee" }

          // Restore skip link if any files were split in this batch
          if (this._splitStates && this._splitStates.length > 0) {
            this._showSplitBannerWithSkipLink()
          }
        }
      }
    })

    await Promise.allSettled(promises)
    // Re-enable upload button after AI processing
    this._updateUploadBtnText()
    this.uploadSubmitBtnTarget.disabled = this._uploadFiles.length === 0
    this._checkDuplicates()
  }

  async _checkSheetAfterExtract(uploadId, sheetNumber) {
    try {
      const token = this._csrfToken()
      const res = await fetch(this.checkSheetsUrlValue + `?s=${encodeURIComponent(this.stepNameValue)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ sheet_numbers: [sheetNumber] })
      })
      const results = await res.json()
      const result = results[0]
      if (!result) return

      const uf = this._uploadFiles.find(f => f.id === uploadId)
      if (!uf) return
      const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uploadId}"]`)
      if (!row) return
      const sheetInput = row.querySelector(`input[data-upload-id="${uploadId}"]`)
      if (!sheetInput) return

      const existingLabel = row.querySelector("[data-sheet-label]")
      if (existingLabel) existingLabel.remove()
      const fileNameSpan = row.querySelector("td:first-child span")

      if (result.exists) {
        sheetInput.title = `Revises ${result.sheet_number} (currently Rev ${result.current_revision || "?"})`
        sheetInput.classList.add("bg-amber-50", "border-amber-300")
        sheetInput.classList.remove("bg-blue-50", "border-blue-200")
        fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[10px] font-bold text-amber-500 ml-1.5 whitespace-nowrap">REV</span>`)
      } else {
        sheetInput.title = "New sheet"
        fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[10px] font-bold text-emerald-500 ml-1.5 whitespace-nowrap">NEW</span>`)
      }
    } catch (e) {
      console.error("[design-step] check_sheets after extract failed:", e)
    }
  }

  async _batchCheckSheets(sheetNumbers) {
    if (!this.hasCheckSheetsUrlValue || !sheetNumbers.length) return
    try {
      const token = this._csrfToken()
      const res = await fetch(this.checkSheetsUrlValue + `?s=${encodeURIComponent(this.stepNameValue)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ sheet_numbers: sheetNumbers })
      })
      const results = await res.json()

      results.forEach(result => {
        const uf = this._uploadFiles.find(f => f.sheetNumber === result.sheet_number)
        if (!uf) return
        const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uf.id}"]`)
        if (!row) return
        const sheetInput = row.querySelector(`input[data-upload-id="${uf.id}"]`)
        if (!sheetInput) return

        const existingLabel = row.querySelector("[data-sheet-label]")
        if (existingLabel) existingLabel.remove()

        const fileNameSpan = row.querySelector("td:first-child span")

        if (result.exists) {
          sheetInput.title = `Revises ${result.sheet_number} (currently Rev ${result.current_revision || "?"})`
          sheetInput.classList.add("bg-amber-50", "border-amber-300")
          sheetInput.classList.remove("bg-blue-50", "border-blue-200")
          fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[10px] font-bold text-amber-500 ml-1.5 whitespace-nowrap">REV</span>`)
        } else {
          sheetInput.title = "New sheet"
          fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[10px] font-bold text-emerald-500 ml-1.5 whitespace-nowrap">NEW</span>`)
        }
      })
    } catch (e) {
      console.error("[design-step] Batch check sheets failed:", e)
    }
  }

  async _batchCheckSections(sectionNumbers) {
    if (!this.hasCheckSectionsUrlValue || !sectionNumbers.length) return
    try {
      const token = this._csrfToken()
      const res = await fetch(this.checkSectionsUrlValue + `?s=${encodeURIComponent(this.stepNameValue)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ section_numbers: sectionNumbers })
      })
      const results = await res.json()

      results.forEach(result => {
        const uf = this._uploadFiles.find(f => f.sheetNumber === result.section_number && f._documentType === "specification")
        if (!uf) return
        const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uf.id}"]`)
        if (!row) return
        const sectionInput = row.querySelector(`input[data-upload-id="${uf.id}"]`)
        if (!sectionInput) return

        const existingLabel = row.querySelector("[data-sheet-label]")
        if (existingLabel) existingLabel.remove()

        const fileNameSpan = row.querySelector("td:first-child span.text-\\[13px\\]")

        if (result.exists) {
          sectionInput.title = `Revises ${result.section_number} (currently Rev ${result.current_revision || "?"})`
          sectionInput.classList.add("bg-amber-50", "border-amber-300")
          sectionInput.classList.remove("bg-purple-50", "border-purple-200")
          fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[10px] font-bold text-amber-500 ml-1.5 whitespace-nowrap">REV</span>`)
        } else {
          sectionInput.title = "New section"
          fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[10px] font-bold text-emerald-500 ml-1.5 whitespace-nowrap">NEW</span>`)
        }
      })
    } catch (e) {
      console.error("[design-step] Batch check sections failed:", e)
    }
  }

  // Primary action + the two menu variants, as on the Construction pages.
  // mode: "both" (default) | "combined" (just the combined originals) |
  // "extracted" (just the sheets / sections).
  submitUpload() { this._doUpload("both") }
  submitUploadCombinedOnly() { this._doUpload("combined") }
  submitUploadExtractedOnly() { this._doUpload("extracted") }

  toggleUploadMenu(event) {
    if (event) event.stopPropagation()
    if (this.hasUploadMenuTarget) this.uploadMenuTarget.classList.toggle("hidden")
  }

  _closeUploadMenu() {
    if (this.hasUploadMenuTarget) this.uploadMenuTarget.classList.add("hidden")
  }

  async _doUpload(mode = "both") {
    this._closeUploadMenu()
    const files = this._filesForMode(mode)
    if (files.length === 0) {
      if (this._uploadFiles.some(uf => uf._duplicate)) this._showToast("These files are already on this step", "error")
      return
    }
    this.uploadSubmitBtnTarget.disabled = true
    this.uploadSubmitBtnTarget.textContent = "Uploading..."
    if (this.hasUploadMenuBtnTarget) this.uploadMenuBtnTarget.disabled = true

    const token = this._csrfToken()

    try {
      // 1. Get presigned URLs
      const fileMeta = files.map(uf => ({
        filename: uf.file.name,
        content_type: uf.file.type || "application/octet-stream",
        byte_size: uf.file.size
      }))

      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ files: fileMeta })
      })

      if (!presignRes.ok) {
        const err = await presignRes.json()
        throw new Error(err.error || "Presign failed")
      }

      const { uploads } = await presignRes.json()

      // 2. Upload each file to S3
      const uploadPromises = uploads.map((upload, i) => {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          const uf = files[i]
          const progressTrack = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uf.id}"] [data-progress-track]`)
          const progressBar = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uf.id}"] [data-progress-bar]`)
          if (progressTrack) { progressTrack.classList.remove("hidden"); progressTrack.classList.add("bg-gray-100") }

          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && progressBar) {
              const pct = Math.round((e.loaded / e.total) * 100)
              progressBar.style.width = `${pct}%`
            }
          }

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve({
                cache_name: upload.cache_name,
                filename: upload.filename,
                sheet_number: uf.sheetNumber,
                title: uf.title,
                discipline: uf.discipline,
                document_type: uf._documentType || "drawing",
                combined: uf._isCombined === true,
                pack: uf._packKey || null
              })
            } else {
              reject(new Error(`Upload failed: ${xhr.status}`))
            }
          }
          xhr.onerror = () => reject(new Error("Upload error"))

          xhr.open("PUT", upload.upload_url)
          xhr.send(uf.file)
        })
      })

      const results = await Promise.all(uploadPromises)

      // 3. Register
      const folderId = this.hasUploadFolderSelectTarget ? this.uploadFolderSelectTarget.value : ""
      const notify = this.hasUploadNotifyCheckTarget ? this.uploadNotifyCheckTarget.checked : false

      const registerRes = await fetch(this.registerUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({
          files: results,
          s: this.stepNameValue,
          folder_id: folderId || null,
          send_notif: notify ? "true" : "false"
        })
      })
      if (!registerRes.ok) throw new Error("Registration failed")
      const registered = await registerRes.json().catch(() => ({}))
      track("Design Files Uploaded", {
        step_name: this.stepNameValue,
        upload_mode: mode,
        file_count: files.length,
        combined_count: files.filter(uf => uf._isCombined).length,
        skipped_count: this._uploadFiles.length - files.length
      })

      this.closeUploadModal()
      const hasSpec = files.some(uf => uf._documentType === "specification")
      const hasDrawing = files.some(uf => !uf._documentType || uf._documentType === "drawing")
      const hasDoc = files.some(uf => uf._documentType === "document")
      let unit = "file"
      if (hasDrawing && !hasSpec && !hasDoc) unit = "sheet"
      else if (hasSpec && !hasDrawing && !hasDoc) unit = "section"
      this._showToast(`${results.length} ${unit}${results.length > 1 ? "s" : ""} uploaded`)

      // Reload
      if (this._currentFolderId && folderId && parseInt(folderId) === this._currentFolderId) {
        this._navigateToFolder(this._currentFolderId, this._currentFolderName)
      } else {
        this._loadInitial()
      }

      // Offer to notify specific people about what was just uploaded — the
      // "notify everyone" checkbox above already mailed the whole team, so the
      // picker only opens when it wasn't used.
      if (!notify && registered.ids?.length) this._openDistributeModal(registered.ids)
    } catch (error) {
      console.error("[design-step] Upload failed:", error)
      this._showToast(error.message || "Upload failed", "error")
    } finally {
      this.uploadSubmitBtnTarget.disabled = false
      if (this.hasUploadMenuBtnTarget) this.uploadMenuBtnTarget.disabled = false
      this._updateUploadBtnText()
    }
  }

  // What each upload option sends. "Combined only" is just the combined
  // originals now — it used to take every loose file in the pop-up along too
  // (James Collins, 2026-11-02). Files already on the step are left out while
  // "Skip them" is picked.
  _filesForMode(mode) {
    let files = this._uploadFiles
    if (this._dupMode !== "upload") files = files.filter(uf => !uf._duplicate)
    if (mode === "combined") return files.filter(uf => uf._isCombined)
    if (mode === "extracted") return files.filter(uf => !uf._isCombined)
    return files
  }

  _setMenuCounts() {
    if (!this.hasUploadMenuTarget) return
    this.uploadMenuTarget.querySelectorAll("[data-menu-count]").forEach(el => {
      const n = this._filesForMode(el.dataset.menuCount).length
      el.textContent = `· ${n} file${n === 1 ? "" : "s"}`
    })
  }

  // Asks which of the pop-up's files are already on this step by name; those
  // get an "Already here" tag and are skipped unless the person says otherwise.
  async _checkDuplicates() {
    if (!this.hasCheckDuplicatesUrlValue || this._uploadFiles.length === 0) return
    const names = [...new Set(this._uploadFiles.map(uf => uf.file.name))]
    try {
      const res = await fetch(this.checkDuplicatesUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
        credentials: "same-origin",
        body: JSON.stringify({ filenames: names })
      })
      if (!res.ok) return
      const existing = new Set((await res.json()).existing || [])
      this._uploadFiles.forEach(uf => { uf._duplicate = existing.has(uf.file.name) })
    } catch (e) {
      console.error("[design-step] duplicate check failed:", e)
      return
    }
    this._paintDuplicates()
    this._updateUploadBtnText()
  }

  _paintDuplicates() {
    const dups = this._uploadFiles.filter(uf => uf._duplicate)
    const skipping = this._dupMode !== "upload"
    this._uploadFiles.forEach(uf => {
      const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uf.id}"]`)
      if (!row) return
      row.classList.toggle("opacity-50", !!uf._duplicate && skipping)
      const pill = row.querySelector("[data-dup-pill]")
      if (uf._duplicate && !pill) {
        row.querySelector("td")?.insertAdjacentHTML("beforeend",
          `<span data-dup-pill class="inline-block mt-0.5 px-2 rounded-full bg-amber-100 text-amber-800 text-[13px]">Already here</span>`)
      } else if (!uf._duplicate && pill) {
        pill.remove()
      }
    })
    if (!this.hasUploadDupBannerTarget) return
    this.uploadDupBannerTarget.classList.toggle("hidden", dups.length === 0)
    if (this.hasUploadDupTextTarget) {
      this.uploadDupTextTarget.textContent = `${dups.length} of these ${dups.length === 1 ? "file is" : "files are"} already on this step`
    }
  }

  setDupMode(event) {
    this._dupMode = event.currentTarget.value === "upload" ? "upload" : "skip"
    this._paintDuplicates()
    this._updateUploadBtnText()
  }

  _resetDupBanner() {
    this._dupMode = "skip"
    if (!this.hasUploadDupBannerTarget) return
    this.uploadDupBannerTarget.classList.add("hidden")
    const skip = this.uploadDupBannerTarget.querySelector('input[value="skip"]')
    if (skip) skip.checked = true
  }

  _populateFolderSelect() {
    if (!this.hasUploadFolderSelectTarget) return
    let html = '<option value="">No folder</option>'
    const addOptions = (folders, indent = 0) => {
      for (const f of folders) {
        const prefix = "\u00A0\u00A0".repeat(indent)
        html += `<option value="${f.id}">${prefix}${this._esc(f.name)}</option>`
        if (f.children && f.children.length > 0) {
          addOptions(f.children, indent + 1)
        }
      }
    }
    addOptions(this._folders)
    this.uploadFolderSelectTarget.innerHTML = html

    // Pre-select current folder
    if (this._currentFolderId) {
      this.uploadFolderSelectTarget.value = this._currentFolderId
    }
  }

  _populateEditFolderSelect() {
    if (!this.hasEditPdfFolderTarget) return
    let html = '<option value="">No Folder</option>'
    const addOptions = (folders, indent = 0) => {
      for (const f of folders) {
        const prefix = "\u00A0\u00A0".repeat(indent)
        html += `<option value="${f.id}">${prefix}${this._esc(f.name)}</option>`
        if (f.children && f.children.length > 0) {
          addOptions(f.children, indent + 1)
        }
      }
    }
    addOptions(this._folders)
    this.editPdfFolderTarget.innerHTML = html
  }

  // ── Drag and Drop ─────────────────────────────────────────────

  _isUploadModalOpen() {
    return this.hasUploadModalTarget && !this.uploadModalTarget.classList.contains("hidden")
  }

  _setupPageDrag() {
    this._onDragOver = (e) => { e.preventDefault() }
    this._onDrop = (e) => {
      e.preventDefault()
      if (this._isUploadModalOpen()) return
      if (e.dataTransfer?.files?.length > 0) {
        this.openUploadModal()
        setTimeout(() => this.addFiles(e.dataTransfer.files), 100)
      }
    }
    document.addEventListener("dragover", this._onDragOver)
    document.addEventListener("drop", this._onDrop)
  }

  _teardownPageDrag() {
    if (this._onDragOver) document.removeEventListener("dragover", this._onDragOver)
    if (this._onDrop) document.removeEventListener("drop", this._onDrop)
  }

  // Drop zone styling for the modal
  dragOverZone(e) { e.preventDefault() }
  dragEnterZone(e) {
    e.preventDefault()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.add("border-brand/40", "bg-brand/5")
      this.dropZoneTarget.classList.remove("border-gray-300")
    }
  }
  dragLeaveZone() {
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("border-brand/40", "bg-brand/5")
      this.dropZoneTarget.classList.add("border-gray-300")
    }
  }
  dropOnZone(e) {
    e.preventDefault()
    this.dragLeaveZone()
    if (e.dataTransfer?.files?.length > 0) {
      this.addFiles(e.dataTransfer.files)
    }
  }

  // ── PDF Viewer ────────────────────────────────────────────────

  openPdf(event) {
    if (event.target.closest("[data-file-checkbox]") || event.target.closest("[data-row-menu]")) return
    const id = event.currentTarget.dataset.pdfId
    if (!id) return

    // Search-inside-documents: when this row matched the document's text, a
    // row click expands/collapses its snippet panel instead of opening the
    // file (the panel's items open the file at the matched page).
    if (event.currentTarget.tagName === "TR" && this._contentMatches?.[String(id)]) {
      this._toggleMatchRow(event.currentTarget, String(id))
      return
    }

    // PDFs go straight to the Apryse viewer; anything else opens the detail
    // panel, which previews images and offers a download for the rest.
    const pdf = this._pdfsById?.[String(id)]
    if (pdf && pdf.extension === "dwg") return this._openCadModal(pdf)
    if (pdf && pdf.is_pdf === false) {
      this._openDetail(id)
      return
    }
    this._openPdfById(id)
  }

  _openPdfById(pdfId, extra = {}) {
    if (!pdfId) return

    const pdf = this._pdfsById?.[String(pdfId)]
    if (!pdf || !pdf.is_pdf) return

    // Active Bluebeam session → open in Bluebeam directly instead of Apryse
    if (pdf.bluebeam_status === "active") {
      this._triggerBluebeamOpen(pdfId)
      return
    }

    const viewerCtrl = this._viewerController()
    if (!viewerCtrl) return

    const dataUrl = this._viewerDataUrl(pdfId)
    const saveUrl = this.saveAnnotationsBaseUrlValue.replace(/\/0(\/|$)/, `/${pdfId}$1`)
    const label = [pdf.sheet_number, pdf.title].filter(Boolean).join(" — ")
    const displayName = label ? `${label}  ·  ${pdf.file_name}` : (pdf.title || pdf.file_name || "document.pdf")

    // "Split" picker: this file's other versions + the step's other PDFs.
    const splitListUrl = this.hasSplitDocumentsBaseUrlValue
      ? this.splitDocumentsBaseUrlValue.replace(/\/0(\/|$)/, `/${pdfId}$1`)
      : null

    // References strip: documents THIS sheet cites, detected from its text.
    const referencesUrl = (this.hasReferencesUrlValue && this.referencesUrlValue)
      ? `${this.referencesUrlValue}?doc_id=${pdfId}`
      : null

    // Compare is offered when an older revision of this sheet exists.
    const compareEnabled = pdf.is_pdf && !pdf.combined && this._hasPreviousVersion(pdf)

    // Prev/next sheets' data URLs — the viewer prefetches their PDFs after
    // this one loads, so arrow navigation swaps near-instantly.
    const nav = this._viewerNavIds()
    const idx = nav.indexOf(String(pdfId))
    const neighbors = [nav[idx + 1], nav[idx - 1]].filter(Boolean).map(nid => this._viewerDataUrl(nid))

    // Start the PDF download NOW, in parallel with the metadata request — the
    // list already carries this sheet's direct URL. No extra requests.
    if (pdf.pdf_url && typeof viewerCtrl.primeBlob === "function") {
      viewerCtrl.primeBlob(dataUrl, pdf.pdf_url)
    }

    this._viewerPdfId = pdfId
    const isHistorical = pdf.status !== "current"
    viewerCtrl.open({
      dataUrl, saveUrl, filename: displayName, punchlistId: pdfId,
      version: pdf.revision_number || "1", versionCurrent: !isHistorical,
      readOnly: isHistorical, splitListUrl, referencesUrl, compareEnabled,
      compareFileId: pdfId, neighbors, ...extra
    })
    this._setViewerNavState(pdfId)
  }

  // ── Viewer prev/next: page through the step's PDF list without closing
  // the viewer (same pattern as the Specs & Drawings pages). Navigation
  // follows the CURRENT files in their on-screen order; historical versions
  // opened from version history just don't page. ──
  // Viewer arrows follow the on-screen order (the grouped/sorted render), not
  // the fetch order — otherwise they walk the list backwards and skip rows.
  _viewerNavIds() {
    return this._navOrder().filter(id => {
      const p = this._pdfsById?.[String(id)]
      return p && p.is_pdf && p.bluebeam_status !== "active"
    })
  }

  _setViewerNavState(pdfId) {
    const nav = this._viewerNavIds()
    const idx = nav.indexOf(String(pdfId))
    if (this.hasViewerPrevBtnTarget) this.viewerPrevBtnTarget.disabled = idx <= 0
    if (this.hasViewerNextBtnTarget) this.viewerNextBtnTarget.disabled = idx < 0 || idx >= nav.length - 1
  }

  viewerPrev() {
    const nav = this._viewerNavIds()
    const idx = nav.indexOf(String(this._viewerPdfId))
    if (idx > 0) this._openPdfById(nav[idx - 1])
  }

  viewerNext() {
    const nav = this._viewerNavIds()
    const idx = nav.indexOf(String(this._viewerPdfId))
    if (idx >= 0 && idx < nav.length - 1) this._openPdfById(nav[idx + 1])
  }

  // ── Notify Team & Merged PDF ────────────────────────────────

  async notifyOfComments() {
    if (!this._viewerPdfId || !this.notifyMembersUrlValue) return

    // Show modal and load members
    if (this.hasNotifyModalTarget) this.notifyModalTarget.classList.remove("hidden")

    try {
      const resp = await fetch(this.notifyMembersUrlValue, {
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrfToken() },
        credentials: "same-origin"
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const data = await resp.json()
      this._renderNotifyMembers(data.groups)
    } catch (e) {
      console.error("[design-step] Failed to load notify members:", e)
      if (this.hasNotifyBodyTarget) {
        this.notifyBodyTarget.innerHTML = `<p class="text-sm text-gray-500 py-4 text-center">Failed to load members.</p>`
      }
    }
  }

  closeNotifyModal() {
    if (this.hasNotifyModalTarget) this.notifyModalTarget.classList.add("hidden")
  }

  notifyToggleAll(event) {
    const checked = event.currentTarget.checked
    if (this.hasNotifyBodyTarget) {
      this.notifyBodyTarget.querySelectorAll('input[type="checkbox"][data-email]').forEach(cb => {
        cb.checked = checked
      })
    }
  }

  async sendNotifyComments() {
    if (!this._viewerPdfId || !this.hasNotifyBodyTarget) return

    const checkboxes = this.notifyBodyTarget.querySelectorAll('input[type="checkbox"][data-email]:checked')
    const emails = Array.from(checkboxes).map(cb => cb.dataset.email)
    if (emails.length === 0) return

    const btn = this.hasNotifySendBtnTarget ? this.notifySendBtnTarget : null
    if (btn) { btn.disabled = true; btn.textContent = "Sending..." }

    try {
      const resp = await fetch(this.notifyCommentsUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
        credentials: "same-origin",
        body: JSON.stringify({ dev_pdf_id: this._viewerPdfId, project_id: this.projectIdValue, emails })
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)

      this.closeNotifyModal()
      this._showToast("Notifications sent")
    } catch (e) {
      console.error("[design-step] Notify of comments error:", e)
      this._showToast("Failed to send notifications", "error")
    } finally {
      if (btn) {
        btn.disabled = false
        btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3.5 h-3.5 inline -mt-0.5 mr-1"><path stroke-linecap="round" stroke-linejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"/></svg>Send Notifications`
      }
    }
  }

  _renderNotifyMembers(groups) {
    if (!this.hasNotifyBodyTarget) return

    if (!groups || groups.length === 0) {
      this.notifyBodyTarget.innerHTML = `<p class="text-sm text-gray-500 py-4 text-center">No members found.</p>`
      return
    }

    let html = ""
    groups.forEach(group => {
      html += `<div class="mb-3">
        <div class="flex items-center gap-2 mb-1.5">
          <span class="text-[13px] font-bold uppercase tracking-wide text-gray-400">${this._esc(group.role)}</span>
          <span class="text-[13px] text-gray-400">&middot;</span>
          <span class="text-[13px] font-medium text-gray-500">${this._esc(group.company_name)}</span>
        </div>
        <div class="space-y-1">`

      group.members.forEach(member => {
        html += `<label class="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
            <input type="checkbox" data-email="${this._esc(member.email)}" class="form-checkbox rounded shrink-0" style="accent-color: var(--color-brand)">
            <span class="text-[13px] text-gray-700 truncate">${this._esc(member.name)}</span>
            <span class="text-[13px] text-gray-400 truncate">${this._esc(member.email)}</span>
          </label>`
      })

      html += `</div></div>`
    })

    this.notifyBodyTarget.innerHTML = html

    if (this.hasNotifySelectAllTarget) {
      this.notifySelectAllTarget.checked = false
    }
  }

  async downloadMergedPdf(event) {
    const pdfId = this._viewerPdfId
    if (!pdfId || !this.mergedPdfUrlValue) return

    const btn = event.currentTarget
    const originalHtml = btn.innerHTML
    const spinner = `<svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span class="hidden sm:inline">Preparing...</span>`
    btn.innerHTML = spinner
    btn.disabled = true

    try {
      const resp = await fetch(this.mergedPdfUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
        credentials: "same-origin",
        body: JSON.stringify({ dev_pdf_id: pdfId })
      })
      if (resp.ok) {
        const contentType = resp.headers.get("Content-Type") || ""
        if (contentType.includes("application/json")) {
          // No markups — server returned the direct S3 URL
          const data = await resp.json()
          if (data.url) window.open(data.url, "_blank")
        } else {
          // Merged PDF blob
          const blob = await resp.blob()
          const disposition = resp.headers.get("Content-Disposition") || ""
          const match = disposition.match(/filename="?([^"]+)"?/)
          const filename = match ? match[1] : "merged.pdf"

          const url = URL.createObjectURL(blob)
          const a = document.createElement("a")
          a.href = url
          a.download = filename
          document.body.appendChild(a)
          a.click()
          a.remove()
          URL.revokeObjectURL(url)
        }
      } else {
        const data = await resp.json().catch(() => ({}))
        this._showToast(data.error || "Failed to generate merged PDF", "error")
      }
    } catch (e) {
      console.error("[design-step] Download merged PDF error:", e)
      this._showToast("Failed to generate merged PDF", "error")
    } finally {
      btn.innerHTML = originalHtml
      btn.disabled = false
    }
  }

  // ── Edit/Delete PDF ───────────────────────────────────────────

  openEditPdfModal(event) {
    event.stopPropagation()
    const el = event.currentTarget
    if (this.hasEditPdfTitleTarget) this.editPdfTitleTarget.value = el.dataset.title || ""
    if (this.hasEditPdfSheetNumberTarget) this.editPdfSheetNumberTarget.value = el.dataset.sheetNumber || ""
    if (this.hasEditPdfDisciplineTarget) this.editPdfDisciplineTarget.value = el.dataset.discipline || ""
    if (this.hasEditPdfIdTarget) this.editPdfIdTarget.value = el.dataset.pdfId || ""
    if (this.hasEditPdfTypeTarget) {
      this.editPdfTypeTarget.value = el.dataset.documentType || "drawing"
      this._updateEditPdfTypeUI(el.dataset.documentType || "drawing")
    }
    if (this.hasEditPdfFolderTarget) {
      this._populateEditFolderSelect()
      this.editPdfFolderTarget.value = el.dataset.folderId || ""
    }
    if (this.hasEditPdfModalTarget) this.editPdfModalTarget.classList.remove("hidden")
  }

  closeEditPdfModal() {
    if (this.hasEditPdfModalTarget) this.editPdfModalTarget.classList.add("hidden")
  }

  editPdfTypeChanged() {
    if (!this.hasEditPdfTypeTarget) return
    this._updateEditPdfTypeUI(this.editPdfTypeTarget.value)
  }

  _updateEditPdfTypeUI(docType) {
    const isDoc = docType === "document"
    const isSpec = docType === "specification"

    // Hide sheet/discipline row for documents
    if (this.hasEditPdfSheetDisciplineRowTarget) {
      this.editPdfSheetDisciplineRowTarget.classList.toggle("hidden", isDoc)
    }

    // Update labels for spec vs drawing
    if (this.hasEditPdfSheetLabelTarget) {
      this.editPdfSheetLabelTarget.textContent = isSpec ? "Section #" : "Sheet #"
    }
    if (this.hasEditPdfSheetNumberTarget) {
      this.editPdfSheetNumberTarget.placeholder = isSpec ? "e.g. 03 30 00" : "e.g. A-101"
    }
    if (this.hasEditPdfDisciplineLabelTarget) {
      this.editPdfDisciplineLabelTarget.textContent = isSpec ? "Division" : "Discipline"
    }

    // Swap discipline options based on type
    if (this.hasEditPdfDisciplineTarget) {
      const select = this.editPdfDisciplineTarget
      const currentVal = select.value
      if (isSpec) {
        const divisionNames = [...new Set(Object.values(CSI_DIVISIONS))]
        select.innerHTML = '<option value="">--</option>' + divisionNames.map(d => `<option value="${d}">${d}</option>`).join("")
      } else {
        const discNames = [...new Set(Object.values(DISCIPLINE_PREFIXES))]
        select.innerHTML = '<option value="">--</option>' + discNames.map(d => `<option value="${d}">${d}</option>`).join("")
      }
      // Restore value if it still exists in new options
      if ([...select.options].some(o => o.value === currentVal)) {
        select.value = currentVal
      }
    }
  }

  async confirmEditPdf() {
    const pdfId = this.hasEditPdfIdTarget ? this.editPdfIdTarget.value : ""
    if (!pdfId) return

    const url = this.updatePdfUrlValue.replace("__PDF_ID__", pdfId)
    try {
      const resp = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
        credentials: "same-origin",
        body: JSON.stringify({
          name: this.hasEditPdfTitleTarget ? this.editPdfTitleTarget.value.trim() : "",
          sheet_number: this.hasEditPdfSheetNumberTarget ? this.editPdfSheetNumberTarget.value.trim() : "",
          discipline: this.hasEditPdfDisciplineTarget ? this.editPdfDisciplineTarget.value : "",
          document_type: this.hasEditPdfTypeTarget ? this.editPdfTypeTarget.value : "",
          folder_id: this.hasEditPdfFolderTarget ? this.editPdfFolderTarget.value : ""
        })
      })
      if (!resp.ok) throw new Error("Request failed")
      this.closeEditPdfModal()
      this._refreshCurrentView()
      this._showToast("PDF updated")
    } catch (e) {
      console.error("[design-step] Edit PDF error:", e)
      this._showAlert("Error", "Failed to update PDF. Please try again.")
    }
  }

  deletePdf(event) {
    event.stopPropagation()
    const pdfId = event.currentTarget.dataset.pdfId
    const title = event.currentTarget.dataset.title || "this PDF"

    // Close the row menu and show confirmation modal
    document.querySelectorAll("[data-row-menu]").forEach(m => m.classList.add("hidden"))
    this._deleteTarget = null
    this._deletePdfId = pdfId
    if (this.hasDeletePdfNameTarget) this.deletePdfNameTarget.textContent = title
    this._setDeletePackNote([pdfId])
    if (this.hasDeletePdfModalTarget) this.deletePdfModalTarget.classList.remove("hidden")
  }

  // Deleting a combined pack takes the sheets cut from it along (the server
  // trashes them together) - say so first, like the Construction pages do.
  _setDeletePackNote(ids) {
    if (!this.hasDeletePdfPackNoteTarget) return
    const chosen = new Set(ids.map(String))
    const packs = this._pdfs.filter(p => p.combined && chosen.has(String(p.id))).length
    const sheets = this._pdfs.filter(p => p.combined_parent_id && chosen.has(String(p.combined_parent_id)) && !chosen.has(String(p.id))).length
    const show = packs > 0 && sheets > 0
    this.deletePdfPackNoteTarget.classList.toggle("hidden", !show)
    this.deletePdfPackNoteTarget.textContent = !show ? ""
      : ids.length === 1
        ? `This is a combined file — its ${sheets} extracted ${sheets === 1 ? "sheet is" : "sheets are"} deleted with it.`
        : `The combined files you picked take their ${sheets} extracted ${sheets === 1 ? "sheet" : "sheets"} with them.`
  }

  closeDeletePdfModal() {
    if (this.hasDeletePdfModalTarget) this.deletePdfModalTarget.classList.add("hidden")
    this._deletePdfId = null
    this._deleteTarget = null
  }

  async confirmDeletePdf() {
    // The same modal confirms a bulk delete from the select bar.
    if (this._deleteTarget?.type === "bulk") {
      this._deleteTarget = null
      this.closeDeletePdfModal()
      await this._performBulkDelete()
      return
    }

    const pdfId = this._deletePdfId
    if (!pdfId) return

    this.closeDeletePdfModal()

    try {
      const resp = await fetch(this.destroyPdfUrlValue, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
        credentials: "same-origin",
        body: JSON.stringify({ pdf_id: pdfId, project_id: this.projectIdValue })
      })
      if (!resp.ok) throw new Error("Request failed")
      this._refreshCurrentView()
      this._bumpTrashBadge(1)
      this._showToast("PDF deleted")
    } catch (e) {
      console.error("[design-step] Delete PDF error:", e)
      this._showAlert("Error", "Failed to delete PDF. Please try again.")
    }
  }

  // ── Trash can (Luma 2026-09-02, Phelipe 2026-09-14) ─────────────────
  // Deleted files and folders of this step; Restore puts one back where it was.

  async openTrash() {
    if (!this.hasTrashModalTarget) return
    this.trashModalTarget.classList.remove("hidden")
    this.trashListTarget.innerHTML = ""
    this.trashEmptyTarget.classList.add("hidden")
    this.trashLoadingTarget.classList.remove("hidden")
    if (this.hasHistoryUploadsTarget) this.historyUploadsTarget.innerHTML = ""
    const getJson = url => fetch(url, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      .then(r => (r.ok ? r.json() : {}))
      .catch(e => { console.error("[design-step] history load error:", e); return {} })
    const [trash, history] = await Promise.all([
      getJson(this.trashUrlValue),
      this.hasUploadHistoryUrlValue ? getJson(this.uploadHistoryUrlValue) : Promise.resolve({})
    ])
    this._renderUploads(history.uploads || [])
    this._renderTrash(trash.items || [])
  }

  closeTrash() {
    if (this.hasTrashModalTarget) this.trashModalTarget.classList.add("hidden")
  }

  _renderTrash(items) {
    this.trashLoadingTarget.classList.add("hidden")
    this.trashEmptyTarget.classList.toggle("hidden", items.length > 0)
    this._setTrashBadge(items.length)
    const folderIcon = '<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z"/></svg>'
    const fileIcon = '<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>'
    const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`
    this.trashListTarget.innerHTML = items.map(it => {
      const isFolder = it.kind === "folder"
      const name = isFolder ? it.title : ([it.sheet_number, it.title].filter(Boolean).join(" — ") || it.file_name || `#${it.id}`)
      const bits = []
      if (it.deleted_ago) bits.push(`${this._esc(it.deleted_ago)}${it.deleted_by ? ` by ${this._esc(it.deleted_by)}` : ""}`)
      if (isFolder && it.files_inside) bits.push(`${plural(it.files_inside, "file")} inside`)
      if (!isFolder && it.sheets_inside) bits.push(`${plural(it.sheets_inside, "sheet")} come back with it`)
      if (!isFolder && it.comments_count) bits.push(plural(it.comments_count, "comment"))
      const restore = it.can_restore
        ? `<button type="button" data-action="click->design-step#restoreTrashItem" data-kind="${it.kind}" data-id="${it.id}" class="shrink-0 px-3 py-1 text-[13px] font-medium text-brand border border-brand rounded-lg hover:bg-blue-50 cursor-pointer">Restore</button>`
        : ""
      return `<div class="flex items-center gap-3 py-2.5" data-trash-row="${it.kind}-${it.id}">
        <span class="text-gray-400 shrink-0">${isFolder ? folderIcon : fileIcon}</span>
        <div class="min-w-0 flex-1">
          <p class="text-[13px] font-medium text-gray-900 truncate">${this._esc(name)}</p>
          <p class="text-[13px] text-gray-500 truncate">${bits.join(" · ")}</p>
        </div>
        ${restore}
      </div>`
    }).join("")
  }

  async restoreTrashItem(event) {
    const btn = event.currentTarget
    const { kind, id } = btn.dataset
    btn.disabled = true
    const url = kind === "folder"
      ? this.restoreFolderUrlValue.replace("__FOLDER_ID__", id)
      : this.restorePdfUrlValue.replace("__PDF_ID__", id)
    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!resp.ok) throw new Error("Request failed")
      const row = btn.closest("[data-trash-row]")
      if (row) row.remove()
      const left = this.trashListTarget.querySelectorAll("[data-trash-row]").length
      this.trashEmptyTarget.classList.toggle("hidden", left > 0)
      this._setTrashBadge(left)
      this._refreshCurrentView()
      this._showToast(kind === "folder" ? "Folder restored" : "File restored")
    } catch (e) {
      btn.disabled = false
      console.error("[design-step] restore error:", e)
      this._showAlert("Error", "Could not restore. Please try again.")
    }
  }

  _setTrashBadge(n) {
    if (!this.hasTrashBadgeTarget) return
    this.trashBadgeTarget.textContent = n
    this.trashBadgeTarget.classList.toggle("hidden", n === 0)
  }

  _bumpTrashBadge(by) {
    if (!this.hasTrashBadgeTarget) return
    this._setTrashBadge((parseInt(this.trashBadgeTarget.textContent, 10) || 0) + by)
  }

  // ── History: uploads (James Collins, Phelipe 2026-11-03) ─────────
  // Each upload can be undone in one go: its files go to Deleted (restorable)
  // and the older versions of those sheets become current again.

  _renderUploads(uploads) {
    if (!this.hasHistoryUploadsTarget) return
    this._historyUploads = uploads
    if (this.hasHistoryUploadsEmptyTarget) this.historyUploadsEmptyTarget.classList.toggle("hidden", uploads.length > 0)
    const uploadIcon = '<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/></svg>'
    this.historyUploadsTarget.innerHTML = uploads.map((u, i) => {
      const bits = [u.ago, u.uploaded_by ? `by ${u.uploaded_by}` : null].filter(Boolean).map(b => this._esc(b))
      const undo = u.can_undo
        ? `<button type="button" data-action="click->design-step#askUndoUpload" data-index="${i}" class="shrink-0 px-3 py-1 text-[13px] font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 cursor-pointer">Undo this upload</button>`
        : ""
      return `<div class="flex items-center gap-3 py-2.5">
        <span class="text-gray-400 shrink-0">${uploadIcon}</span>
        <div class="min-w-0 flex-1">
          <p class="text-[13px] font-medium text-gray-900 truncate">${this._esc(this._uploadWhen(u))} · ${u.count} file${u.count === 1 ? "" : "s"}</p>
          <p class="text-[13px] text-gray-500 truncate">${bits.join(" ")}</p>
        </div>
        ${undo}
      </div>`
    }).join("")
  }

  _uploadWhen(u) {
    return new Date(u.started_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
  }

  askUndoUpload(event) {
    const u = (this._historyUploads || [])[parseInt(event.currentTarget.dataset.index, 10)]
    if (!u || !this.hasUndoUploadModalTarget) return
    this._pendingUndo = u
    const files = `${u.count} file${u.count === 1 ? "" : "s"}`
    this.undoUploadTextTarget.textContent =
      `The ${files} from the ${this._uploadWhen(u)} upload${u.uploaded_by ? ` by ${u.uploaded_by}` : ""} go to Deleted, and older versions of those sheets become current again. You can restore any of them from History.`
    const marked = u.marked_count || 0
    this.undoUploadMarkedTarget.classList.toggle("hidden", marked === 0)
    this.undoUploadMarkedTarget.textContent = marked === 0 ? ""
      : `${marked} of these ${marked === 1 ? "file has" : "files have"} comments or markups. They go to Deleted with the file and come back with it.`
    this.undoUploadModalTarget.classList.remove("hidden")
  }

  closeUndoUpload() {
    if (this.hasUndoUploadModalTarget) this.undoUploadModalTarget.classList.add("hidden")
    this._pendingUndo = null
  }

  async confirmUndoUpload() {
    const u = this._pendingUndo
    if (!u) return
    if (this.hasUndoUploadBtnTarget) this.undoUploadBtnTarget.disabled = true
    try {
      const resp = await fetch(this.bulkDestroyUrlValue, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
        credentials: "same-origin",
        body: JSON.stringify({ ids: u.ids })
      })
      if (!resp.ok) throw new Error("Request failed")
      const data = await resp.json()
      const n = data.deleted_count ?? u.ids.length
      track("Design Upload Undone", { step_name: this.stepNameValue, file_count: n, marked_count: u.marked_count || 0 })
      this.closeUndoUpload()
      this._refreshCurrentView()
      this._showToast(`Upload undone: ${n} file${n === 1 ? "" : "s"} moved to Deleted`)
      this.openTrash()
    } catch (e) {
      console.error("[design-step] undo upload error:", e)
      this._showToast("Couldn't undo the upload", "error")
    } finally {
      if (this.hasUndoUploadBtnTarget) this.undoUploadBtnTarget.disabled = false
    }
  }

  // ── Row Menu ──────────────────────────────────────────────────

  toggleRowMenu(event) {
    event.stopPropagation()
    const pdfId = event.currentTarget.dataset.pdfId
    // Close all other menus
    document.querySelectorAll("[data-row-menu]").forEach(m => {
      if (m.dataset.rowMenuId !== pdfId) m.classList.add("hidden")
    })
    const menu = this.element.querySelector(`[data-row-menu-id="${pdfId}"]`)
    if (menu) menu.classList.toggle("hidden")
  }

  // ── DWG pop-up (E5-3) ──────────────────────────────────────────────
  // Markup and annotations only work on PDFs, so a CAD file explains that
  // and hands over the file itself.
  _openCadModal(pdf) {
    if (!this.hasCadModalTarget) return
    this.cadModalNameTarget.textContent = pdf.title || pdf.file_name || "DWG"
    this.cadModalDownloadTarget.href = pdf.file_url || "#"
    this.cadModalDownloadTarget.classList.toggle("pointer-events-none", !pdf.file_url)
    this.cadModalDownloadTarget.classList.toggle("opacity-50", !pdf.file_url)
    this.cadModalTarget.classList.remove("hidden")
  }

  closeCadModal() {
    if (this.hasCadModalTarget) this.cadModalTarget.classList.add("hidden")
  }

  stopProp(event) {
    event.stopPropagation()
  }

  // ── Due Date ─────────────────────────────────────────────────

  async setDueDate() {
    if (!this.hasDueDateInputTarget || !this.setDeadlineUrlValue) return
    const token = this._csrfToken()
    const dateVal = this.dueDateInputTarget.value || null

    this._updateDaysLeft(dateVal)

    try {
      const resp = await fetch(this.setDeadlineUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({
          phase_status_id: this.phaseStatusIdValue,
          due_date: dateVal
        })
      })
      if (!resp.ok) throw new Error("Request failed")
      this._showToast("Due date updated")
    } catch (e) {
      console.error("[design-step] Set due date error:", e)
      this._showAlert("Error", "Failed to update due date. Please try again.")
    }
  }

  _updateDaysLeft(dateStr) {
    if (!this.hasDaysLeftTarget) return
    if (!dateStr) { this.daysLeftTarget.innerHTML = ""; return }

    const due = new Date(dateStr + "T00:00:00")
    const now = new Date(); now.setHours(0, 0, 0, 0)
    const diff = Math.round((due - now) / 86400000)

    let text, cls
    if (diff < 0) {
      const abs = Math.abs(diff)
      text = `${abs} ${abs === 1 ? "day" : "days"} overdue`
      cls = "text-[13px] text-red-500 font-medium"
    } else if (diff === 0) {
      text = "Due today"
      cls = "text-[13px] text-amber-500 font-medium"
    } else if (diff <= 7) {
      text = `${diff} ${diff === 1 ? "day" : "days"} left`
      cls = "text-[13px] text-amber-500 font-medium"
    } else {
      text = `${diff} ${diff === 1 ? "day" : "days"} left`
      cls = "text-[13px] text-gray-400 font-medium"
    }

    this.daysLeftTarget.innerHTML = `<span class="${cls}">${text}</span>`
  }

  // ── Bluebeam Integration ─────────────────────────────────────

  async openInBluebeam(event) {
    event.stopPropagation()
    const pdfId = event.currentTarget.dataset.pdfId
    if (!pdfId) return

    // Close the row menu
    document.querySelectorAll("[data-row-menu]").forEach(m => m.classList.add("hidden"))

    this._triggerBluebeamOpen(pdfId)
  }

  async _triggerBluebeamOpen(pdfId) {
    const url = this.bluebeamOpenBaseUrlValue.replace(/\/0(\/|$)/, `/${pdfId}$1`)
    const token = this._csrfToken()

    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ step: this.stepNameValue })
      })
      const data = await resp.json()

      if (data.needs_auth) {
        // Redirect to Bluebeam OAuth
        window.location.href = data.auth_url
        return
      }

      if (data.status === "active" && data.session_url) {
        // Session already exists — show launch popover
        this._bluebeamSessionUrl = data.session_url
        this._bluebeamSessionId = data.session_id
        this._showBluebeamPopover()
        return
      }

      if (data.status === "creating") {
        // Session being created — show toast and poll for status
        this._showBluebeamToast("Creating Bluebeam session...")
        this._pollBluebeamStatus(pdfId)
      }
    } catch (e) {
      console.error("[design-step] Bluebeam open error:", e)
      this._showAlert("Bluebeam Error", "Failed to open in Bluebeam. Please try again.")
    }
  }

  _showBluebeamPopover() {
    // Surface the Studio Session ID as a manual-join fallback: if the one-click
    // studio:// hand-off doesn't pull the file into Revu Desktop, the user can
    // paste this ID into Revu (Studio → Join) and the document loads.
    if (this.hasBluebeamSessionIdTarget) {
      this.bluebeamSessionIdTarget.textContent = this._bluebeamSessionId || "—"
    }
    if (this.hasBluebeamSessionIdCopyLabelTarget) {
      this.bluebeamSessionIdCopyLabelTarget.textContent = "Copy"
    }
    if (this.hasBluebeamPopoverTarget) {
      this.bluebeamPopoverTarget.classList.remove("hidden")
    }
  }

  // Bluebeam's own session launcher — the documented hand-off, which routes
  // to installed Revu (or explains itself when Revu is missing). The old
  // `studio://studio.bluebeam.com/sessions/…` scheme was invented: on Luma's
  // machine (2026-08-28) it fronted Revu but never loaded the session,
  // because Revu doesn't understand that path. Studio IDs are shown
  // dash-formatted (269-728-452), so a bare 9-digit id gets its dashes back.
  _bluebeamJoinUrl() {
    if (!this._bluebeamSessionId) return null
    const raw = String(this._bluebeamSessionId).trim()
    const id = /^\d{9}$/.test(raw) ? `${raw.slice(0, 3)}-${raw.slice(3, 6)}-${raw.slice(6)}` : raw
    return `https://studio.bluebeam.com/join.html?ID=${encodeURIComponent(id)}`
  }

  copyBluebeamSessionId() {
    if (!this._bluebeamSessionId) return
    navigator.clipboard.writeText(this._bluebeamSessionId).then(() => {
      if (this.hasBluebeamSessionIdCopyLabelTarget) {
        this.bluebeamSessionIdCopyLabelTarget.textContent = "Copied!"
        setTimeout(() => {
          if (this.hasBluebeamSessionIdCopyLabelTarget) this.bluebeamSessionIdCopyLabelTarget.textContent = "Copy"
        }, 2000)
      }
    })
  }

  closeBluebeamPopover() {
    if (this.hasBluebeamPopoverTarget) {
      this.bluebeamPopoverTarget.classList.add("hidden")
    }
    this._bluebeamSessionUrl = null
    this._bluebeamSessionId = null
  }

  launchBluebeamWeb() {
    if (this._bluebeamSessionUrl) {
      window.open(this._bluebeamSessionUrl, "_blank")
    }
    this.closeBluebeamPopover()
  }

  launchBluebeamDesktop() {
    const url = this._bluebeamJoinUrl()
    if (url) window.open(url, "_blank")
    this.closeBluebeamPopover()
  }

  copyBluebeamLink() {
    if (this._bluebeamSessionUrl) {
      navigator.clipboard.writeText(this._bluebeamSessionUrl).then(() => {
        if (this.hasCopyLinkLabelTarget) {
          this.copyLinkLabelTarget.textContent = "Copied!"
          setTimeout(() => { this.copyLinkLabelTarget.textContent = "Copy Session Link" }, 2000)
        }
      })
    }
  }

  showSaveMarkupsModal(event) {
    event.stopPropagation()
    event.preventDefault()
    const pdfId = event.currentTarget.dataset.pdfId
    if (!pdfId) return

    this._saveMarkupsPdfId = pdfId
    if (this.hasSaveMarkupsModalTarget) {
      this.saveMarkupsModalTarget.classList.remove("hidden")
    }
  }

  closeSaveMarkupsModal() {
    if (this.hasSaveMarkupsModalTarget) {
      this.saveMarkupsModalTarget.classList.add("hidden")
    }
    this._saveMarkupsPdfId = null
  }

  async confirmSaveMarkups() {
    const pdfId = this._saveMarkupsPdfId
    if (!pdfId) return

    this.closeSaveMarkupsModal()
    this._showBluebeamToast("Saving markups from Bluebeam...")

    const url = this.bluebeamSyncBaseUrlValue.replace(/\/0(\/|$)/, `/${pdfId}$1`)
    const token = this._csrfToken()

    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      const data = await resp.json()

      if (data.status === "syncing") {
        this._pollBluebeamStatus(pdfId)
      } else if (data.error) {
        this._hideBluebeamToast()
        this._showAlert("Bluebeam Error", data.error)
      }
    } catch (e) {
      console.error("[design-step] Bluebeam save markups error:", e)
      this._hideBluebeamToast()
      this._showAlert("Save Failed", "Failed to save markups. Please try again.")
    }
  }

  closeBluebeamSession(event) {
    event.stopPropagation()
    const pdfId = event.currentTarget.dataset.pdfId
    if (!pdfId) return

    document.querySelectorAll("[data-row-menu]").forEach(m => m.classList.add("hidden"))
    this._closeBluebeamPdfId = pdfId
    if (this.hasCloseBluebeamModalTarget) this.closeBluebeamModalTarget.classList.remove("hidden")
  }

  closeCloseBluebeamModal() {
    if (this.hasCloseBluebeamModalTarget) this.closeBluebeamModalTarget.classList.add("hidden")
    this._closeBluebeamPdfId = null
  }

  async confirmCloseBluebeam() {
    const pdfId = this._closeBluebeamPdfId
    if (!pdfId) return

    this.closeCloseBluebeamModal()

    const url = this.bluebeamCloseBaseUrlValue.replace(/\/0(\/|$)/, `/${pdfId}$1`)
    try {
      await fetch(url, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
        credentials: "same-origin"
      })
      this._refreshCurrentView()
    } catch (e) {
      console.error("[design-step] Bluebeam close error:", e)
    }
  }

  _pollBluebeamStatus(pdfId) {
    // Clear any existing poller for this PDF
    if (this._bluebeamPollers[pdfId]) {
      clearInterval(this._bluebeamPollers[pdfId])
    }

    const url = this.bluebeamStatusBaseUrlValue.replace(/\/0(\/|$)/, `/${pdfId}$1`)
    let pollCount = 0
    const maxPolls = 40 // 40 x 3s = 2 minutes

    this._bluebeamPollers[pdfId] = setInterval(async () => {
      pollCount++
      try {
        const resp = await this._fetch(url)
        const data = await resp.json()

        if (data.status === "active" && data.session_url) {
          clearInterval(this._bluebeamPollers[pdfId])
          delete this._bluebeamPollers[pdfId]
          this._hideBluebeamToast()
          this._bluebeamSessionUrl = data.session_url
          this._bluebeamSessionId = data.session_id
          this._showBluebeamPopover()
          this._refreshCurrentView()
        } else if (data.status === "closed") {
          clearInterval(this._bluebeamPollers[pdfId])
          delete this._bluebeamPollers[pdfId]
          this._hideBluebeamToast()
          this._refreshCurrentView()
        } else if (data.status === "error") {
          clearInterval(this._bluebeamPollers[pdfId])
          delete this._bluebeamPollers[pdfId]
          this._hideBluebeamToast()
          this._showAlert("Bluebeam Error", data.message || "Bluebeam session failed. Please try again.")
          this._refreshCurrentView()
        } else if (pollCount >= maxPolls) {
          clearInterval(this._bluebeamPollers[pdfId])
          delete this._bluebeamPollers[pdfId]
          this._hideBluebeamToast()
          this._showAlert("Bluebeam Error", "Bluebeam session creation timed out. Please try again.")
        }
        // "none" or "creating" — keep polling
      } catch (e) {
        console.error("[design-step] Bluebeam poll error:", e)
        clearInterval(this._bluebeamPollers[pdfId])
        delete this._bluebeamPollers[pdfId]
        this._hideBluebeamToast()
      }
    }, 3000)
  }

  _showBluebeamToast(message) {
    this._hideBluebeamToast()
    const toast = document.createElement("div")
    toast.id = "bluebeam-toast"
    toast.className = "flash-toast-info"
    toast.style.opacity = "0"
    toast.style.transform = "translateX(-50px)"
    toast.style.transition = "opacity 0.3s, transform 0.3s"
    toast.innerHTML = `
      <div class="shrink-0">
        <svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
      </div>
      <p class="flex-1 text-sm">${message}</p>
    `
    document.body.appendChild(toast)

    // Position like flash toasts — left of company logo
    requestAnimationFrame(() => {
      const logo = document.querySelector("[data-company-logo]")
      if (logo) {
        const logoRect = logo.getBoundingClientRect()
        const toastHeight = toast.offsetHeight || 32
        const top = (50 - toastHeight) / 2
        toast.style.top = `${Math.max(top, 4)}px`
        toast.style.right = "auto"
        toast.style.left = `${logoRect.left - toast.offsetWidth - 12}px`
      }
      toast.style.opacity = "1"
      toast.style.transform = "translateX(0)"
    })
  }

  _hideBluebeamToast() {
    const toast = document.getElementById("bluebeam-toast")
    if (toast) {
      toast.style.opacity = "0"
      setTimeout(() => toast.remove(), 300)
    }
  }

  // ── Saved filters + per-user group state ──────────────────────
  // One JSON blob per user (design_step_filters) holds the search term, the
  // date range, the view mode and which tree groups were left open — the same
  // shape the Drawings/Specs pages persist.

  _restoreSavedFilters() {
    const saved = this.savedFiltersValue || {}
    this._groupState = saved._groups || {}
    this._filters = { ...saved }
    delete this._filters._groups
    delete this._filters._view

    if (this._filters.search && this.hasSearchInputTarget) {
      this._searchTerm = this._filters.search
      this.searchInputTarget.value = this._filters.search
      this._toggleSearchClear()
    }
    if (this._filters.date_from && this.hasDateFromTarget) this.dateFromTarget.value = this._filters.date_from
    if (this._filters.date_to && this.hasDateToTarget) this.dateToTarget.value = this._filters.date_to
    this._updateFilterBadge()
  }

  _persistFilters() {
    if (!this.hasSaveFiltersUrlValue || !this.saveFiltersUrlValue) return
    const payload = { ...this._filters }
    if (this._groupState && Object.keys(this._groupState).length) payload._groups = this._groupState
    payload._view = this.currentViewValue
    fetch(this.saveFiltersUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
      credentials: "same-origin",
      body: JSON.stringify({ filters: payload })
    }).catch(() => {})
  }

  _groupExpanded(key, def) {
    return (this._groupState && key in this._groupState) ? this._groupState[key] : def
  }

  _recordGroup(key, expanded) {
    if (!key) return
    ;(this._groupState ||= {})[key] = expanded
    clearTimeout(this._groupSaveTimer)
    this._groupSaveTimer = setTimeout(() => this._persistFilters(), 400)
  }

  _updateFilterBadge() {
    if (!this.hasFilterBadgeTarget) return
    let count = 0
    if (this._filters.date_from || this._filters.date_to) count++
    if (this._typeFilter) count++
    if (this._versionFilter) count++
    if (this._activeDiscipline) count++
    if (count > 0) {
      this.filterBadgeTarget.classList.remove("hidden")
      this.filterBadgeTarget.classList.add("inline-flex")
      this.filterBadgeTarget.textContent = count
      if (this.hasClearFiltersRowTarget) this.clearFiltersRowTarget.style.display = ""
    } else {
      this.filterBadgeTarget.classList.add("hidden")
      this.filterBadgeTarget.classList.remove("inline-flex")
      if (this.hasClearFiltersRowTarget) this.clearFiltersRowTarget.style.display = "none"
    }
  }

  // ── Date filters ──────────────────────────────────────────────

  selectDatePreset(event) {
    const preset = event.currentTarget.dataset.preset
    const today = new Date()
    let dateFrom = ""
    if (preset) {
      const d = new Date(today)
      d.setDate(d.getDate() - parseInt(preset, 10))
      dateFrom = d.toISOString().slice(0, 10)
    }
    this._filters.date_from = dateFrom
    this._filters.date_to = dateFrom ? today.toISOString().slice(0, 10) : ""
    if (this.hasDateFromTarget) this.dateFromTarget.value = this._filters.date_from
    if (this.hasDateToTarget) this.dateToTarget.value = this._filters.date_to

    this.datePresetsTargets.forEach(container => {
      container.querySelectorAll("button").forEach(btn => {
        if (btn.dataset.preset === preset) {
          btn.className = btn.className.replace(/text-gray-500 hover:bg-gray-50 border-gray-200/, "bg-brand/10 text-brand font-medium border-brand/30")
        } else {
          btn.className = btn.className.replace(/bg-brand\/10 text-brand font-medium border-brand\/30/, "text-gray-500 hover:bg-gray-50 border-gray-200")
        }
      })
    })

    this._resetAndLoad()
    this._persistFilters()
    this._updateFilterBadge()
  }

  filterByDate() {
    this._filters.date_from = this.hasDateFromTarget ? this.dateFromTarget.value : ""
    this._filters.date_to = this.hasDateToTarget ? this.dateToTarget.value : ""
    this.datePresetsTargets.forEach(container => {
      container.querySelectorAll("button").forEach(btn => {
        btn.className = btn.className.replace(/bg-brand\/10 text-brand font-medium border-brand\/30/, "text-gray-500 hover:bg-gray-50 border-gray-200")
      })
    })
    this._resetAndLoad()
    this._persistFilters()
    this._updateFilterBadge()
  }

  clearFilters() {
    this._filters = {}
    this._typeFilter = ""
    this._versionFilter = ""
    this._activeDiscipline = null
    this._paintChips(this.hasTypeChipsTarget ? this.typeChipsTarget : null, "docType", "")
    this._paintChips(this.hasVersionChipsTarget ? this.versionChipsTarget : null, "version", "")
    if (this._disciplineCounts) this._renderDisciplineTabs(this._disciplineCounts)
    this._searchTerm = ""
    this._clearAiState()
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    this._toggleSearchClear()
    if (this.hasDateFromTarget) this.dateFromTarget.value = ""
    if (this.hasDateToTarget) this.dateToTarget.value = ""

    this.datePresetsTargets.forEach(container => {
      container.querySelectorAll("button").forEach((btn, i) => {
        if (i === 0) {
          btn.className = btn.className.replace(/text-gray-500 hover:bg-gray-50 border-gray-200/, "bg-brand/10 text-brand font-medium border-brand/30")
        } else {
          btn.className = btn.className.replace(/bg-brand\/10 text-brand font-medium border-brand\/30/, "text-gray-500 hover:bg-gray-50 border-gray-200")
        }
      })
    })

    this._resetAndLoad()
    this._persistFilters()
    this._updateFilterBadge()
  }

  // ── Discipline tabs ───────────────────────────────────────────

  _renderDisciplineTabs(counts) {
    if (!this.hasDisciplineMenuTarget || !counts) return
    this._disciplineCounts = counts
    this.disciplineMenuTarget.innerHTML = counts.map(disc => {
      const isActive = (this._activeDiscipline === null && disc.id === null) ||
                       String(disc.id) === String(this._activeDiscipline)
      return `<button type="button"
                      class="px-2.5 py-1 text-[13px] rounded-full transition-colors cursor-pointer border ${isActive ? CHIP_ON : CHIP_OFF}"
                      data-action="click->design-step#selectDiscipline"
                      data-discipline="${disc.id || ""}">${this._esc(disc.name)} <span class="text-gray-400">${disc.count}</span></button>`
    }).join("")
  }

  selectDiscipline(event) {
    event.preventDefault()
    const disc = event.currentTarget.dataset.discipline || null
    this._activeDiscipline = disc === "" ? null : disc
    if (this._disciplineCounts) this._renderDisciplineTabs(this._disciplineCounts)
    this._resetAndLoad()
    this._updateFilterBadge()
  }

  // ── List / grid view ──────────────────────────────────────────

  switchView(event) {
    const view = event.currentTarget.dataset.view
    if (view === this.currentViewValue) return
    this._applyViewMode(view)
  }

  _applyViewMode(view, { reload = true, persist = true } = {}) {
    this.currentViewValue = view === "grid" ? "grid" : "list"
    if (this.hasViewSwitcherTarget) {
      this.viewSwitcherTarget.querySelectorAll("button").forEach(btn => {
        const on = btn.dataset.view === this.currentViewValue
        btn.classList.toggle("text-brand", on)
        btn.classList.toggle("font-bold", on)
        btn.classList.toggle("text-text-muted", !on)
        btn.classList.toggle("font-medium", !on)
        btn.classList.toggle("hover:text-gray-700", !on)
      })
    }
    if (reload) this._renderPdfTable()
    if (!persist || !this.hasSaveViewModeUrlValue || !this.saveViewModeUrlValue) return
    fetch(this.saveViewModeUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
      credentials: "same-origin",
      body: JSON.stringify({ view_mode: this.currentViewValue })
    }).catch(() => {})
  }

  // ── Selection + bulk actions ──────────────────────────────────

  toggleBulkMode() {
    this._bulkMode = !this._bulkMode
    this._selectedIds.clear()
    if (this.hasBulkToggleTarget) this.bulkToggleTarget.classList.toggle("hidden", this._bulkMode)
    if (this.hasBulkBarTarget) {
      this.bulkBarTarget.classList.toggle("hidden", !this._bulkMode)
      this.bulkBarTarget.classList.toggle("flex", this._bulkMode)
    }
    if (this.hasSelectAllColTarget) this.selectAllColTarget.classList.toggle("hidden", !this._bulkMode)
    if (this.hasSelectAllCheckboxTarget) {
      this.selectAllCheckboxTarget.checked = false
      this.selectAllCheckboxTarget.indeterminate = false
    }
    this._renderPdfTable()
    this._updateBulkBar()
  }

  // The table is fixed-layout, sized by its colgroup — give the checkbox its
  // own narrow column while bulk mode is on, or every column shifts.
  _syncBulkColumn() {
    if (!this.hasPdfTableTarget) return
    const cg = this.pdfTableTarget.querySelector("colgroup")
    if (!cg) return
    let cbCol = cg.querySelector("[data-cb-col]")
    if (this._bulkMode && !cbCol) {
      cbCol = document.createElement("col")
      cbCol.setAttribute("data-cb-col", "")
      cbCol.style.width = "40px"
      cg.insertBefore(cbCol, cg.children[1] || null)
    } else if (!this._bulkMode && cbCol) {
      cbCol.remove()
    }
    this.pdfTableTarget.querySelectorAll("[data-checkbox-col]").forEach(td => {
      td.classList.toggle("hidden", !this._bulkMode)
    })
  }

  toggleSelectAll(event) {
    const checked = event.currentTarget.checked
    this.element.querySelectorAll("[data-file-checkbox]").forEach(cb => {
      cb.checked = checked
      if (checked) this._selectedIds.add(cb.dataset.fileId)
      else this._selectedIds.delete(cb.dataset.fileId)
    })
    this._updateBulkBar()
  }

  toggleFileSelect(event) {
    const id = event.currentTarget.dataset.fileId
    if (event.currentTarget.checked) this._selectedIds.add(id)
    else this._selectedIds.delete(id)
    if (this.hasSelectAllCheckboxTarget) {
      const total = this.element.querySelectorAll("[data-file-checkbox]").length
      this.selectAllCheckboxTarget.checked = total > 0 && this._selectedIds.size === total
      this.selectAllCheckboxTarget.indeterminate = this._selectedIds.size > 0 && this._selectedIds.size < total
    }
    this._updateBulkBar()
  }

  _updateBulkBar() {
    const n = this._selectedIds.size
    if (this.hasBulkCountTarget) this.bulkCountTarget.textContent = `${n} selected`
    if (this.hasCompareBtnTarget) {
      const { ok, reason } = this._compareEligibility()
      this.compareBtnTarget.classList.toggle("opacity-40", !ok)
      this.compareBtnTarget.classList.toggle("cursor-not-allowed", !ok)
      this.compareBtnTarget.classList.toggle("cursor-pointer", ok)
      this.compareBtnTarget.classList.toggle("hover:bg-brand/5", ok)
      this.compareBtnTarget.dataset.tip = reason || "Compare the two selected sheets."
    }
  }

  bulkDelete() {
    if (this._selectedIds.size === 0) return
    this._deleteTarget = { type: "bulk" }
    if (this.hasDeletePdfNameTarget) {
      this.deletePdfNameTarget.textContent = `${this._selectedIds.size} file${this._selectedIds.size === 1 ? "" : "s"}`
    }
    this._setDeletePackNote([...this._selectedIds])
    if (this.hasDeletePdfModalTarget) this.deletePdfModalTarget.classList.remove("hidden")
  }

  async _performBulkDelete() {
    const ids = [...this._selectedIds]
    try {
      const resp = await fetch(this.bulkDestroyUrlValue, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
        credentials: "same-origin",
        body: JSON.stringify({ ids })
      })
      if (!resp.ok) throw new Error("Request failed")
      const data = await resp.json()
      this._selectedIds.clear()
      this._updateBulkBar()
      this._refreshCurrentView()
      this._bumpTrashBadge(data.deleted_count ?? ids.length)
      this._showToast(`${data.deleted_count ?? ids.length} file${(data.deleted_count ?? ids.length) === 1 ? "" : "s"} deleted`)
    } catch (e) {
      console.error("[design-step] Bulk delete error:", e)
      this._showToast("Couldn't delete the selected files", "error")
    }
  }

  // ── Search-inside-documents result rows ───────────────────────

  _toggleMatchRow(rowEl, id) {
    const existing = this.pdfTableBodyTarget.querySelector(`[data-match-row-for="${id}"]`)
    const chev = rowEl.querySelector("[data-match-chevron]")
    if (existing) {
      existing.remove()
      chev?.classList.remove("rotate-180")
      return
    }
    const file = this._pdfsById?.[String(id)]
    const match = this._contentMatches?.[String(id)]
    if (!file || !match) return
    // Span only the VISIBLE columns — a display:none td still counts as a
    // child, and an oversized colspan reserves a phantom column on the right.
    const colCount = rowEl.querySelectorAll("td:not(.hidden)").length
    rowEl.insertAdjacentHTML("afterend", this._matchRowHtml(file, match, colCount))
    chev?.classList.add("rotate-180")
  }

  _matchRowHtml(file, match, colCount) {
    const term = this._searchTerm || ""
    // AI-filter snippets search a short phrase from their own quote (first 8
    // words) instead of the (empty) search-bar term.
    const termAttr = (p) => {
      if (!p.term) return ""
      const phrase = String(p.term).split(/\s+/).slice(0, 8).join(" ")
      return ` data-term="${this._esc(phrase).replace(/"/g, "&quot;")}"`
    }
    const items = (match.pages || []).map(p => `
      <button type="button" data-action="click->design-step#openFileAtPage" data-file-id="${file.id}" data-page="${p.page}"${termAttr(p)}
              class="w-full flex items-start gap-2.5 text-left rounded-lg border border-gray-200 bg-white hover:border-brand/40 hover:bg-brand/[0.03] px-3 py-2 cursor-pointer">
        <span class="shrink-0 inline-flex items-center rounded bg-gray-100 text-gray-600 px-1.5 py-px text-[13px] font-medium mt-px">p. ${p.page}</span>
        <span class="text-[13px] text-gray-700 leading-5 min-w-0 break-words">${this._highlightTerm(p.snippet, term)}</span>
        ${p.count > 1 ? `<span class="ml-auto shrink-0 text-[13px] text-gray-400">${p.count}×</span>` : ""}
      </button>`).join("")
    const more = match.more_pages > 0
      ? `<div class="text-[13px] text-gray-400 mt-1.5">+ ${match.more_pages} more page${match.more_pages === 1 ? "" : "s"} with matches — open the document to see all</div>`
      : ""
    // The w-0/min-w-full wrapper keeps this wide content out of the table's
    // auto layout, or a long snippet stretches every other row.
    return `<tr data-match-row-for="${file.id}" class="bg-brand/[0.02]">
      <td colspan="${colCount}" class="px-4 py-3">
        <div class="w-0 min-w-full">
          <div class="flex items-center justify-between gap-3 mb-2">
            <span class="text-[13px] font-medium text-gray-700">${match.count} result${match.count === 1 ? "" : "s"} inside this document</span>
            <button type="button" data-action="click->design-step#openFileAtPage" data-file-id="${file.id}" data-page="${match.pages?.[0]?.page || 1}"
                    class="text-[13px] text-brand font-medium hover:underline cursor-pointer shrink-0">Open document →</button>
          </div>
          <div class="space-y-1.5">${items}</div>
          ${more}
        </div>
      </td>
    </tr>`
  }

  _highlightTerm(text, term) {
    const esc = this._esc(text || "")
    if (!term) return esc
    const safe = this._esc(term).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    try {
      return esc.replace(new RegExp(safe, "gi"), m => `<mark class="bg-amber-200/80 text-gray-900 rounded-sm px-0.5">${m}</mark>`)
    } catch (_) {
      return esc
    }
  }

  // Snippet click → open the PDF at that page with the term pre-searched.
  openFileAtPage(event) {
    event.stopPropagation()
    const id = event.currentTarget.dataset.fileId
    const page = parseInt(event.currentTarget.dataset.page, 10) || 1
    const term = event.currentTarget.dataset.term || this._searchTerm || null
    this._openPdfById(id, { initialPage: page, searchTerm: term })
  }

  // Hover thumbnail on the sheet list.
  sheetPreviewShow(event) {
    const el = event.currentTarget
    showSheetPreview(el, el.dataset.pdfUrl, el.dataset.thumbLabel)
  }

  sheetPreviewHide() { hideSheetPreview() }

  // ── "Show me" (AI answer filter) ──────────────────────────────

  _applyAiSources(sources) {
    const valid = (Array.isArray(sources) ? sources : []).filter(s => s && s.id)
    if (!valid.length) return
    this._aiSources = valid
    // The AI filter replaces any typed search — clear it so they don't fight.
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    this._searchTerm = ""
    this._filters.search = ""
    this._toggleSearchClear()
    this._updateAiChip()
    this._resetAndLoad()
    track("AI Show Me Clicked", {
      project_id: this.projectIdValue,
      module: "design",
      documents: new Set(valid.map(s => String(s.id))).size
    })
  }

  clearAiFilter(e) {
    if (e) e.preventDefault()
    if (!this._aiSources) return
    this._clearAiState()
    this._resetAndLoad()
  }

  _clearAiState() {
    this._aiSources = null
    this._updateAiChip()
  }

  _updateAiChip() {
    if (!this.hasAiFilterChipTarget) return
    const on = !!this._aiSources
    this.aiFilterChipTarget.classList.toggle("hidden", !on)
    this.aiFilterChipTarget.classList.toggle("inline-flex", on)
    if (on) {
      const n = new Set(this._aiSources.map(s => String(s.id))).size
      const label = this.aiFilterChipTarget.querySelector("[data-ai-chip-label]")
      if (label) label.textContent = `AI answer · ${n} doc${n === 1 ? "" : "s"}`
    }
  }

  // Synthesize the per-row badge + snippet data from the AI's cited quotes,
  // reusing the search-results rendering as-is.
  _mergeAiMatches() {
    const map = {}
    this._aiSources.forEach(s => {
      const k = String(s.id)
      map[k] ||= { count: 0, pages: [], more_pages: 0 }
      map[k].count++
      map[k].pages.push({ page: s.page || 1, count: 1, snippet: s.quote || s.label || "", term: s.quote || "" })
    })
    this._contentMatches = map
  }

  // ── Detail slide-over ─────────────────────────────────────────

  _openDetail(id) {
    id = String(id)
    this._activeFileId = id
    this._modalOpen = true
    const file = this._pdfsById[id]

    const nav = this._navOrder()
    const idx = nav.indexOf(id)
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = idx <= 0
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = idx < 0 || idx >= nav.length - 1

    this.detailModalTarget.classList.remove("hidden")
    this.detailLoadingTarget.classList.remove("hidden")
    this.detailPreviewTarget.innerHTML = ""
    this.detailMetaTarget.classList.add("hidden")
    if (this.hasDetailRevisionHistoryTarget) this.detailRevisionHistoryTarget.classList.add("hidden")
    if (this.hasDetailLinkedItemsTarget) this.detailLinkedItemsTarget.classList.add("hidden")

    requestAnimationFrame(() => {
      this.detailBackdropTarget.classList.add("opacity-100")
      this.detailBackdropTarget.classList.remove("opacity-0")
      this.detailPanelTarget.classList.remove("translate-x-full")
    })

    document.addEventListener("keydown", this._onKeydown)

    if (!file) {
      this.detailLoadingTarget.classList.add("hidden")
      this.detailPreviewTarget.innerHTML = '<p class="p-6 text-gray-400">File not found</p>'
      return
    }

    const titleText = file.sheet_number
      ? `${file.sheet_number}${file.title ? " — " + file.title : ""}`
      : (file.title || file.file_name || "File")
    this.detailTitleTarget.querySelector("span").textContent = titleText
    this.detailDownloadBtnTarget.href = file.file_url || "#"
    this.detailLoadingTarget.classList.add("hidden")

    if (file.is_image && file.file_url) {
      this.detailPreviewTarget.innerHTML = `
        <div class="flex-1 flex items-center justify-center p-4 bg-gray-50">
          <img src="${file.file_url}" alt="${this._esc(file.file_name || "")}"
               class="max-w-full max-h-[70vh] object-contain rounded shadow-sm">
        </div>`
    } else if (file.is_pdf && file.file_url) {
      this.detailPreviewTarget.innerHTML = `
        <div class="flex-1 flex items-center justify-center p-8">
          <button type="button" class="flex flex-col items-center gap-3 px-8 py-6 rounded-xl border-2 border-dashed border-gray-300 hover:border-brand/50 hover:bg-brand/5 cursor-pointer transition-colors"
                  data-action="click->design-step#openPdfFromDetail" data-pdf-id="${file.id}">
            <svg class="w-12 h-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>
            <span class="text-sm font-medium text-gray-600">Open PDF viewer</span>
          </button>
        </div>`
    } else {
      this.detailPreviewTarget.innerHTML = `
        <div class="flex-1 flex items-center justify-center p-8">
          <div class="text-center">
            <p class="text-base font-medium text-gray-600 mb-1">${this._esc(file.file_name || "")}</p>
            <p class="text-sm text-gray-400">.${this._esc(file.extension || "file")} file</p>
            ${file.file_url ? `<a href="${file.file_url}" target="_blank" rel="noopener" class="inline-flex items-center gap-1.5 mt-4 px-4 py-2 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg transition-colors">Download</a>` : ""}
          </div>
        </div>`
    }

    const discBadge = file.discipline
      ? `<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium ${DISCIPLINE_COLORS[file.discipline] || "bg-gray-100 text-gray-600"}">${this._esc(file.discipline)}</span>`
      : '<span class="text-gray-400">—</span>'

    this.detailMetaTarget.classList.remove("hidden")
    this.detailMetaTarget.innerHTML = `
      <div class="flex items-center justify-between">
        <span class="text-gray-400">Type</span>
        ${typeBadgeHtml(file.document_type)}
      </div>
      <div class="flex items-center justify-between">
        <span class="text-gray-400">Discipline</span>
        ${discBadge}
      </div>
      <div class="flex items-center justify-between">
        <span class="text-gray-400">Current Rev</span>
        <span>${this._esc(file.revision_number || "—")}${file.formatted_date ? ` · ${this._esc(file.formatted_date)}` : ""}</span>
      </div>
      <div class="flex items-center justify-between">
        <span class="text-gray-400">Markups</span>
        <span>${file.comments_count || 0}</span>
      </div>
      <div class="flex items-center justify-between">
        <span class="text-gray-400">Uploaded by</span>
        <span>${this._esc(file.uploader_name || "—")}</span>
      </div>
      <div class="flex items-center justify-between">
        <span class="text-gray-400">File</span>
        <span class="truncate max-w-[300px]">${this._esc(file.file_name || "—")}</span>
      </div>`

    this._loadSheetHistory(id)
  }

  openPdfFromDetail(event) {
    event.stopPropagation()
    const id = event.currentTarget.dataset.pdfId
    this.closeDetail()
    this._openPdfById(id)
  }

  closeDetail() {
    if (!this._modalOpen) return
    this._modalOpen = false
    this.detailBackdropTarget.classList.remove("opacity-100")
    this.detailBackdropTarget.classList.add("opacity-0")
    this.detailPanelTarget.classList.add("translate-x-full")
    setTimeout(() => this.detailModalTarget.classList.add("hidden"), 300)
    document.removeEventListener("keydown", this._onKeydown)
    this._activeFileId = null
  }

  backdropMousedown(event) {
    this._backdropMousedown = event.target === this.detailBackdropTarget
  }

  closeOnBackdrop(event) {
    if (this._backdropMousedown && event.target === this.detailBackdropTarget) this.closeDetail()
    this._backdropMousedown = false
  }

  prevFile() {
    const nav = this._navOrder()
    const idx = nav.indexOf(String(this._activeFileId))
    if (idx > 0) this._openDetail(nav[idx - 1])
  }

  nextFile() {
    const nav = this._navOrder()
    const idx = nav.indexOf(String(this._activeFileId))
    if (idx >= 0 && idx < nav.length - 1) this._openDetail(nav[idx + 1])
  }

  _onKeydown(event) {
    if (!this._modalOpen) return
    if (event.key === "Escape") {
      this.closeDetail()
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault()
      this.prevFile()
    } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault()
      this.nextFile()
    }
  }

  async _loadSheetHistory(id) {
    if (!this.hasSheetHistoryUrlTemplateValue || !this.sheetHistoryUrlTemplateValue) return
    const url = this.sheetHistoryUrlTemplateValue.replace("__ID__", id)
    try {
      const res = await this._fetch(url)
      const data = await res.json()

      if (data.revisions?.length && this.hasDetailRevisionHistoryTarget) {
        this.detailRevisionHistoryTarget.classList.remove("hidden")
        this.detailRevisionHistoryTarget.innerHTML = `
          <h4 class="text-[13px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Revision History</h4>
          <div class="space-y-1.5">
            ${data.revisions.map(rev => `
              <div class="flex items-center gap-3 text-[13px] ${rev.is_current ? "" : "text-gray-400"}">
                <span class="w-3 h-3 rounded-full ${rev.is_current ? "bg-brand" : "border-2 border-gray-300"} shrink-0"></span>
                <span class="font-medium ${rev.is_current ? "text-gray-800" : ""}">Rev ${this._esc(rev.revision_number || "?")}</span>
                <span class="flex-1"></span>
                <span>${this._esc(rev.formatted_date || "")}</span>
                <span class="text-gray-400 truncate max-w-[80px]">${this._esc(rev.uploader_name || "")}</span>
                ${rev.file_url ? `<a href="${rev.file_url}" target="_blank" rel="noopener" class="text-brand hover:text-brand-dark text-[13px] font-medium">view</a>` : ""}
              </div>`).join("")}
          </div>`
      }

      if (data.linked_items?.length && this.hasDetailLinkedItemsTarget) {
        this.detailLinkedItemsTarget.classList.remove("hidden")
        this.detailLinkedItemsTarget.innerHTML = `
          <h4 class="text-[13px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Linked Action Items</h4>
          <div class="space-y-1.5">
            ${data.linked_items.map(item => `
              <div class="flex items-center gap-2 text-[13px]">
                <span class="font-medium text-gray-700">${this._esc(item.kind || "")}</span>
                <span class="text-gray-600 truncate flex-1">${this._esc(item.name || "")}</span>
                <span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium ${item.status === "open" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}">${this._esc(item.status || "")}</span>
              </div>`).join("")}
          </div>`
      }
    } catch (e) {
      console.error("[design-step] sheet history failed:", e)
    }
  }

  // ── Compare (split + overlay) ─────────────────────────────────

  openCompareChoice() {
    if (!this._compareEligibility().ok) return
    // The choice popup only appears when there IS a choice: overlay needs two
    // versions of the SAME sheet, so two different sheets go straight to split.
    if (!this._overlayEligibility().ok) return this.compareSelected()
    this.compareChoiceModalTarget.classList.remove("hidden")
  }

  closeCompareChoice() {
    if (this.hasCompareChoiceModalTarget) this.compareChoiceModalTarget.classList.add("hidden")
  }

  chooseSplitCompare() {
    this.closeCompareChoice()
    this.compareSelected()
  }

  chooseOverlayCompare() {
    if (!this._overlayEligibility().ok) return
    this.closeCompareChoice()
    this.compareOverlaySelected()
  }

  _compareEligibility() {
    const files = [...this._selectedIds].map(id => (this._pdfsById || {})[id]).filter(Boolean)
    if (files.length !== 2) return { ok: false, reason: "Select exactly 2 sheets to compare." }
    if (!files.every(f => f.is_pdf)) return { ok: false, reason: "Both selected sheets must be PDFs." }
    if (files.some(f => f.combined)) return { ok: false, reason: "The combined file can't be compared — pick 2 single sheets." }
    return { ok: true, reason: "" }
  }

  _overlayEligibility() {
    const base = this._compareEligibility()
    if (!base.ok) return base
    const [a, b] = [...this._selectedIds].map(id => (this._pdfsById || {})[id])
    const sameSheet = a.sheet_number && b.sheet_number && a.sheet_number === b.sheet_number
    if (!sameSheet) return { ok: false, reason: "Overlay works on two versions of the SAME sheet." }
    return { ok: true, reason: "" }
  }

  // Whether this document has an older version we know about — nested under it
  // (default view) or elsewhere in the loaded rows (historical view).
  _hasPreviousVersion(file) {
    if (!file || file.combined) return false
    if ((file.versions || []).length > 0) return true
    const key = file.sheet_number
    if (!key) return false
    const rev = parseInt(file.revision_number, 10) || 0
    return Object.values(this._pdfsById || {}).some(o =>
      o && String(o.id) !== String(file.id) && !o.combined &&
      o.sheet_number === key && o.document_type === file.document_type &&
      ((parseInt(o.revision_number, 10) || 0) < rev))
  }

  compareSelected() {
    if (!this._compareEligibility().ok) return
    const [a, b] = [...this._selectedIds]
    const [left, right] = this._olderFirst(a, b)
    this._dispatchCompare(left, right)
  }

  async compareOverlaySelected() {
    if (!this._overlayEligibility().ok) return
    const [a, b] = [...this._selectedIds]
    const [olderId, newerId] = this._olderFirst(a, b)
    const viewerCtrl = this._viewerController()
    if (!viewerCtrl || typeof viewerCtrl.openOverlayCompare !== "function") return

    this._openPdfById(newerId)
    const ready = await this._waitForViewerReady(viewerCtrl)
    if (!ready) return

    const revOf = (id) => {
      const f = (this._pdfsById || {})[String(id)] || {}
      return f.revision_number != null ? `Rev ${f.revision_number}` : ""
    }
    const f = (this._pdfsById || {})[String(newerId)] || {}
    const base = [f.sheet_number, f.title].filter(Boolean).join(" — ") || f.file_name || "Sheet"
    const title = `${base} · Overlay: ${revOf(olderId) || "older"} (red) vs ${revOf(newerId) || "newer"} (blue)`
    viewerCtrl.openOverlayCompare({ oldUrl: this._viewerDataUrl(olderId), newUrl: this._viewerDataUrl(newerId), title })
  }

  _viewerController() {
    const viewer = this.element.querySelector("[data-controller='action-item-pdf-viewer']")
    return viewer && this.application.getControllerForElementAndIdentifier(viewer, "action-item-pdf-viewer")
  }

  _viewerDataUrl(id) {
    return this.viewerDataBaseUrlValue.replace(/\/0(\/|$)/, `/${id}$1`)
  }

  // The overlay needs the viewer's engine up and the sheet in — poll until the
  // instance reports document-ready (45s ceiling).
  _waitForViewerReady(viewerCtrl) {
    return new Promise((resolve) => {
      const t0 = Date.now()
      const poll = setInterval(() => {
        if (viewerCtrl._documentReady && viewerCtrl._instance) { clearInterval(poll); resolve(true) }
        else if (Date.now() - t0 > 45000) { clearInterval(poll); resolve(false) }
      }, 300)
    })
  }

  // Order two ids so the OLDER one is on the left ("before → after").
  _olderFirst(a, b) {
    const data = this._pdfsById || {}
    const rank = (id) => {
      const f = data[id] || {}
      const statusRank = f.status === "current" ? 1 : 0
      const rev = parseInt(f.revision_number, 10)
      return [statusRank, isNaN(rev) ? 0 : rev, Date.parse(f.created_at) || 0]
    }
    const [ra, rb] = [rank(a), rank(b)]
    const aOlder = ra[0] !== rb[0] ? ra[0] < rb[0]
      : ra[1] !== rb[1] ? ra[1] < rb[1]
      : ra[2] <= rb[2]
    return aOlder ? [a, b] : [b, a]
  }

  _dispatchCompare(left, right) {
    window.dispatchEvent(new CustomEvent("pdf-compare:open", {
      detail: { leftVersionId: left, rightVersionId: right }
    }))
  }

  compareToPrevious(event) {
    if (event) event.stopPropagation()
    const id = event.currentTarget.dataset.pdfId
    if (!id) return
    this._dispatchCompare(id, "previous")
  }

  // ── In-viewer compare picker ──────────────────────────────────

  async openComparePicker(event) {
    const id = String(event?.detail?.fileId || this._viewerPdfId || "")
    if (!id) return
    const file = this._pdfsById?.[id]
    this._compareSourceId = id
    this._compareVersions = []
    this._compareMode = event?.detail?.mode === "overlay" ? "overlay" : "split"

    if (this.hasComparePickerEmptyTarget && this._comparePickerEmptyDefault == null) {
      this._comparePickerEmptyDefault = this.comparePickerEmptyTarget.textContent.trim()
    }
    if (this.hasComparePickerTitleTarget) {
      const label = file ? ([file.sheet_number, file.title].filter(Boolean).join(" — ") || file.file_name) : "sheet"
      this.comparePickerTitleTarget.textContent = `${this._compareMode === "overlay" ? "Overlay" : "Compare"} ${label}`
    }

    this._setComparePickerSections({ loading: true })
    this.comparePickerModalTarget.classList.remove("hidden")

    let versions = []
    try {
      const res = await this._fetch(this.sheetHistoryUrlTemplateValue.replace("__ID__", id))
      const data = await res.json()
      versions = Array.isArray(data.revisions) ? data.revisions : []
    } catch (_) { versions = [] }

    if (this._compareSourceId !== id) return // closed while loading
    this._compareVersions = versions
    this._renderComparePicker(versions)
  }

  closeComparePicker() {
    if (this.hasComparePickerModalTarget) this.comparePickerModalTarget.classList.add("hidden")
    this._compareSourceId = null
    this._compareVersions = []
  }

  compareWithPrevious() {
    if (this._compareSourceId) this._launchCompare(this._compareSourceId, "previous")
  }

  compareWithPicked(event) {
    const otherId = String(event.currentTarget.dataset.pdfId || "")
    const id = this._compareSourceId
    if (!id || !otherId) return
    const [left, right] = this._orderVersions(id, otherId)
    this._launchCompare(left, right)
  }

  // Close the picker and open the side-by-side compare ON TOP of the viewer
  // (K7-20): closing the viewer first stepped the browser back a page,
  // which made Turbo restore the page and wipe out the compare too. Overlay
  // mode stays INSIDE the viewer instead.
  _launchCompare(left, right) {
    if (this._compareMode === "overlay") return this._launchOverlay(left, right)
    this.closeComparePicker()
    this._dispatchCompare(left, right)
  }

  _launchOverlay(left, right) {
    const vs = this._compareVersions || []
    let oldId, newId
    if (String(right) === "previous") {
      newId = String(left)
      const idx = vs.findIndex(v => String(v.id) === newId)
      const prev = (idx >= 0 && vs[idx + 1]) || vs.find(v => String(v.id) !== newId)
      if (!prev) { this.closeComparePicker(); return }
      oldId = String(prev.id)
    } else {
      oldId = String(left)
      newId = String(right)
    }
    const revOf = (id) => {
      const v = vs.find(x => String(x.id) === String(id))
      return v && v.revision_number != null ? `Rev ${v.revision_number}` : ""
    }
    const file = this._pdfsById?.[String(this._compareSourceId)] || {}
    const base = [file.sheet_number, file.title].filter(Boolean).join(" — ") || file.file_name || "Sheet"
    const title = `${base} · Overlay: ${revOf(oldId) || "older"} (red) vs ${revOf(newId) || "newer"} (blue)`

    this.closeComparePicker()
    const viewerCtrl = this._viewerController()
    if (!viewerCtrl || typeof viewerCtrl.openOverlayCompare !== "function") return
    viewerCtrl.openOverlayCompare({ oldUrl: this._viewerDataUrl(oldId), newUrl: this._viewerDataUrl(newId), title })
  }

  // sheet history is newest-first, so a higher array index = older.
  _orderVersions(aId, bId) {
    const vs = this._compareVersions || []
    const idx = (vid) => vs.findIndex(v => String(v.id) === String(vid))
    return idx(aId) > idx(bId) ? [aId, bId] : [bId, aId]
  }

  _renderComparePicker(versions) {
    const count = versions.length
    this._setComparePickerSections({
      previous: count >= 2,
      older: count > 2,
      empty: count < 2,
      message: this._comparePickerEmptyDefault
    })

    if (count > 2 && this.hasComparePickerListTarget) {
      const others = versions.filter(v => String(v.id) !== String(this._compareSourceId))
      this.comparePickerListTarget.innerHTML = others.map(v => {
        const rev = this._esc(v.revision_number != null ? `Rev ${v.revision_number}` : "—")
        const meta = [this._esc(v.formatted_date || ""), this._esc(v.uploader_name || "")].filter(Boolean).join(" · ")
        const current = v.is_current ? `<span class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[13px] font-medium bg-emerald-100 text-emerald-700">Current</span>` : ""
        return `<button type="button" data-pdf-id="${v.id}"
                  data-action="click->design-step#compareWithPicked"
                  class="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left hover:bg-gray-50 cursor-pointer transition-colors">
            <span class="text-[13px] font-semibold text-gray-800 w-24 shrink-0 truncate">${rev}${current}</span>
            <span class="min-w-0 flex-1">
              ${meta ? `<span class="block text-[13px] text-gray-500 truncate">${meta}</span>` : ""}
            </span>
          </button>`
      }).join("")
    }
  }

  _setComparePickerSections({ loading = false, previous = false, older = false, empty = false, message = null } = {}) {
    if (this.hasComparePickerPreviousWrapTarget) this.comparePickerPreviousWrapTarget.classList.toggle("hidden", loading || !previous)
    if (this.hasComparePickerOlderSectionTarget) this.comparePickerOlderSectionTarget.classList.toggle("hidden", loading || !older)
    if (this.hasComparePickerEmptyTarget) {
      if (loading) this.comparePickerEmptyTarget.textContent = "Loading…"
      else if (message != null) this.comparePickerEmptyTarget.textContent = message
      this.comparePickerEmptyTarget.classList.toggle("hidden", !(loading || empty))
    }
  }

  // ── Distribution modal ────────────────────────────────────────

  async _openDistributeModal(uploadedIds) {
    if (!this.hasDistributeModalTarget || !uploadedIds?.length) return
    this._distributeFileIds = uploadedIds
    this._distributeSelectedEmails = new Map()
    this._distributeLockedEmails = new Set()
    this._distributeMembers = []

    try {
      const resp = await this._fetch(this.distributionMembersUrlValue)
      if (!resp.ok) return
      this._distributeMembers = await resp.json()
    } catch { return }

    if (this._distributeMembers.length === 0) return

    // Pre-select (and lock) users who asked for document notifications.
    for (const company of this._distributeMembers) {
      for (const user of company.users) {
        if (user.documents_notif) {
          this._distributeSelectedEmails.set(user.email, { name: user.name, email: user.email })
          this._distributeLockedEmails.add(user.email)
        }
      }
    }

    this.distributeSearchInputTarget.value = ""
    this._renderDistributeList()
    this._updateDistributeUI()
    this.distributeModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  filterDistributeMembers() { this._renderDistributeList() }

  _renderDistributeList() {
    const query = (this.distributeSearchInputTarget.value || "").toLowerCase().trim()
    let html = ""
    for (const company of this._distributeMembers) {
      const users = company.users.filter(u =>
        !query || u.name.toLowerCase().includes(query) || u.email.toLowerCase().includes(query))
      if (users.length === 0) continue

      html += `<div class="px-5 pt-3 pb-1">
        <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">${this._esc(company.name)}</span>
      </div>`

      for (const user of users) {
        const checked = this._distributeSelectedEmails.has(user.email)
        const locked = this._distributeLockedEmails.has(user.email)
        html += `<label class="flex items-center gap-2.5 px-5 py-1.5 hover:bg-gray-50 ${locked ? "cursor-default opacity-70" : "cursor-pointer"} select-none">
          <input type="checkbox" class="form-checkbox rounded shrink-0" style="accent-color: var(--color-brand); width: 14px; height: 14px"
                 data-email="${this._esc(user.email)}"
                 data-name="${this._esc(user.name)}"
                 data-action="change->design-step#toggleDistributeMember"
                 ${checked ? "checked" : ""} ${locked ? "disabled" : ""}>
          <div class="min-w-0 flex-1">
            <div class="text-sm text-gray-800 truncate">${this._esc(user.name)}</div>
            <div class="text-[13px] text-gray-400 truncate">${this._esc(user.email)}</div>
          </div>
          ${locked ? '<span class="text-[13px] text-gray-400 shrink-0">auto</span>' : ""}
        </label>`
      }
    }
    this.distributeDropdownListTarget.innerHTML =
      html || '<div class="px-5 py-4 text-sm text-gray-400 text-center">No members found</div>'
  }

  toggleDistributeMember(event) {
    const cb = event.currentTarget
    if (cb.checked) this._distributeSelectedEmails.set(cb.dataset.email, { name: cb.dataset.name, email: cb.dataset.email })
    else this._distributeSelectedEmails.delete(cb.dataset.email)
    this._updateDistributeUI()
  }

  toggleDistributeSelectAll(event) {
    const checked = event.currentTarget.checked
    for (const company of this._distributeMembers) {
      for (const user of company.users) {
        if (checked) this._distributeSelectedEmails.set(user.email, { name: user.name, email: user.email })
        else if (!this._distributeLockedEmails.has(user.email)) this._distributeSelectedEmails.delete(user.email)
      }
    }
    this._renderDistributeList()
    this._updateDistributeUI()
  }

  _updateDistributeUI() {
    const count = this._distributeSelectedEmails.size
    const totalUsers = this._distributeMembers.reduce((sum, c) => sum + c.users.length, 0)
    this.distributeSendBtnTarget.disabled = count === 0
    this.distributeCountTarget.textContent = count > 0 ? `(${count}/${totalUsers})` : ""
    if (totalUsers > 0) {
      this.distributeSelectAllTarget.checked = count >= totalUsers
      this.distributeSelectAllTarget.indeterminate = count > 0 && count < totalUsers
    }
  }

  async submitDistribute() {
    const emails = [...this._distributeSelectedEmails.keys()]
    if (emails.length === 0) return

    this.distributeSendBtnTarget.disabled = true
    const label = this.distributeSendBtnTarget.textContent
    this.distributeSendBtnTarget.textContent = "Sending..."
    try {
      const resp = await fetch(this.distributeUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
        credentials: "same-origin",
        body: JSON.stringify({ emails, ids: this._distributeFileIds, folder_id: this._currentFolderId })
      })
      if (resp.ok) {
        this._closeDistributeModal()
        this._showToast("Notification sent")
      } else {
        this._showToast("Failed to send notification", "error")
      }
    } catch {
      this._showToast("Failed to send notification", "error")
    } finally {
      this.distributeSendBtnTarget.disabled = false
      this.distributeSendBtnTarget.textContent = label
    }
  }

  skipDistribute() { this._closeDistributeModal() }

  _closeDistributeModal() {
    this.distributeModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    this._distributeFileIds = []
    this._distributeSelectedEmails = new Map()
    this._distributeLockedEmails = new Set()
  }

  // ── Helpers ───────────────────────────────────────────────────

  _refreshCurrentView() {
    if (this._currentFolderId) {
      this._navigateToFolder(this._currentFolderId, this._currentFolderName)
    } else {
      this._loadInitial()
    }
  }

  _showAlert(title, message) {
    if (!this.hasAlertModalTarget) return
    if (this.hasAlertModalTitleTarget) this.alertModalTitleTarget.textContent = title
    if (this.hasAlertModalMessageTarget) this.alertModalMessageTarget.textContent = message
    this.alertModalTarget.classList.remove("hidden")
  }

  closeAlertModal() {
    if (this.hasAlertModalTarget) this.alertModalTarget.classList.add("hidden")
  }

  _fetch(url) {
    const token = this._csrfToken()
    return fetch(url, {
      headers: { "Accept": "application/json", "X-CSRF-Token": token },
      credentials: "same-origin"
    })
  }

  _subscribeToDevPdfChannel() {
    const cable = getCable()

    this._cableSubscription = cable.subscriptions.create(
      { channel: "DevPdfChannel", project_id: `${this.projectIdValue}-dev` },
      {
        received: (data) => {
          // Update annotation count in the local data + DOM
          const pdf = this._pdfs.find(p => p.id === data.pdf_id)
          if (pdf) {
            pdf.comments_count = data.comments_count
            const row = this.element.querySelector(`tr[data-pdf-id="${data.pdf_id}"]`)
            if (row) {
              const countCell = row.querySelectorAll("td")[3]
              if (countCell) countCell.textContent = data.comments_count || 0
            }
          }
        }
      }
    )
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  // Safe in BOTH text and attribute position — this is interpolated into
  // value="", data-thumb-label="" and friends, and a sheet title carrying an
  // inch mark would otherwise close the attribute and eat the rest of the tag.
  _esc(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  // ── Export ─────────────────────────────────────────────────────

  stopPropagation(event) { event.stopPropagation() }

  async openExportModal() {
    // Show modal immediately with loading state
    this.exportDocTypesTarget.innerHTML = '<p class="text-sm text-gray-400">Loading...</p>'
    this.exportFileCountTarget.textContent = ""
    this.exportHistoricalTarget.checked = false
    this._exportLastHistorical = false
    this.exportSortSelectTarget.value = "type_then_sheet"

    // Populate folder dropdown from loaded folders
    const folders = this._folders || []
    let folderOptions = '<option value="">All Folders</option><option value="root">Root files (no folder)</option>'
    folders.forEach(f => {
      folderOptions += `<option value="${f.id}">${this._escapeHtml(f.name)}</option>`
    })
    this.exportFolderSelectTarget.innerHTML = folderOptions

    this.exportModalTarget.classList.remove("hidden")

    // Fetch real counts from server
    try {
      const resp = await this._fetch(this.exportCountsUrlValue)
      this._exportCounts = await resp.json()
    } catch (e) {
      console.error("[design-step] Failed to load export counts:", e)
      this._exportCounts = { type_counts: {}, root_type_counts: {}, folder_type_counts: {}, total: 0 }
    }

    this._renderExportDocTypes()
    this._updateExportFileCount()

    // Listen for filter changes
    this._exportFolderHandler = () => { this._renderExportDocTypes(); this._updateExportFileCount() }
    this._exportCountHandler = this._updateExportFileCount.bind(this)
    this.exportFolderSelectTarget.addEventListener("change", this._exportFolderHandler)
    this.exportDocTypesTarget.addEventListener("change", this._exportCountHandler)
    this.exportHistoricalTarget.addEventListener("change", this._exportCountHandler)
  }

  hideExportModal() {
    this.exportModalTarget.classList.add("hidden")
    if (this._exportFolderHandler) {
      this.exportFolderSelectTarget.removeEventListener("change", this._exportFolderHandler)
    }
    if (this._exportCountHandler) {
      this.exportDocTypesTarget.removeEventListener("change", this._exportCountHandler)
      this.exportHistoricalTarget.removeEventListener("change", this._exportCountHandler)
    }
  }

  _renderExportDocTypes() {
    if (!this._exportCounts) return

    const folderId = this.exportFolderSelectTarget.value
    let tc
    if (!folderId) {
      tc = this._exportCounts.type_counts
    } else if (folderId === "root") {
      tc = this._exportCounts.root_type_counts
    } else {
      tc = this._exportCounts.folder_type_counts[folderId] || {}
    }

    const typeLabels = { drawing: "Drawings", specification: "Specifications", document: "Documents" }
    const types = Object.entries(typeLabels).filter(([key]) => (tc[key] || 0) > 0)
    if (types.length > 0) {
      this.exportDocTypesTarget.innerHTML = types.map(([key, label]) =>
        `<label class="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox" class="form-checkbox rounded" style="accent-color: var(--color-brand)"
                 value="${key}" checked data-export-doc-type>
          <span class="text-sm text-gray-700">${label} <span class="text-gray-400">(${tc[key]})</span></span>
        </label>`
      ).join("")
    } else {
      this.exportDocTypesTarget.innerHTML = '<p class="text-sm text-gray-400">No documents in this folder</p>'
    }

    // Re-attach the count listener since innerHTML replaced the checkboxes
    if (this._exportCountHandler) {
      this.exportDocTypesTarget.addEventListener("change", this._exportCountHandler)
    }
  }

  _updateExportFileCount() {
    if (!this.hasExportFileCountTarget || !this._exportCounts) return

    const includeHistorical = this.exportHistoricalTarget.checked

    // When historical changes, re-fetch counts from server
    if (includeHistorical !== this._exportLastHistorical) {
      this._exportLastHistorical = includeHistorical
      this._refetchExportCounts()
      return
    }

    const folderId = this.exportFolderSelectTarget.value
    let tc
    if (!folderId) {
      tc = this._exportCounts.type_counts
    } else if (folderId === "root") {
      tc = this._exportCounts.root_type_counts
    } else {
      tc = this._exportCounts.folder_type_counts[folderId] || {}
    }

    const checkedTypes = [...this.exportDocTypesTarget.querySelectorAll("[data-export-doc-type]:checked")]
      .map(cb => cb.value)
    const allTypes = [...this.exportDocTypesTarget.querySelectorAll("[data-export-doc-type]")]
      .map(cb => cb.value)

    let total = 0
    if (checkedTypes.length === 0 || checkedTypes.length === allTypes.length) {
      Object.values(tc).forEach(v => { total += (v || 0) })
    } else {
      checkedTypes.forEach(t => { total += (tc[t] || 0) })
    }

    this.exportFileCountTarget.textContent = `${total} file${total !== 1 ? "s" : ""} selected`
  }

  async _refetchExportCounts() {
    try {
      const includeHistorical = this.exportHistoricalTarget.checked
      const url = new URL(this.exportCountsUrlValue, window.location.origin)
      if (includeHistorical) url.searchParams.set("include_historical", "true")
      const resp = await this._fetch(url.toString())
      this._exportCounts = await resp.json()
      this._renderExportDocTypes()
      this._updateExportFileCount()
    } catch (e) {
      console.error("[design-step] Failed to refetch export counts:", e)
    }
  }

  _buildExportParams() {
    const checkedTypes = [...this.exportDocTypesTarget.querySelectorAll("[data-export-doc-type]:checked")]
      .map(cb => cb.value)
    const folderId = this.exportFolderSelectTarget.value
    const includeHistorical = this.exportHistoricalTarget.checked
    const sortBy = this.exportSortSelectTarget.value

    const body = new URLSearchParams()
    if (checkedTypes.length > 0) {
      checkedTypes.forEach(t => body.append("document_types[]", t))
    }
    if (folderId) body.set("folder_id", folderId)
    if (includeHistorical) body.set("include_historical", "true")
    body.set("sort_by", sortBy)

    return body
  }

  async submitExportZip() {
    const body = this._buildExportParams()

    this.exportZipBtnTarget.disabled = true
    this.exportZipBtnTarget.textContent = "Exporting..."

    try {
      const resp = await fetch(this.exportZipUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body
      })

      if (resp.ok) {
        this.hideExportModal()
        this._showToast("Your export is being prepared. You will receive an email with the download link.")
      } else {
        this._showToast("Export failed. Please try again.", "error")
      }
    } catch {
      this._showToast("Export failed. Please try again.", "error")
    } finally {
      this.exportZipBtnTarget.disabled = false
      this.exportZipBtnTarget.textContent = "Export ZIP"
    }
  }

  async submitCompiledPdf() {
    const body = this._buildExportParams()

    this.exportCompiledBtnTarget.disabled = true
    this.exportZipBtnTarget.disabled = true
    this.exportCompiledBtnTarget.textContent = "Compiling..."

    try {
      const resp = await fetch(this.exportCompiledPdfUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body
      })

      if (!resp.ok) {
        this._showToast("Export failed. Please try again.", "error")
        return
      }

      const { token } = await resp.json()
      this.hideExportModal()
      this._showCompilingToast("Compiling documents into a single PDF...")
      this._pollCompiledPdf(token)
    } catch {
      this._showToast("Export failed. Please try again.", "error")
    } finally {
      this.exportCompiledBtnTarget.disabled = false
      this.exportZipBtnTarget.disabled = false
      this.exportCompiledBtnTarget.textContent = "Export Compiled PDF"
    }
  }

  async _pollCompiledPdf(token, attempts = 0) {
    if (attempts > 60) {
      this._hideCompilingToast()
      this._showToast("PDF compilation is taking longer than expected. Please try again.", "error")
      return
    }

    await new Promise(r => setTimeout(r, 2000))

    try {
      const resp = await fetch(`${this.compiledPdfStatusUrlValue}?token=${token}`)
      const data = await resp.json()

      if (data.status === "processing") {
        this._pollCompiledPdf(token, attempts + 1)
      } else if (data.status === "ready") {
        this._hideCompilingToast()
        this._showToast("Compiled PDF ready — downloading now.")
        const blob = await fetch(data.url).then(r => r.blob())
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = data.filename || "design-compiled.pdf"
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
      } else {
        this._hideCompilingToast()
        this._showToast(data.message || "PDF compilation failed.", "error")
      }
    } catch {
      this._hideCompilingToast()
      this._showToast("Failed to check compilation status.", "error")
    }
  }

  _showCompilingToast(message) {
    this._hideCompilingToast()
    const el = document.createElement("div")
    el.id = "dev-compiled-pdf-generating-toast"
    el.className = "flash-toast-info"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "0")
    el.setAttribute("role", "status")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = `<svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>`
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  _hideCompilingToast() {
    const existing = document.getElementById("dev-compiled-pdf-generating-toast")
    if (existing) existing.remove()
  }

  _escapeHtml(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
